import { useEffect, useRef, useState, useCallback, useLayoutEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { Viewer as PSViewer } from '@photo-sphere-viewer/core';
import { MarkersPlugin, type Marker, type MarkerConfig } from '@photo-sphere-viewer/markers-plugin';
import '@photo-sphere-viewer/core/index.css';
import '@photo-sphere-viewer/markers-plugin/index.css';
import { useAuth } from '../../contexts/AuthContext';
import { accessOf, NO_ACCESS, sceneLayerAllowed, type FeatureAccess } from '../../utils/access';
import { PROJECT_FILES_QUERY, FILE_ANNOTATIONS_QUERY, SHARED_FILE_ANNOTATIONS_QUERY, SHARED_NOTES_ACCESS_QUERY, FILE_FOLDERS_QUERY, FILE_MEASUREMENTS_QUERY, SHARED_FILE_MEASUREMENTS_QUERY, FILE_FOCUS_ZONES_QUERY, BASEMAP_CONFIG_QUERY, PROJECT_SCENE_QUERY, SCENE_SETTINGS_QUERY, SHARED_TOUR_AREAS_QUERY } from '../../graphql/queries';
import {
  CREATE_ANNOTATION_MUTATION,
  REPLY_ANNOTATION_MUTATION,
  RESOLVE_ANNOTATION_MUTATION,
  DELETE_ANNOTATION_MUTATION,
  SHARED_CREATE_ANNOTATION_MUTATION,
  SHARED_REPLY_ANNOTATION_MUTATION,
  CREATE_MEASUREMENT_MUTATION,
  CREATE_AREA_MEASUREMENT_MUTATION,
  DELETE_MEASUREMENT_MUTATION,
  SHARED_CREATE_MEASUREMENT_MUTATION,
  SHARED_CREATE_AREA_MEASUREMENT_MUTATION,
  SHARED_DELETE_MEASUREMENT_MUTATION,
  CREATE_FOCUS_ZONE_MUTATION,
  DELETE_FOCUS_ZONE_MUTATION,
  ADD_ANNOTATION_ATTACHMENT_MUTATION,
  DELETE_ANNOTATION_ATTACHMENT_MUTATION,
  LINK_ANNOTATION_FILE_MUTATION,
  UNLINK_ANNOTATION_FILE_MUTATION,
  SET_FILE_SCENE_TRANSFORM_MUTATION,
  SET_PROJECT_BASEMAP_MUTATION,
  SET_PROJECT_SITE_PLACEMENT_MUTATION,
  SET_TOUR_AREA_NAME_MUTATION,
  SET_TOUR_AREA_MODE_MUTATION,
  SAVE_TOUR_AREA_GROUPS_MUTATION,
} from '../../graphql/mutations';
import { putWithProgress } from '../../utils/putWithProgress';
import { LinkedFile, linkedFileIcon, linkedFileRoute } from '../../utils/linkedFiles';
import { useDocumentFormats } from '../../hooks/useDocumentFormats';
import { measurementLabel, measurementTarget, type OrthoAreaResult, type OrthoLineResult } from '../../utils/sceneMeasure';
import { freeWindow, inWindow, type SliderWindow } from '../../utils/freeSlider';
import { sceneCrs } from '../../utils/crs';
import {
  basemapEngineConfig, basemapState, projectBasemapOf, providerOrFallback, sameProjectBasemap, basemapLocatable,
  gridFit, sceneBasemapFrame, sitePlacementOf, sameSitePlacement, pinPlacement, screenShift, nudgeGridAdjust, nudgePlacement,
  type BasemapConfig, type BasemapFrameSource, type BasemapState, type GridAdjust, type GridFit, type ProjectBasemap, type SitePlacement,
} from '../../utils/basemap';
import SitePlacementPanel, { type SceneAnchor } from './SitePlacementPanel';
import NoteFilePicker from '../../components/common/NoteFilePicker';
import styles from './SmartView.module.css';
import ConversionProgressBar from '../../components/common/ConversionProgressBar';
import { useConversionProgress } from '../../hooks/useConversionProgress';
import { parseOccupancy, addToOccIndex, isOccluded, floorBelow, ringOccluded, type OccGrid, type OccIndex } from './occlusionGrid';
import { TOUR_RING_WORLD_RADIUS, ringExtent, ringsOverlap } from '../../utils/tourRings';
import { batchArrivals } from '../../utils/batchArrivals';
import { areaByStation, bearingOfHeading, floorLevels, formatAreaDistance, headingToArea, nearestStation, ringLink, tourAreas, type TourArea, type TourAreaGroup, type TourAreaMode, type TourAreaName, type TourStation } from '../../utils/tourAreas';
import { buildTourMap, type TourMap as TourMapModel } from '../../utils/tourMap';
import { bubbleRadius, chipWidth, edgeArrows, layoutEdgeChips, siteView, type EdgeArrow } from '../../utils/tourMinimap';
import { canEditProject, type ProjectRole } from '../../utils/projectRole';
import TourHorizon, { type HorizonArea } from './TourHorizon';
import TourMap from './TourMap';
import TourAreaEditor from './TourAreaEditor';
import type { RailArea } from './TourAreaRail';
import { canUnpackGzip, cdnAssetUrl, cdnFolder } from '../../utils/cdnAssets';
import { isSuperAdmin } from '../../utils/staff';
import Icon, { iconHtml, type IconName } from '../../components/common/Icon';

const TOKEN_KEY = 'geodraft_token';

type ColorMode = 'rgb' | 'elevation' | 'intensity';
type NavMode   = 'orbit' | 'firstPerson' | 'walk';
type PanelKind = 'files' | 'bim' | 'photos' | 'annotations' | 'measures' | 'zones' | 'settings' | 'inspect' | 'align' | null;
type BimXform = { dx: number; dy: number; dz: number; rot: number; scale: number };
const DEFAULT_BIM_XFORM: BimXform = { dx: 0, dy: 0, dz: 0, rot: 0, scale: 1 };
// Absolute placement of a scene layer / BIM model: world position of its
// centre, rotation about Z in degrees, uniform scale (File.sceneTransform).
export type SceneXform = { x: number; y: number; z: number; rot: number; scale: number };
export type SceneLayerKind = 'ortho' | 'drawing' | 'splat';
const layerKindOf = (f: { type?: string; extension?: string }): SceneLayerKind | null => {
  if (f.type === 'orthomosaic') return 'ortho';
  if (f.type === 'gaussian') return 'splat';
  if (f.type === 'two_d' && ['dwg', 'dxf'].includes((f.extension || '').toLowerCase())) return 'drawing';
  return null;
};
const sameXform = (a: SceneXform | null | undefined, b: SceneXform | null | undefined) =>
  !!a && !!b && ['x', 'y', 'z', 'rot', 'scale'].every((k) => Math.abs((a as Record<string, number>)[k] - (b as Record<string, number>)[k]) < 1e-6);

interface MeasurementItem {
  id: string;
  kind: 'distance' | 'area';
  createdBy: { id: string; firstName: string; lastName: string };
  guestName?: string | null;
  // Share links only: a guest of this link made it, so a guest may delete it.
  fromThisLink?: boolean;
  start?: Vec3 | null;
  end?: Vec3 | null;
  distance?: number | null;
  points?: Vec3[] | null;
  area?: number | null;
  createdAt: string;
  // Set on an orthomosaic's measurements: their points are in its map CRS.
  layerId?: string;
}

interface Vec3 { x: number; y: number; z: number; }
const toPointInput = (p: Vec3): Vec3 => ({ x: p.x, y: p.y, z: p.z });

interface FocusZoneItem {
  id: string;
  name: string;
  min: Vec3;
  max: Vec3;
  createdBy: { id: string; firstName: string; lastName: string };
  createdAt: string;
}
interface CloudGeometry {
  fileId: string;
  worldMin: Vec3;
  worldMax: Vec3;
  localMin: Vec3;
  localMax: Vec3;
  offset: Vec3 | null;
  spacing: number | null;
  pointCount: number | null;
}

// 8-color palette used for diagnostic bbox overlays. Cycles if there are more files.
const INSPECT_COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899'];

interface PCFile {
  id: string;
  originalName: string;
  fileSize: number;
  extension: string;
  status: string;
  type?: string;
  folderId?: string | null;
  panoramaCount?: number;
  sceneTransform?: SceneXform | null;
  srs?: { epsg?: number | null; proj4?: string | null; projected?: boolean | null } | null;
}

interface PCFolder {
  id: string;
  name: string;
}

type PanoConvention = 'world-cw' | 'scanner-cw' | 'scanner-ccw';

interface PanoramaEntry {
  id: string;
  fileId: string;
  index: number;
  image: string;
  position: { x: number; y: number; z: number };
  quaternion: { w: number; x: number; y: number; z: number };
  url: string;
  convention?: PanoConvention;
}
// One file's answer: its stations, or its occupancy grid (fetched after).
type PanoArrival = { fileId: string; panos?: PanoramaEntry[]; grid?: OccGrid };

interface AnnotationPosition { x: number; y: number; z: number; }
interface AnnotationUser { id: string; firstName: string; lastName: string; }
interface AnnotationReply { id: string; createdBy: AnnotationUser; guestName?: string | null; text: string; createdAt: string; }
interface NoteAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  createdAt: string;
  url: string | null;
  downloadUrl: string | null;
  uploadedBy: AnnotationUser | null;
}
interface Annotation {
  id: string;
  createdBy: AnnotationUser;
  guestName?: string | null;
  // Which file the note belongs to; layerId is set when that file is a splat
  // or orthomosaic layer (the position is then in the layer's own frame).
  fileId?: string;
  layerId?: string | null;
  position: AnnotationPosition;
  text: string;
  status: string;
  replies: AnnotationReply[];
  attachments: NoteAttachment[];
  linkedFiles: LinkedFile[];
  createdAt: string;
}

// Notes accept PDFs only (the backend enforces the same rule and checks the
// file's %PDF- signature).
const PDF_MIME = 'application/pdf';
const isPdfFile = (file: File) => file.type === PDF_MIME || /\.pdf$/i.test(file.name);
const isPdfAttachment = (att: NoteAttachment) => att.mimeType === PDF_MIME || /\.pdf$/i.test(att.fileName);
const formatBytes = (b: number) =>
  b < 1024 ? `${b} B`
    : b < 1024 * 1024 ? `${(b / 1024).toFixed(0)} KB`
      : `${(b / (1024 * 1024)).toFixed(1)} MB`;

// Share-link guests write under the link creator's account; their own name
// is what's shown.
const authorName = (x: { guestName?: string | null; createdBy: AnnotationUser }) =>
  x.guestName ? `${x.guestName} (guest)` : `${x.createdBy.firstName} ${x.createdBy.lastName}`;

const GUEST_NAME_KEY = 'geodraft_guest_name';

const formatTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

function yawFromQuaternion(q: { w: number; x: number; y: number; z: number }): number {
  return Math.atan2(2 * (q.w * q.z + q.x * q.y), 1 - 2 * (q.y * q.y + q.z * q.z));
}

// Panorama equirect conventions written by the conversion-service:
//   'world-cw'     — stitch_pinhole_panoramas.py. Equirect in world frame with
//                    theta = atan2(-wy, wx). PSV_yaw = -world_heading.
//   'scanner-cw'   — synthetic generator without the empirical fliplr.
//                    Scanner-local frame, theta = atan2(-y, x).
//                    PSV_yaw = yawFromQuaternion(q) - world_heading.
//   'scanner-ccw'  — synthetic generator with fliplr applied. The image is
//                    horizontally mirrored relative to scanner-cw, so the
//                    PSV-yaw formula has the opposite sign on the heading
//                    term: PSV_yaw = world_heading - yawFromQuaternion(q).
// Panoramas without the field default to 'world-cw' (recent stitch_pinhole).

function psvYawFromHeading(
  convention: PanoConvention,
  worldHeading: number,
  q: PanoramaEntry['quaternion'],
): number {
  if (convention === 'world-cw')    return -worldHeading;
  if (convention === 'scanner-ccw') return worldHeading - yawFromQuaternion(q);
  return yawFromQuaternion(q) - worldHeading; // 'scanner-cw'
}

function headingFromPsvYaw(
  convention: PanoConvention,
  psvYaw: number,
  q: PanoramaEntry['quaternion'],
): number {
  if (convention === 'world-cw')    return -psvYaw;
  if (convention === 'scanner-ccw') return psvYaw + yawFromQuaternion(q);
  return yawFromQuaternion(q) - psvYaw; // 'scanner-cw'
}

// ── Tour-marker math + ring projection (preserved) ───────────────────────────
function tourMarkerYawPitch(
  convention: PanoConvention,
  fromPos:  { x: number; y: number; z: number },
  fromQuat: { w: number; x: number; y: number; z: number },
  toPos:    { x: number; y: number; z: number },
): { yaw: number; pitch: number; distance: number } {
  const dx = toPos.x - fromPos.x;
  const dy = toPos.y - fromPos.y;
  const dz = toPos.z - fromPos.z;
  const distance = Math.sqrt(dx*dx + dy*dy + dz*dz);
  if (distance < 1e-6) return { yaw: 0, pitch: 0, distance };

  if (convention === 'world-cw') {
    // World-frame equirect: PSV_yaw 0 = world +X regardless of scanner.
    return { yaw: Math.atan2(-dy, dx), pitch: Math.asin(dz / distance), distance };
  }

  // 'scanner-cw' and 'scanner-ccw' both project from the scanner-local frame;
  // they differ only by a horizontal flip of the image, which corresponds to
  // the sign of ly in the yaw formula.
  const { w, x: qx, y: qy, z: qz } = fromQuat;
  const m00 = 1 - 2*(qy*qy + qz*qz);
  const m01 = 2*(qx*qy - qz*w);
  const m02 = 2*(qx*qz + qy*w);
  const m10 = 2*(qx*qy + qz*w);
  const m11 = 1 - 2*(qx*qx + qz*qz);
  const m12 = 2*(qy*qz - qx*w);
  const m20 = 2*(qx*qz - qy*w);
  const m21 = 2*(qy*qz + qx*w);
  const m22 = 1 - 2*(qx*qx + qy*qy);
  const lx = m00*dx + m10*dy + m20*dz;
  const ly = m01*dx + m11*dy + m21*dz;
  const lz = m02*dx + m12*dy + m22*dz;
  const r  = Math.sqrt(lx*lx + ly*ly + lz*lz);
  const yawSign = convention === 'scanner-ccw' ? 1 : -1;
  return { yaw: Math.atan2(yawSign * ly, lx), pitch: Math.asin(lz / r), distance };
}

const TOUR_NEAR_DISTANCE = 2;
const TOUR_FAR_DISTANCE  = 5;
const TOUR_MAX_DISTANCE  = 20;
// Without an occupancy grid nothing tells one storey from another, so stations
// more than this above or below are dropped. With a grid, every storey within
// TOUR_MAX_DISTANCE is a candidate and the sight line decides (ringOccluded).
const TOUR_MAX_Z_DELTA   = 2;
const TOUR_OPACITY_NEAR  = 1.0;
const TOUR_OPACITY_FAR   = 0.35;
// Height band for picking the OPENING panorama's floor. Must stay well under
// floor-to-floor (~3 m) so the densest band is one storey, never two merged —
// tripod-height variance within a floor is ~0.5 m, so 1 m captures a full floor.
const TOUR_START_Z_BAND  = 1;
// Occlusion is computed against the loaded octree; a half-streamed cloud under-
// occludes (missing points = false "clear line of sight"), so on initial load
// markers can show through walls until the wall's points arrive. Rather than
// continuously re-poll (which churns the octree and freezes the tour), run a FIXED
// set of bounded passes spread across the load window — each re-checks against
// whatever has loaded by then — then stop. Discrete passes, not a runaway loop.
const OCCL_PASS_MS = [600, 1400, 2800, 5000, 8000];

// Presigned S3 URLs carry query params — escape before embedding in tooltip HTML.
const escAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

function tourMarkerStyle(distance: number): { sizePx: number; opacity: number } {
  if (distance <= TOUR_NEAR_DISTANCE) return { sizePx: 56, opacity: TOUR_OPACITY_NEAR };
  if (distance >= TOUR_FAR_DISTANCE)  return { sizePx: 18, opacity: TOUR_OPACITY_FAR };
  const t = (distance - TOUR_NEAR_DISTANCE) / (TOUR_FAR_DISTANCE - TOUR_NEAR_DISTANCE);
  return {
    sizePx:  Math.round(56 - t * (56 - 18)),
    opacity: +(TOUR_OPACITY_NEAR - t * (TOUR_OPACITY_NEAR - TOUR_OPACITY_FAR)).toFixed(2),
  };
}

const TOUR_RING_SEGMENTS     = 36;
// Fallback ring height under a station the occupancy grid has no floor for.
const TOUR_SCANNER_HEIGHT    = 1.5;
// A ring whose floor is this far from the viewer's gets a plumb line to it.
const TOUR_LEVEL_STEP        = 0.25;

function tourFloorRingPoints(convention: PanoConvention, from: PanoramaEntry, to: PanoramaEntry, floorZ: number): Array<[number, number]> {
  const points: Array<[number, number]> = [];
  for (let i = 0; i < TOUR_RING_SEGMENTS; i++) {
    const a  = (i / TOUR_RING_SEGMENTS) * 2 * Math.PI;
    const wx = to.position.x + Math.cos(a) * TOUR_RING_WORLD_RADIUS;
    const wy = to.position.y + Math.sin(a) * TOUR_RING_WORLD_RADIUS;
    const { yaw, pitch } = tourMarkerYawPitch(convention, from.position, from.quaternion, { x: wx, y: wy, z: floorZ });
    points.push([yaw, pitch]);
  }
  return points;
}

// ── Panorama minimap ─────────────────────────────────────────────────────────
// In cloud view, the minimap lives inside the iframe (Potree renders the cloud
// top-down into a scissored corner of the main canvas). In pano view that
// iframe is entirely covered by the overlay backdrop, so we render a duplicate
// minimap here on top: solid background, red dots for the other scan stations,
// yellow dot at the current scanner position, FOV wedge that follows the
// user's panorama yaw in real time.

interface PanoMinimapProps {
  panoramas:   PanoramaEntry[];
  currentPano: PanoramaEntry;
  convention:  PanoConvention;
  psvRef:      React.MutableRefObject<PSViewer | null>;
  iframeRef:   React.RefObject<HTMLIFrameElement>;
  onSelect:    (target: PanoramaEntry) => void;
  post:        (cmd: Record<string, unknown>) => void;
  // Advanced Virtual Tour with two or more areas: dots take their area's
  // colour, arrows on the edge point to the areas out of the window, and
  // Whole site shows each area as a bubble to click.
  areas?:      TourArea[];
  areaOf?:     Map<string, TourArea>;
  onGoArea?:   (key: string) => void;
}

function PanoMinimap({ panoramas, currentPano, convention, psvRef, iframeRef, onSelect, post, areas, areaOf, onGoArea }: PanoMinimapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoveringRef = useRef(false);
  const [snapshot, setSnapshot] = useState<{ img: HTMLImageElement; cx: number; cy: number; half: number } | null>(null);
  const pendingReqRef = useRef<string | null>(null);
  // User zoom/pan: null = follow the default framing. Absolute world framing —
  // the station doesn't move while inside a panorama, so no camera-follow here.
  const viewRef = useRef<{ cx: number; cy: number; half: number } | null>(null);
  const dragRef = useRef({ active: false, id: -1, lastX: 0, lastY: 0, moved: 0 });
  const suppressClickRef = useRef(false);
  const snapTimerRef = useRef<number | undefined>(undefined);
  const showAreas = !!areas && areas.length > 1 && !!onGoArea;
  const [mode, setMode] = useState<'around' | 'site'>('around');
  const [arrows, setArrows] = useState<{ list: EdgeArrow[]; w: number; h: number }>({ list: [], w: 0, h: 0 });
  const arrowSigRef = useRef('');
  const site = useMemo(() => (showAreas && areas ? siteView(areas, 0.32) : null), [showAreas, areas]);
  const hereArea = areaOf?.get(currentPano.id);

  // Fallback framing if the iframe snapshot isn't back yet: square box around
  // all known scan-station positions with a 25 % margin.
  const fallback = useMemo(() => {
    if (!panoramas.length) return null;
    let minX =  Infinity, maxX = -Infinity, minY =  Infinity, maxY = -Infinity;
    for (const p of panoramas) {
      if (p.position.x < minX) minX = p.position.x;
      if (p.position.x > maxX) maxX = p.position.x;
      if (p.position.y < minY) minY = p.position.y;
      if (p.position.y > maxY) maxY = p.position.y;
    }
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const half = Math.max(maxX - minX, maxY - minY, 4) * 0.5 * 1.25;
    return { cx, cy, half };
  }, [panoramas]);

  // Prefer the iframe-rendered cloud background when available, fall back to
  // the panorama-positions bounds (used until the snapshot arrives and when
  // switching panoramas).
  const bounds = snapshot
    ? { cx: snapshot.cx, cy: snapshot.cy, half: snapshot.half }
    : fallback;
  const boundsRef = useRef(bounds);
  boundsRef.current = bounds;

  // The framing to draw with: the user's zoomed/panned view, or the default.
  const effectiveView = useCallback(
    () => viewRef.current ?? boundsRef.current,
    [],
  );

  const requestSnapshot = useCallback(() => {
    if (!currentPano) return;
    const v = viewRef.current;
    const reqId = 'mm-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    pendingReqRef.current = reqId;
    post({
      type:      'requestMinimapSnapshot',
      requestId: reqId,
      center:    v ? { x: v.cx, y: v.cy } : { x: currentPano.position.x, y: currentPano.position.y },
      ...(v ? { halfWidth: v.half } : {}),
    });
  }, [currentPano, post]);

  // After a zoom/pan settles, fetch a sharp snapshot at the new framing (the
  // old image is rescaled meanwhile, so the map responds instantly but soft).
  const scheduleSnapshot = useCallback(() => {
    if (snapTimerRef.current) window.clearTimeout(snapTimerRef.current);
    snapTimerRef.current = window.setTimeout(requestSnapshot, 350);
  }, [requestSnapshot]);

  useEffect(() => () => { if (snapTimerRef.current) window.clearTimeout(snapTimerRef.current); }, []);

  // Request a snapshot whenever the active panorama changes — the iframe will
  // render the cloud top-down centred on this scanner and post the image back.
  // The octree keeps streaming AFTER arrival (worst on a fresh page load, when
  // only coarse root nodes exist yet), so a single early snapshot freezes a
  // sparse minimap — re-request a few times and let each denser frame replace
  // the previous one (the old image stays up until the new one decodes).
  useEffect(() => {
    if (!currentPano) return;
    viewRef.current = null;
    setMode('around');
    setSnapshot(null);
    requestSnapshot();
    const timers = [1500, 4000, 9000].map((ms) => window.setTimeout(requestSnapshot, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [currentPano, requestSnapshot]);

  // Receive the snapshot from the iframe.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      if (e.data.type !== 'minimapSnapshot') return;
      if (e.data.requestId !== pendingReqRef.current) return; // stale
      const { dataURL, framing } = e.data as {
        dataURL: string;
        framing: { centerX: number; centerY: number; halfWidth: number };
      };
      const img = new Image();
      img.onload = () => {
        setSnapshot({ img, cx: framing.centerX, cy: framing.centerY, half: framing.halfWidth });
      };
      img.src = dataURL;
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const toPx = useCallback((x: number, y: number, w: number, h: number) => {
    const v = effectiveView();
    if (!v) return { x: 0, y: 0 };
    const nx = (x - v.cx) / v.half;
    const ny = (y - v.cy) / v.half;
    return { x: ( nx * 0.5 + 0.5) * w, y: (-ny * 0.5 + 0.5) * h };
  }, [effectiveView]);

  // Draw loop — reads PSV yaw each frame so the wedge follows the rotation.
  useEffect(() => {
    if (!bounds) return;
    let rafId = 0;
    const dpr = window.devicePixelRatio || 1;

    const draw = () => {
      rafId = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const cssW = canvas.clientWidth, cssH = canvas.clientHeight;
      if (!cssW || !cssH) return;
      if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
        canvas.width  = Math.round(cssW * dpr);
        canvas.height = Math.round(cssH * dpr);
      }
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssW, cssH);

      if (snapshot?.img.complete && snapshot.img.naturalWidth > 0) {
        // Map the snapshot from ITS framing into the current view, so a
        // zoomed/panned map rescales the image instead of mislocating it.
        const tl = toPx(snapshot.cx - snapshot.half, snapshot.cy + snapshot.half, cssW, cssH);
        const v = effectiveView();
        const scale = v ? snapshot.half / v.half : 1;
        ctx.drawImage(snapshot.img, tl.x, tl.y, scale * cssW, scale * cssH);
      }

      const siteMode = showAreas && mode === 'site';
      for (const p of panoramas) {
        if (p.id === currentPano.id) continue;
        const sp = toPx(p.position.x, p.position.y, cssW, cssH);
        const colour = showAreas ? areaOf?.get(p.id)?.colour : undefined;
        ctx.beginPath();
        ctx.arc(sp.x, sp.y, siteMode ? 2.5 : 4.5, 0, Math.PI * 2);
        ctx.fillStyle   = colour ?? 'rgba(220, 38, 38, 0.85)';
        ctx.fill();
        ctx.lineWidth   = siteMode ? 1 : 1.5;
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.stroke();
      }

      if (siteMode && areas) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        for (const a of areas) {
          const c = toPx(a.centre.x, a.centre.y, cssW, cssH);
          const r = bubbleRadius(a.stations.length);
          const isHere = hereArea?.key === a.key;
          ctx.beginPath();
          ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
          ctx.fillStyle = a.colour;
          ctx.fill();
          ctx.lineWidth = isHere ? 3.5 : 2;
          ctx.strokeStyle = isHere ? 'rgba(250, 204, 21, 1)' : '#ffffff';
          ctx.stroke();
          ctx.fillStyle = '#ffffff';
          ctx.font = '700 11px Inter, system-ui, sans-serif';
          ctx.fillText(String(a.stations.length), c.x, c.y + 0.5);
          const label = isHere ? `${a.name} (you)` : a.name;
          ctx.font = '600 11px Inter, system-ui, sans-serif';
          ctx.lineWidth = 3.5;
          ctx.lineJoin = 'round';
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
          ctx.strokeText(label, c.x, c.y + r + 10);
          ctx.fillStyle = '#131b2e';
          ctx.fillText(label, c.x, c.y + r + 10);
        }
      }

      if (showAreas && mode === 'around' && areas) {
        const v = effectiveView();
        const list = v ? edgeArrows(v, areas.filter((a) => a.key !== hereArea?.key)) : [];
        const sig = `${cssW}x${cssH}|` + list.map((a) => `${a.key}:${a.nx.toFixed(2)},${a.ny.toFixed(2)}`).join('|');
        if (sig !== arrowSigRef.current) { arrowSigRef.current = sig; setArrows({ list, w: cssW, h: cssH }); }
      } else if (arrowSigRef.current) {
        arrowSigRef.current = '';
        setArrows({ list: [], w: 0, h: 0 });
      }
      // Whole site marks you with the yellow ring on your area's bubble.
      if (siteMode) return;

      const here  = toPx(currentPano.position.x, currentPano.position.y, cssW, cssH);
      const psv   = psvRef.current;
      const pos   = psv?.getPosition();
      const psvYaw = pos?.yaw ?? 0;
      // Convert PSV yaw → world heading using whichever convention the
      // current panorama uses (or the user-overridden one).
      const worldHeading = headingFromPsvYaw(convention, psvYaw, currentPano.quaternion);

      // PSV has minFov=30, maxFov=120; getZoomLevel returns 0–100, but the
      // mapping is roughly linear in 1/tan(fov/2). For the minimap we just
      // want an indicative wedge — a 60° horizontal slice works well enough.
      const fovHalf  = (60 * Math.PI / 180) * 0.5;
      const wedgeLen = Math.min(cssW, cssH) * 0.22;
      // World y → canvas −y (we flip in toPx), so canvas angle = −worldHeading.
      ctx.save();
      ctx.translate(here.x, here.y);
      ctx.rotate(-worldHeading);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, wedgeLen, -fovHalf, fovHalf);
      ctx.closePath();
      ctx.fillStyle = 'rgba(250, 204, 21, 0.45)';
      ctx.fill();
      ctx.restore();

      ctx.beginPath();
      ctx.arc(here.x, here.y, 5.5, 0, Math.PI * 2);
      ctx.fillStyle   = 'rgba(250, 204, 21, 1.0)';
      ctx.fill();
      ctx.lineWidth   = 1.5;
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.stroke();
    };

    draw();
    return () => cancelAnimationFrame(rafId);
  }, [bounds, panoramas, currentPano, psvRef, toPx, snapshot, convention, effectiveView, showAreas, mode, areas, areaOf, hereArea]);

  const bubbleAt = useCallback((clientX: number, clientY: number): string | null => {
    const canvas = canvasRef.current;
    if (!canvas || !areas || mode !== 'site') return null;
    const rect = canvas.getBoundingClientRect();
    const lx = clientX - rect.left, ly = clientY - rect.top;
    for (const a of areas) {
      const c = toPx(a.centre.x, a.centre.y, rect.width, rect.height);
      if (Math.hypot(lx - c.x, ly - c.y) <= bubbleRadius(a.stations.length) + 2) return a.key;
    }
    return null;
  }, [areas, mode, toPx]);

  const chooseMode = useCallback((m: 'around' | 'site') => {
    setMode(m);
    viewRef.current = m === 'site' ? site : null;
    requestSnapshot();
  }, [site, requestSnapshot]);

  const hitTest = useCallback((clientX: number, clientY: number): PanoramaEntry | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const lx = clientX - rect.left;
    const ly = clientY - rect.top;
    if (lx < 0 || ly < 0 || lx > rect.width || ly > rect.height) return null;
    const HIT_PX = 10;
    let best: PanoramaEntry | null = null;
    let bestDist = HIT_PX;
    for (const p of panoramas) {
      if (p.id === currentPano.id) continue;
      const sp = toPx(p.position.x, p.position.y, rect.width, rect.height);
      const d = Math.hypot(lx - sp.x, ly - sp.y);
      if (d < bestDist) { bestDist = d; best = p; }
    }
    return best;
  }, [panoramas, currentPano, toPx]);

  // Wheel zoom, anchored at the cursor. Native non-passive listener — React's
  // delegated onWheel is passive, so preventDefault would be ignored there.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const base = boundsRef.current;
      if (!base) return;
      const v = viewRef.current ?? base;
      const rect = canvas.getBoundingClientRect();
      const factor = Math.exp(e.deltaY * (e.ctrlKey ? 0.01 : 0.0022));
      const newHalf = Math.min(base.half * 6, Math.max(base.half / 16, v.half * factor));
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = 1 - ((e.clientY - rect.top) / rect.height) * 2;
      viewRef.current = {
        cx: v.cx + nx * (v.half - newHalf),
        cy: v.cy + ny * (v.half - newHalf),
        half: newHalf,
      };
      scheduleSnapshot();
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [scheduleSnapshot]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.button !== 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture(e.pointerId);
    dragRef.current = { active: true, id: e.pointerId, lastX: e.clientX, lastY: e.clientY, moved: 0 };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (drag.active && e.pointerId === drag.id) {
      const canvas = canvasRef.current;
      const base = boundsRef.current;
      if (!canvas || !base) return;
      const rect = canvas.getBoundingClientRect();
      const dxPx = e.clientX - drag.lastX, dyPx = e.clientY - drag.lastY;
      drag.lastX = e.clientX; drag.lastY = e.clientY;
      drag.moved += Math.abs(dxPx) + Math.abs(dyPx);
      const v = viewRef.current ?? base;
      viewRef.current = {
        cx: v.cx - (dxPx / rect.width) * 2 * v.half,
        cy: v.cy + (dyPx / rect.height) * 2 * v.half,
        half: v.half,
      };
      if (drag.moved > 4) scheduleSnapshot();
      return;
    }
    const hit = !!hitTest(e.clientX, e.clientY) || !!bubbleAt(e.clientX, e.clientY);
    if (hit && !hoveringRef.current) {
      hoveringRef.current = true;
      if (canvasRef.current) canvasRef.current.style.cursor = 'pointer';
    } else if (!hit && hoveringRef.current) {
      hoveringRef.current = false;
      if (canvasRef.current) canvasRef.current.style.cursor = '';
    }
  }, [hitTest, bubbleAt, scheduleSnapshot]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag.active || e.pointerId !== drag.id) return;
    drag.active = false;
    if (drag.moved > 4) suppressClickRef.current = true;
  }, []);

  const onClick = useCallback((e: React.MouseEvent) => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    const bubble = bubbleAt(e.clientX, e.clientY);
    if (bubble && onGoArea) { onGoArea(bubble); return; }
    const hit = hitTest(e.clientX, e.clientY);
    if (hit) onSelect(hit);
  }, [hitTest, bubbleAt, onSelect, onGoArea]);

  const onDoubleClick = useCallback(() => {
    viewRef.current = mode === 'site' ? site : null;
    requestSnapshot();
  }, [requestSnapshot, mode, site]);

  if (!bounds) return null;
  return (
    <div className={`${styles.panoMinimap} ${showAreas && mode === 'site' ? styles.panoMinimapSite : ''}`}>
      <canvas
        ref={canvasRef}
        className={styles.panoMinimapCanvas}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      />
      {showAreas && mode === 'around' && areas && (() => {
        const items = arrows.list.flatMap((ar) => {
          const a = areas.find((x) => x.key === ar.key);
          if (!a) return [];
          const dist = formatAreaDistance(headingToArea(a, currentPano.position).distance);
          return [{ ar, a, dist, width: chipWidth(a.name, dist) }];
        });
        const boxes = new Map(layoutEdgeChips(items.map((i) => ({ key: i.a.key, nx: i.ar.nx, ny: i.ar.ny, width: i.width })), arrows.w, arrows.h).map((b) => [b.key, b]));
        return items.map(({ ar, a, dist, width }) => {
        const box = boxes.get(a.key);
        if (!box) return null;
        return (
          <button
            key={ar.key}
            type="button"
            className={styles.mmArrow}
            style={{ left: box.left, top: box.top, width }}
            onClick={() => onGoArea?.(a.key)}
            aria-label={`Go to ${a.name}, ${dist}`}
            title={`Go to ${a.name}`}
          >
            <Icon name="navigation" className={styles.mmArrowIcon} style={{ color: a.colour, transform: `rotate(${90 - (ar.angle * 180) / Math.PI}deg)` }} />
            <span className={styles.mmArrowName}>{a.name}</span>
            <span className={styles.mmArrowDist}>{dist}</span>
          </button>
        );
        });
      })()}
      {showAreas && (
        <div className={styles.mmModes} role="group" aria-label="Minimap view">
          <button type="button" className={mode === 'around' ? styles.mmModeOn : styles.mmMode} aria-pressed={mode === 'around'} onClick={() => chooseMode('around')}>Around you</button>
          <button type="button" className={mode === 'site' ? styles.mmModeOn : styles.mmMode} aria-pressed={mode === 'site'} onClick={() => chooseMode('site')}>Whole site</button>
        </div>
      )}
    </div>
  );
}

const NO_AREA_NAMES: TourAreaName[] = [];
const NO_AREA_GROUPS: TourAreaGroup[] = [];

// A panorama has no inherent range and a flat photo has no depth, so true
// occlusion ("is this behind a wall?") isn't possible. Instead a measurement is
// only shown if it's within the station's rough coverage radius — derived from
// the spacing to the nearest neighbouring scan, clamped to this range.
const PANO_MEASURE_RADIUS_MIN    = 6;
// How long per-file station and occupancy answers are gathered before one update.
const PANO_BATCH_MS = 250;
const PANO_MEASURE_RADIUS_MAX    = 18;
const PANO_MEASURE_RADIUS_FACTOR = 1.5;

function dist3(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

function pointInBox(p: Vec3, min: Vec3, max: Vec3): boolean {
  return p.x >= min.x && p.x <= max.x &&
         p.y >= min.y && p.y <= max.y &&
         p.z >= min.z && p.z <= max.z;
}

// Project saved measurements (cloud-frame 3D) into PSV markers for the current
// panorama viewpoint: a polyline (line) or filled polygon (area) plus a label.
// Each edge is subdivided so a straight 3D segment renders as the correct curve
// on the sphere — same projection (tourMarkerYawPitch) used for the floor rings.
// Measurements whose nearest point is beyond maxDist of the station are skipped.
function buildMeasureMarkers(
  conv: PanoConvention,
  from: PanoramaEntry,
  measurements: MeasurementItem[],
  hidden: Set<string>,
  maxDist: number,
  occluded: Set<string>,
): MarkerConfig[] {
  const out: MarkerConfig[] = [];
  const proj = (p: Vec3): [number, number] => {
    const { yaw, pitch } = tourMarkerYawPitch(conv, from.position, from.quaternion, p);
    return [yaw, pitch];
  };
  const pushEdge = (a: Vec3, b: Vec3, steps: number, arr: [number, number][]) => {
    for (let s = 0; s < steps; s++) {
      const t = s / steps;
      arr.push(proj({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t }));
    }
  };
  const nearEnough = (pts: Vec3[]) => pts.some((p) => dist3(p, from.position) <= maxDist);
  const label = (id: string, at: [number, number], text: string): MarkerConfig => ({
    id,
    position: { yaw: at[0], pitch: at[1] },
    html: `<div class="${styles.panoMeasureLabel}">${text}</div>`,
    anchor: 'center center',
  });

  for (const m of measurements) {
    if (hidden.has(m.id) || occluded.has(m.id)) continue;
    if (m.kind === 'area' && m.points && m.points.length >= 3) {
      if (!nearEnough(m.points)) continue;
      const poly: [number, number][] = [];
      for (let i = 0; i < m.points.length; i++) pushEdge(m.points[i], m.points[(i + 1) % m.points.length], 10, poly);
      const c = { x: 0, y: 0, z: 0 };
      m.points.forEach((p) => { c.x += p.x; c.y += p.y; c.z += p.z; });
      c.x /= m.points.length; c.y /= m.points.length; c.z /= m.points.length;
      out.push({
        id: `measure-area-${m.id}`,
        polygon: poly,
        svgStyle: { fill: 'rgba(56,189,248,0.28)', stroke: '#0ea5e9', strokeWidth: '3px' },
      });
      out.push(label(`measure-area-lbl-${m.id}`, proj(c), `${(m.area ?? 0).toFixed(2)} m²`));
    } else if (m.kind !== 'area' && m.start && m.end) {
      if (!nearEnough([m.start, m.end])) continue;
      const line: [number, number][] = [];
      pushEdge(m.start, m.end, 24, line);
      line.push(proj(m.end));
      const mid = { x: (m.start.x + m.end.x) / 2, y: (m.start.y + m.end.y) / 2, z: (m.start.z + m.end.z) / 2 };
      out.push({
        id: `measure-line-${m.id}`,
        polyline: line,
        svgStyle: { stroke: '#0ea5e9', strokeWidth: '3px', fill: 'none' },
      });
      out.push(label(`measure-line-lbl-${m.id}`, proj(mid), `${(m.distance ?? 0).toFixed(2)} m`));
    }
  }
  return out;
}

// Project note (annotation) icons into PSV markers for the current panorama,
// distance- and occlusion-filtered the same way as measurements.
function buildNoteMarkers(
  conv: PanoConvention,
  from: PanoramaEntry,
  annotations: Annotation[],
  maxDist: number,
  occluded: Set<string>,
): MarkerConfig[] {
  const out: MarkerConfig[] = [];
  for (const a of annotations) {
    if (occluded.has(`note:${a.id}`)) continue;
    if (dist3(a.position, from.position) > maxDist) continue;
    const { yaw, pitch } = tourMarkerYawPitch(conv, from.position, from.quaternion, a.position);
    const color = a.status === 'resolved' ? '#22c55e' : '#f97316';
    const tip = (a.text || '').replace(/[<>&]/g, '').slice(0, 140);
    out.push({
      id: `note-${a.id}`,
      position: { yaw, pitch },
      html: `<div class="${styles.panoNoteMarker}" style="background:${color}">${iconHtml('chat')}</div>`,
      anchor: 'center center',
      tooltip: tip || undefined,
    });
  }
  return out;
}

// ── Panorama overlay (preserved as-is from previous iteration) ───────────────

interface PanoramaOverlayProps {
  panoramas:      PanoramaEntry[];
  startId:        string;
  initialHeading?: number;
  initialPitch?:   number;
  measurements?:  MeasurementItem[];
  hiddenMeasures?: Set<string>;
  annotations?:   Annotation[];
  // Rail-hidden stations: kept in `panoramas` (the CURRENT station may be one —
  // filtering the list would break navigation state) but dropped from tour rings.
  hiddenStations?: Set<string>;
  occIndex?:      OccIndex | null;   // precomputed occupancy grid → instant marker occlusion
  activeZone?:    { min: Vec3; max: Vec3 } | null;
  iframeRef:      React.RefObject<HTMLIFrameElement>;
  post:           (cmd: Record<string, unknown>) => void;
  onClose:        (direction?: { heading: number; pitch: number }) => void;
  // Advanced Virtual Tour; null without the permission.
  tour?:          TourExtras | null;
  // The station shown now (it changes without the overlay closing).
  onStation?:     (id: string) => void;
}

interface TourExtras {
  areas:     TourArea[];
  areaOf:    Map<string, TourArea>;
  map:       TourMapModel | null;
  // Compass heading of the scene's +Y.
  northDeg:  number;
  compassOn: boolean;
  mapOn:     boolean;
  onCompass: (on: boolean) => void;
  onMap:     (on: boolean) => void;
}

function PanoramaOverlay({ panoramas, startId, initialHeading, initialPitch, measurements, hiddenMeasures, annotations, hiddenStations, occIndex, activeZone, iframeRef, post, onClose, tour, onStation }: PanoramaOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const psvRef = useRef<PSViewer | null>(null);
  const markersPluginRef = useRef<MarkersPlugin | null>(null);
  const loadedUrlRef = useRef<string>('');
  const nextYawRef = useRef<{ yaw: number; pitch: number } | null>(null);
  const [currentIdx, setCurrentIdx] = useState(() => {
    const i = panoramas.findIndex((p) => p.id === startId);
    return i >= 0 ? i : 0;
  });
  const current = panoramas[currentIdx];
  // Latest panorama and PSV refs needed by the close handler — keep them in
  // refs so close can read them without becoming a stale closure.
  const currentRef = useRef(current);
  currentRef.current = current;

  // Markers (measurements, notes, neighbouring stations) determined to be hidden
  // behind point-cloud geometry from this station — computed by the Potree
  // iframe (see the occlusion effect below). occlusionOn toggles the whole thing.
  const [occlusionOn, setOcclusionOn] = useState(true);
  // Ring height: the floor measured under each station from the occupancy grid,
  // else a fixed scanner height. Tripods differ by over a metre within one file,
  // so the fixed height alone put rings on box tops and in the air.
  const [floorOn, setFloorOn] = useState(true);
  const floorByStation = useMemo(() => {
    const floors = new Map<string, number>();
    if (occIndex) {
      for (const p of panoramas) {
        const z = floorBelow(occIndex, p.position);
        if (z != null) floors.set(p.id, z);
      }
    }
    return floors;
  }, [occIndex, panoramas]);
  const measuredFloorFor = useCallback(
    (p: PanoramaEntry) => floorByStation.get(p.id) ?? p.position.z - TOUR_SCANNER_HEIGHT,
    [floorByStation],
  );
  const floorZFor = useCallback(
    (p: PanoramaEntry) => (floorOn ? measuredFloorFor(p) : p.position.z - TOUR_SCANNER_HEIGHT),
    [floorOn, measuredFloorFor],
  );
  const [occludedIds, setOccludedIds] = useState<Set<string>>(new Set());
  const occlSeqRef = useRef(0);
  const occlOwnersRef = useRef<{ id: number; owners: string[] }>({ id: 0, owners: [] });
  const EMPTY_SET = useRef(new Set<string>()).current;

  const getEffectiveConvention = useCallback((p: PanoramaEntry): PanoConvention => {
    return p.convention ?? 'world-cw';
  }, []);
  const currentConvention = current ? getEffectiveConvention(current) : 'world-cw';

  const handleClose = useCallback(() => {
    const viewer = psvRef.current;
    const pano   = currentRef.current;
    if (!viewer || !pano) { onClose(); return; }
    const pos = viewer.getPosition();
    const conv = getEffectiveConvention(pano);
    const worldHeading = headingFromPsvYaw(conv, pos?.yaw ?? 0, pano.quaternion);
    onClose({ heading: worldHeading, pitch: pos?.pitch ?? 0 });
  }, [onClose, getEffectiveConvention]);

  // Switch to a different panorama while preserving the world direction the
  // user is currently looking at. Reads each panorama's own convention so a
  // mixed-convention project still transitions correctly.
  const switchPano = useCallback((targetIdx: number, worldHeadingOverride?: number) => {
    if (targetIdx < 0 || targetIdx >= panoramas.length) return;
    const cur = currentRef.current;
    const nxt = panoramas[targetIdx];
    if (!cur || !nxt) return;
    const pos = psvRef.current?.getPosition();
    const curConv = getEffectiveConvention(cur);
    const nxtConv = getEffectiveConvention(nxt);
    // Default: preserve the direction the user is currently looking. A hop can
    // override with the world bearing toward the station it clicked.
    const worldHeading = typeof worldHeadingOverride === 'number'
      ? worldHeadingOverride
      : headingFromPsvYaw(curConv, pos?.yaw ?? 0, cur.quaternion);
    nextYawRef.current = {
      yaw:   psvYawFromHeading(nxtConv, worldHeading, nxt.quaternion),
      pitch: pos?.pitch ?? 0,
    };
    setCurrentIdx(targetIdx);
  }, [panoramas, getEffectiveConvention]);

  // Marker-to-marker hop: instead of swapping the panorama in place, close it
  // (revealing the cloud), glide the cloud camera to the target scan position,
  // then re-open the panorama there — facing the same world direction the user
  // was looking. Kept in a ref so the (once-bound) marker listener stays current.
  const hopToPanorama = useCallback((panoId: string, travel = false) => {
    const target = panoramas.find((p) => p.id === panoId);
    const cur = currentRef.current;
    if (!target || !cur) return;
    // Face the panorama you clicked — the world direction from this scan station
    // toward the target — not the previous panorama's view direction.
    const dx = target.position.x - cur.position.x;
    const dy = target.position.y - cur.position.y;
    const heading = Math.hypot(dx, dy) > 1e-6 ? Math.atan2(dy, dx) : undefined;
    onClose(typeof heading === 'number' ? { heading, pitch: 0 } : undefined);
    post({ type: 'goToPanorama', id: panoId, heading, pitch: 0, travel });
  }, [onClose, post]);
  const hopRef = useRef(hopToPanorama);
  hopRef.current = hopToPanorama;

  const onStationRef = useRef(onStation);
  onStationRef.current = onStation;
  useEffect(() => { if (current) onStationRef.current?.(current.id); }, [current]);

  // Advanced Virtual Tour: the compass and the tour map follow the view, read
  // from the panorama viewer at most once a frame, and only while one is shown.
  const [look, setLook] = useState({ yaw: 0, pitch: 0, hFov: 90, n: 0 });
  const watchLook = !!tour && (tour.compassOn || tour.mapOn);
  useEffect(() => {
    if (!watchLook) return;
    let raf = 0;
    let poll = 0;
    let viewer: PSViewer | null = null;
    const read = () => {
      raf = 0;
      const v = psvRef.current;
      if (!v) return;
      const p = v.getPosition();
      setLook((l) => ({ yaw: p.yaw, pitch: p.pitch, hFov: v.state.hFov, n: l.n + 1 }));
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(read); };
    const attach = () => {
      viewer = psvRef.current;
      if (!viewer) { poll = window.setTimeout(attach, 100); return; }
      viewer.addEventListener('position-updated', schedule);
      viewer.addEventListener('zoom-updated', schedule);
      viewer.addEventListener('size-updated', schedule);
      viewer.addEventListener('panorama-loaded', schedule);
      schedule();
    };
    attach();
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(poll);
      viewer?.removeEventListener('position-updated', schedule);
      viewer?.removeEventListener('zoom-updated', schedule);
      viewer?.removeEventListener('size-updated', schedule);
      viewer?.removeEventListener('panorama-loaded', schedule);
    };
  }, [watchLook]);

  const urlById = useMemo(() => new Map(panoramas.map((p) => [p.id, p.url])), [panoramas]);
  const hereArea = tour && current ? tour.areaOf.get(current.id) ?? null : null;
  const showAreas = !!tour && tour.areas.length > 1;
  const horizon = useMemo(() => {
    if (!tour || !tour.compassOn || !current) return null;
    const conv = getEffectiveConvention(current);
    const items: HorizonArea[] = [];
    const aims = new Map<string, { yaw: number; pitch: number; stationId: string }>();
    for (const a of tour.areas) {
      if (a.key === hereArea?.key) continue;
      const h = headingToArea(a, current.position, tour.northDeg);
      const aim = tourMarkerYawPitch(conv, current.position, current.quaternion, h.station.position);
      aims.set(a.key, { yaw: aim.yaw, pitch: aim.pitch, stationId: h.station.id });
      items.push({
        key: a.key, name: a.name, colour: a.colour, scans: a.stations.length,
        distance: h.distance, bearing: h.bearing, photo: urlById.get(a.representative.id),
      });
    }
    return { items, aims };
  }, [tour, current, hereArea, getEffectiveConvention, urlById]);
  const lookHeading = current ? headingFromPsvYaw(currentConvention, look.yaw, current.quaternion) : 0;
  const placeBeacon = useCallback((key: string) => {
    const v = psvRef.current;
    const aim = horizon?.aims.get(key);
    if (!v || !aim) return null;
    const pt = v.dataHelper.sphericalCoordsToViewerCoords({ yaw: aim.yaw, pitch: aim.pitch });
    return Number.isFinite(pt.x) && Number.isFinite(pt.y) ? pt : null;
  // look.n: the same aims sit elsewhere on screen once the view turns or zooms.
  }, [horizon, look.n]); // eslint-disable-line react-hooks/exhaustive-deps
  const faceArea = useCallback((key: string) => {
    const aim = horizon?.aims.get(key);
    if (aim) psvRef.current?.animate({ yaw: aim.yaw, pitch: 0, speed: '12rpm' });
  }, [horizon]);
  const goToArea = useCallback((key: string) => {
    const area = tour?.areas.find((a) => a.key === key);
    const cur = currentRef.current;
    if (!area || !cur) return;
    const target = nearestStation(area.stations, cur.position);
    if (target.id !== cur.id) hopRef.current(target.id, true);
  }, [tour]);

  const updateMarkers = useCallback(() => {
    const plugin = markersPluginRef.current;
    if (!plugin) return;
    const from = panoramas[currentIdx];
    if (!from) return;
    const zone = activeZone ?? null;
    const inZone = (pt: Vec3) => !zone || pointInBox(pt, zone.min, zone.max);
    const measInZone = (m: MeasurementItem) => {
      const pts = m.points && m.points.length ? m.points : [m.start, m.end];
      return pts.every((pt: Vec3 | null | undefined) => !pt || inZone(pt));
    };
    const fromConv = getEffectiveConvention(from);
    const eyeFloor = floorZFor(from);
    const candidates = panoramas
      .filter((p) => p.id !== from.id && inZone(p.position) && !hiddenStations?.has(p.id))
      .map((p) => {
        const floorZ = floorZFor(p);
        const floorPos = { x: p.position.x, y: p.position.y, z: floorZ };
        const ringCenter = tourMarkerYawPitch(fromConv, from.position, from.quaternion, floorPos);
        const scanDist = Math.sqrt(
          (p.position.x - from.position.x) ** 2 +
          (p.position.y - from.position.y) ** 2 +
          (p.position.z - from.position.z) ** 2,
        );
        const { opacity } = tourMarkerStyle(scanDist);
        const horizontal = Math.hypot(p.position.x - from.position.x, p.position.y - from.position.y);
        const { w, v } = ringExtent(horizontal, from.position.z - floorZ);
        return { pano: p, scanDist, floorZ, yaw: ringCenter.yaw, pitch: ringCenter.pitch, w, v, opacity };
      })
      .filter((c) =>
        c.scanDist <= TOUR_MAX_DISTANCE &&
        (occIndex != null || Math.abs(c.pano.position.z - from.position.z) <= TOUR_MAX_Z_DELTA) &&
        !(occlusionOn && occludedIds.has(`tour:${c.pano.id}`))
      )
      .sort((a, b) => a.scanDist - b.scanDist);

    // A ring drawn on top of a nearer one reads as that ring: keep the nearer.
    const kept: typeof candidates = [];
    for (const c of candidates) {
      if (!kept.some((k) => ringsOverlap(c, k))) kept.push(c);
    }

    const tourMarkers: MarkerConfig[] = kept.map((c) => ({
      id: `tour-${c.pano.id}`,
      polygon: tourFloorRingPoints(fromConv, from, c.pano, c.floorZ),
      svgStyle: {
        fill:        `rgba(255, 255, 255, ${(0.10 * c.opacity).toFixed(3)})`,
        stroke:      `rgba(255, 255, 255, ${(0.95 * c.opacity).toFixed(3)})`,
        strokeWidth: '6px',
        ...(c.floorZ < eyeFloor - TOUR_LEVEL_STEP ? { strokeDasharray: '12 8' } : {}),
      },
      tooltip: {
        content:
          `<img src="${escAttr(c.pano.url)}" draggable="false" crossorigin="anonymous" ` +
          `style="width:200px;height:100px;object-fit:cover;display:block;border-radius:4px;margin-bottom:4px;background:#0b1220" />` +
          `Scan ${c.pano.index + 1} — ${c.scanDist.toFixed(1)} m` +
          (Math.abs(c.floorZ - eyeFloor) >= TOUR_LEVEL_STEP
            ? ` · ${Math.abs(c.floorZ - eyeFloor).toFixed(1)} m ${c.floorZ < eyeFloor ? 'below' : 'above'} your floor`
            : ''),
      },
      data: { panoId: c.pano.id },
    }));

    // A ring on another level than the viewer's floor gets a plumb line from the
    // viewer's floor plane to it: without one, a ring 0.6 m lower looks exactly
    // like a nearer ring on this floor — a circle alone carries no depth.
    const levelMarkers: MarkerConfig[] = kept
      .filter((c) => Math.abs(c.floorZ - eyeFloor) >= TOUR_LEVEL_STEP)
      .map((c) => {
        const pts: Array<[number, number]> = [];
        for (let i = 0; i <= 8; i++) {
          const z = eyeFloor + (c.floorZ - eyeFloor) * (i / 8);
          const { yaw, pitch } = tourMarkerYawPitch(fromConv, from.position, from.quaternion, { x: c.pano.position.x, y: c.pano.position.y, z });
          pts.push([yaw, pitch]);
        }
        return {
          id: `level-${c.pano.id}`,
          polyline: pts,
          svgStyle: { stroke: `rgba(255, 255, 255, ${(0.9 * c.opacity).toFixed(3)})`, strokeWidth: '3px', strokeDasharray: '8 6', fill: 'none' },
        };
      });

    // Coverage radius for this station ≈ spacing to its nearest neighbour scan,
    // clamped — measurements beyond it are hidden (no depth → no true occlusion).
    let nearestScan = Infinity;
    for (const p of panoramas) {
      if (p.id === from.id) continue;
      const d = Math.hypot(p.position.x - from.position.x, p.position.y - from.position.y, p.position.z - from.position.z);
      if (d < nearestScan) nearestScan = d;
    }
    const measureRadius = Number.isFinite(nearestScan)
      ? Math.min(Math.max(nearestScan * PANO_MEASURE_RADIUS_FACTOR, PANO_MEASURE_RADIUS_MIN), PANO_MEASURE_RADIUS_MAX)
      : PANO_MEASURE_RADIUS_MAX;

    const occ = occlusionOn ? occludedIds : EMPTY_SET;
    const measureMarkers = buildMeasureMarkers(fromConv, from, (measurements ?? []).filter(measInZone), hiddenMeasures ?? new Set(), measureRadius, occ);
    const noteMarkers    = buildNoteMarkers(fromConv, from, (annotations ?? []).filter((a) => inZone(a.position)), measureRadius, occ);
    plugin.setMarkers([...tourMarkers, ...levelMarkers, ...measureMarkers, ...noteMarkers]);
  }, [currentIdx, panoramas, getEffectiveConvention, measurements, hiddenMeasures, annotations, hiddenStations, occludedIds, occlusionOn, floorZFor, occIndex, EMPTY_SET, activeZone]);

  // Re-render markers when inputs (measurements, notes, their visibility, the
  // occlusion set, the occlusion or floor toggle) change while a panorama is open.
  // Uses a ref so this doesn't re-fire on every pano switch (the pano-change
  // effect already refreshes markers after the image loads).
  const updateMarkersRef = useRef(updateMarkers);
  updateMarkersRef.current = updateMarkers;
  useEffect(() => {
    if (markersPluginRef.current) updateMarkersRef.current();
  }, [measurements, hiddenMeasures, annotations, hiddenStations, occludedIds, occlusionOn, floorZFor, activeZone]);

  // Ask the Potree iframe which markers (measurements, notes, neighbouring
  // stations) are occluded from this station. Debounced; results arrive via the
  // 'occlusionResult' message handler below. Skipped entirely when toggled off.
  useEffect(() => {
    const from = panoramas[currentIdx];
    if (!occlusionOn || !from) { setOccludedIds(EMPTY_SET); return; }
    let nearestScan = Infinity;
    for (const p of panoramas) {
      if (p.id === from.id) continue;
      const d = dist3(from.position, p.position);
      if (d < nearestScan) nearestScan = d;
    }
    const radius = Number.isFinite(nearestScan)
      ? Math.min(Math.max(nearestScan * PANO_MEASURE_RADIUS_FACTOR, PANO_MEASURE_RADIUS_MIN), PANO_MEASURE_RADIUS_MAX)
      : PANO_MEASURE_RADIUS_MAX;

    const samples: Vec3[] = [];
    const owners: string[] = [];
    const add = (id: string, p: Vec3) => { samples.push({ x: p.x, y: p.y, z: p.z }); owners.push(id); };

    // Neighbouring scan stations (tour rings): the grid tests the sight line to
    // each ring's floor spot (ringOccluded) — the measured floor whatever the
    // floor button says, a ring drawn 1.5 m under a scanner is not where the
    // floor is; the GPU fallback tests eye level.
    const ringFloor = new Map<string, number>();
    for (const p of panoramas) {
      if (p.id === from.id) continue;
      if (dist3(p.position, from.position) > TOUR_MAX_DISTANCE) continue;
      if (!occIndex && Math.abs(p.position.z - from.position.z) > TOUR_MAX_Z_DELTA) continue;
      add(`tour:${p.id}`, p.position);
      ringFloor.set(`tour:${p.id}`, measuredFloorFor(p));
    }

    // Measurements (sampled at endpoints/vertices + mid/centroid).
    for (const m of measurements ?? []) {
      if (hiddenMeasures?.has(m.id)) continue;
      if (m.kind === 'area' && m.points && m.points.length >= 3) {
        if (!m.points.some((p) => dist3(p, from.position) <= radius)) continue;
        const c = { x: 0, y: 0, z: 0 };
        m.points.forEach((p) => { c.x += p.x; c.y += p.y; c.z += p.z; });
        add(m.id, { x: c.x / m.points.length, y: c.y / m.points.length, z: c.z / m.points.length });
        m.points.forEach((p) => add(m.id, p));
      } else if (m.kind !== 'area' && m.start && m.end) {
        if (dist3(m.start, from.position) > radius && dist3(m.end, from.position) > radius) continue;
        add(m.id, m.start);
        add(m.id, { x: (m.start.x + m.end.x) / 2, y: (m.start.y + m.end.y) / 2, z: (m.start.z + m.end.z) / 2 });
        add(m.id, m.end);
      }
    }

    // Notes (single sample at the annotation point).
    for (const a of annotations ?? []) {
      if (dist3(a.position, from.position) > radius) continue;
      add(`note:${a.id}`, a.position);
    }

    if (samples.length === 0) { setOccludedIds(EMPTY_SET); return; }

    // Grid path: raycast every sample against the precomputed occupancy grid —
    // instant, smooth, cross-file, load-independent, no iframe round-trip. A marker
    // is hidden only if EVERY one of its samples is occluded (any visible keeps it).
    if (occIndex) {
      const visibleOwners = new Set<string>();
      const eyeFloor = measuredFloorFor(from);
      for (let i = 0; i < samples.length; i++) {
        const floor = ringFloor.get(owners[i]);
        const hidden = floor !== undefined
          ? ringOccluded(occIndex, from.position, eyeFloor, samples[i], floor)
          : isOccluded(occIndex, from.position, samples[i]);
        if (!hidden) visibleOwners.add(owners[i]);
      }
      const occluded = new Set<string>();
      owners.forEach((o) => { if (!visibleOwners.has(o)) occluded.add(o); });
      setOccludedIds(occluded);
      return;
    }

    // Fallback (older files with no grid): hide tour markers until the first live
    // occlusion result, then a fixed set of bounded GPU-pick passes, then stop.
    setOccludedIds(new Set(owners.filter((o) => o.startsWith('tour:'))));
    const reqId = ++occlSeqRef.current;
    occlOwnersRef.current = { id: reqId, owners };
    const fire = () => post({ type: 'occlusionTest', requestId: reqId, origin: from.position, samples, tolerance: 0.5 });
    const timers = OCCL_PASS_MS.map((ms) => window.setTimeout(fire, ms));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [currentIdx, panoramas, measurements, hiddenMeasures, annotations, occlusionOn, post, EMPTY_SET, occIndex, measuredFloorFor]);

  // Receive occlusion results: a marker is hidden only if every one of its sampled
  // points is occluded (any visible sample keeps it shown). Just apply — the two
  // bounded passes are scheduled in the effect above; there is no re-poll here.
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || e.data.type !== 'occlusionResult') return;
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      const req = occlOwnersRef.current;
      if (!req || e.data.requestId !== req.id) return;
      const visible: boolean[] = Array.isArray(e.data.visible) ? e.data.visible : [];
      const anyVisible = new Set<string>();
      req.owners.forEach((owner, i) => { if (visible[i]) anyVisible.add(owner); });
      const occluded = new Set<string>();
      req.owners.forEach((owner) => { if (!anyVisible.has(owner)) occluded.add(owner); });
      setOccludedIds(occluded);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    let viewer: PSViewer | null = null;
    let aborted = false;
    const timer = window.setTimeout(() => {
      if (aborted || !containerRef.current) return;
      const p = panoramas[currentIdx];
      const conv = getEffectiveConvention(p);
      loadedUrlRef.current = p.url;
      const startYaw = typeof initialHeading === 'number'
        ? psvYawFromHeading(conv, initialHeading, p.quaternion)
        : 0;
      viewer = new PSViewer({
        container: containerRef.current,
        panorama: p.url,
        navbar: false,
        loadingTxt: '',
        defaultYaw: startYaw,
        defaultPitch: typeof initialPitch === 'number' ? initialPitch : 0,
        minFov: 30,
        maxFov: 120,
        plugins: [MarkersPlugin],
      });
      psvRef.current = viewer;
      const plugin = viewer.getPlugin<MarkersPlugin>(MarkersPlugin);
      markersPluginRef.current = plugin;
      plugin.addEventListener('select-marker', (ev: { marker: Marker }) => {
        const data = (ev.marker as unknown as { config?: { data?: { panoId?: string } } }).config?.data;
        const panoId = data?.panoId;
        if (panoId) hopRef.current(panoId);
      });
      viewer.addEventListener('ready', () => updateMarkersRef.current());
    }, 50);
    return () => {
      aborted = true;
      window.clearTimeout(timer);
      viewer?.destroy();
      psvRef.current = null;
      markersPluginRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const viewer = psvRef.current;
    if (!viewer) return;
    const p = panoramas[currentIdx];
    if (p.url === loadedUrlRef.current) return;
    loadedUrlRef.current = p.url;
    markersPluginRef.current?.clearMarkers();
    viewer.setPanorama(p.url, { transition: false }).then(() => {
      if (nextYawRef.current) {
        viewer.rotate(nextYawRef.current);
        nextYawRef.current = null;
      } else {
        viewer.rotate({ yaw: 0, pitch: 0 });
      }
      updateMarkersRef.current();
    }).catch(() => {});
  }, [currentIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (delta: number) =>
    setCurrentIdx((i) => (i + delta + panoramas.length) % panoramas.length);

  return (
    <div className={styles.panoOverlay}>
      <div className={styles.panoHeader}>
        <div className={styles.panoHeaderLeft}>
          <Icon name="panorama" style={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
          <span className={styles.panoTitle}>{current.image}</span>
          <span className={styles.panoBadge}>{String(currentIdx + 1).padStart(2, '0')} / {String(panoramas.length).padStart(2, '0')}</span>
          {showAreas && hereArea && (
            <span className={styles.panoArea} title="The area you are in: scans the rings connect">
              <span className={styles.panoAreaDot} style={{ background: hereArea.colour }} />
              <span className={styles.panoAreaName}>{hereArea.name}</span>
              <span className={styles.panoAreaCount}>{hereArea.stations.findIndex((s) => s.id === current.id) + 1} of {hereArea.stations.length}</span>
            </span>
          )}
        </div>
        <div className={styles.panoHeaderRight}>
          {tour && (
            <>
              <button
                className={styles.panoNavBtn}
                onClick={() => tour.onCompass(!tour.compassOn)}
                aria-pressed={tour.compassOn}
                aria-label="Compass and beacons"
                title={tour.compassOn
                  ? 'Hide the compass and the beacons to the other areas'
                  : 'Show a compass, and beacons pointing to the other areas of the tour'}
                style={{ color: tour.compassOn ? 'var(--sv-cyan)' : undefined }}
              >
                <Icon name="navigation" style={{ fontSize: 18 }} />
              </button>
              <button
                className={styles.panoNavBtn}
                onClick={() => tour.onMap(!tour.mapOn)}
                disabled={!tour.map}
                aria-pressed={tour.mapOn}
                aria-label="Tour map"
                title={tour.mapOn ? 'Hide the tour map' : 'Show the tour map: every scan, its areas and floors in 3D'}
                style={{ color: tour.mapOn ? 'var(--sv-cyan)' : undefined }}
              >
                <Icon name="hub" style={{ fontSize: 18 }} />
              </button>
              <span className={styles.panoHeaderDivider} />
            </>
          )}
          <button
            className={styles.panoNavBtn}
            onClick={() => setOcclusionOn((v) => !v)}
            title={occlusionOn
              ? 'Occlusion on — markers behind walls are hidden (click to show all)'
              : 'Occlusion off — showing all markers (click to hide occluded)'}
            style={{ color: occlusionOn ? 'var(--sv-cyan)' : undefined }}
          >
            <Icon name={occlusionOn ? 'blur_on' : 'blur_off'} style={{ fontSize: 18 }} />
          </button>
          <button
            className={styles.panoNavBtn}
            onClick={() => setFloorOn((v) => !v)}
            disabled={floorByStation.size === 0}
            title={floorByStation.size === 0
              ? 'No occupancy grid for this cloud — rings sit a fixed 1.5 m below each scanner'
              : floorOn
                ? 'Rings on the floor measured under each scanner (click for a fixed 1.5 m below the scanner)'
                : 'Rings a fixed 1.5 m below each scanner (click for the measured floor)'}
            style={{ color: floorOn && floorByStation.size > 0 ? 'var(--sv-cyan)' : undefined }}
          >
            <Icon name="stairs" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.panoNavBtn} onClick={() => go(-1)} title="Previous" disabled={panoramas.length < 2}>
            <Icon name="chevron_left" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.panoNavBtn} onClick={() => go(1)}  title="Next"     disabled={panoramas.length < 2}>
            <Icon name="chevron_right" style={{ fontSize: 18 }} />
          </button>
          <button className={styles.panoCloseBtn} onClick={handleClose} title="Close">
            <Icon name="close" style={{ fontSize: 18 }} />
          </button>
        </div>
      </div>
      <div className={styles.viewModeSwitch}>
        <button
          className={styles.viewModeBtn}
          onClick={() => {
            const viewer = psvRef.current;
            const pano   = currentRef.current;
            if (viewer && pano) {
              const pos = viewer.getPosition();
              const conv = getEffectiveConvention(pano);
              const worldHeading = headingFromPsvYaw(conv, pos?.yaw ?? 0, pano.quaternion);
              const worldPitch   = pos?.pitch ?? 0;
              post({
                type: 'setCameraPose',
                position: pano.position,
                heading: worldHeading,
                pitch: worldPitch,
              });
            }
            onClose();
          }}
          title="Show point cloud at current panorama position"
        >
          <Icon name="scatter_plot" />
          Point Cloud
        </button>
        <div className={styles.viewModeDivider} />
        <button
          className={`${styles.viewModeBtn} ${styles.viewModeBtnActive}`}
          onClick={() => {
            if (panoramas.length <= 1) return;
            const others: number[] = [];
            for (let i = 0; i < panoramas.length; i++) if (i !== currentIdx) others.push(i);
            const idx = others[Math.floor(Math.random() * others.length)];
            switchPano(idx);
          }}
          disabled={panoramas.length <= 1}
          title="Switch to a random panorama"
        >
          <Icon name="tour" />
          Virtual Tour
        </button>
      </div>
      <div className={styles.panoStage}>
        <div ref={containerRef} className={styles.panoViewer} />
        {horizon && (
          <TourHorizon
            areas={horizon.items}
            viewBearing={bearingOfHeading(lookHeading, tour?.northDeg ?? 0)}
            hFov={look.hFov}
            place={placeBeacon}
            onGo={goToArea}
            onFace={faceArea}
          />
        )}
      </div>
      <PanoMinimap
        panoramas={panoramas}
        currentPano={current}
        convention={currentConvention}
        psvRef={psvRef}
        iframeRef={iframeRef}
        post={post}
        onSelect={(target) => {
          const idx = panoramas.findIndex((p) => p.id === target.id);
          if (idx >= 0) switchPano(idx);
        }}
        areas={showAreas ? tour?.areas : undefined}
        areaOf={showAreas ? tour?.areaOf : undefined}
        onGoArea={showAreas ? goToArea : undefined}
      />
      {tour?.mapOn && tour.map && (
        <TourMap
          map={tour.map}
          areas={tour.areas}
          currentStationId={current.id}
          heading={lookHeading}
          photoOf={(id) => urlById.get(id)}
          onGo={(id) => hopRef.current(id, true)}
          onClose={() => tour.onMap(false)}
        />
      )}
    </div>
  );
}

// ── Nav mode segmented control ───────────────────────────────────────────────

const NAV_MODES: { id: NavMode; label: string; icon: IconName }[] = [
  { id: 'orbit',       label: 'Orbit',     icon: '3d_rotation' },
  { id: 'firstPerson', label: 'First-Person', icon: 'view_in_ar' },
  { id: 'walk',        label: 'Walk',      icon: 'directions_walk' },
];

interface ModeSwitchProps { value: NavMode; onChange: (m: NavMode) => void; disabled?: boolean; }

function ModeSwitch({ value, onChange, disabled }: ModeSwitchProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 3, width: 0 });

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const idx = NAV_MODES.findIndex((m) => m.id === value);
    const btn = container.querySelectorAll<HTMLButtonElement>('button')[idx];
    if (btn) setIndicator({ left: btn.offsetLeft, width: btn.offsetWidth });
  }, [value]);

  return (
    <div className={styles.modeSwitch} ref={containerRef}>
      <div className={styles.modeIndicator} style={{ left: indicator.left, width: indicator.width }} />
      {NAV_MODES.map((m) => (
        <button
          key={m.id}
          type="button"
          className={`${styles.modeOption} ${value === m.id ? styles.modeOptionActive : ''}`}
          onClick={() => onChange(m.id)}
          disabled={disabled}
        >
          <Icon name={m.icon} />
          <span className={styles.modeText}>{m.label}</span>
        </button>
      ))}
    </div>
  );
}

// ── Floating panel wrapper ───────────────────────────────────────────────────

interface FloatingPanelProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}
function FloatingPanel({ title, onClose, children, wide }: FloatingPanelProps) {
  return (
    <div className={`${styles.panel} ${wide ? styles.panelWide : ''}`} onClick={(e) => e.stopPropagation()}>
      <div className={styles.panelHeader}>
        <div className={`${styles.panelTitle} ${styles.panelTitleAccent}`}>
          <span>{title}</span>
        </div>
        <button className={styles.panelCloseBtn} onClick={onClose} title="Close">
          <Icon name="close" />
        </button>
      </div>
      <div className={styles.panelBody}>{children}</div>
    </div>
  );
}

// Slider value readout that turns into a small input on click, for typing exact
// alignment values the slider is too coarse for. Module-level so its identity is
// stable across SmartView renders — the input must not remount (and lose focus)
// while the user types. Commit on Enter/blur, cancel on Escape, clamped to the
// slider's range.
const BimValueInput = ({ value, min, max, fmt, onCommit }: {
  value: number; min: number; max: number;
  fmt: (v: number) => string; onCommit: (v: number) => void;
}) => {
  const [draft, setDraft] = useState<string | null>(null);
  const commit = () => {
    if (draft === null) return;
    setDraft(null);
    const v = parseFloat(draft.replace(',', '.'));
    if (Number.isFinite(v)) onCommit(Math.min(max, Math.max(min, v)));
  };
  if (draft === null) {
    return (
      <button
        type="button"
        onClick={() => setDraft(String(Math.round(value * 1000) / 1000))}
        title="Click to type an exact value"
        style={{
          width: 46, textAlign: 'right', color: '#cbd5e1', background: 'none', border: 'none',
          padding: 0, font: 'inherit', cursor: 'text',
          textDecoration: 'underline dotted rgba(148,163,184,0.55)', textUnderlineOffset: 2,
        }}
      >
        {fmt(value)}
      </button>
    );
  }
  return (
    <input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => e.target.select()}
      onBlur={commit}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === 'Enter') commit();
        else if (e.key === 'Escape') setDraft(null);
      }}
      style={{
        width: 46, textAlign: 'right', color: '#e2e8f0', background: 'rgba(148,163,184,0.12)',
        border: '1px solid rgba(148,163,184,0.35)', borderRadius: 4, padding: '0 3px',
        font: 'inherit', outline: 'none',
      }}
    />
  );
};

// A position slider's span is travel, not a limit, and it is STATE: derived
// from the value on every render, it re-centred under the thumb mid-drag and
// the value ran away. A drag stays inside it; only a value set from outside
// (typed, Gather, reset) or a new scene size moves it (freeWindow).
const FreeRange = ({ value, half, onChange }: {
  value: number; half: number; onChange: (v: number) => void;
}) => {
  const [win, setWin] = useState<SliderWindow & { basis: number }>(() => ({ ...freeWindow(value, half), basis: half }));
  let w = win;
  if (w.basis !== half || !inWindow(value, w)) {
    w = { ...freeWindow(value, half), basis: half };
    setWin(w);
  }
  return (
    <input type="range" min={w.centre - w.half} max={w.centre + w.half} step={w.half / 200} value={value}
      onChange={(e) => onChange(parseFloat(e.target.value))} style={{ flex: 1 }} />
  );
};

// ── Main Smart View ──────────────────────────────────────────────────────────

// What the Combined View rail lists per category (notes / photos / measurements /
// zones) with the current visibility state — pushed via onInventoryChange.
// Camera pose in the LAZ world frame (georeferenced CRS metres), streamed from
// the viewer iframe — Combined View maps it onto a georeferenced DWG.
export interface CameraPose { x: number; y: number; z: number; heading: number }

// deletable: false hides the row's delete button (on share links a guest may
// delete only measurements made through the same link).
export interface SmartViewRailItem { id: string; label: string; visible: boolean; deletable?: boolean }
// What a share link lets its guests measure and save (the link's options).
export interface SmartViewGuestMeasures { pointCloud: boolean; ortho: boolean }
// georef: false = the file has no georeference and sits where the scene put it
// (undefined until the viewer has loaded it).
export interface SmartViewBimItem { id: string; visible: boolean; converting: boolean; error?: string; georef?: boolean }
export interface SmartViewLayerItem { id: string; kind: SceneLayerKind; visible: boolean; loading: boolean; error?: string; georef?: boolean }
// The project as the Scene needs it: its grid and base map choices. Share
// links hand it over from the share-link query.
export interface SceneProjectInfo {
  id: string;
  epsg?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  basemap?: Partial<ProjectBasemap> | null;
  sitePlacement?: SitePlacement | null;
}
export interface SmartViewBasemap {
  providers: { id: string; label: string }[];
  prefs: ProjectBasemap;
  state: BasemapState;
  frame: BasemapFrameSource | null;
  // Whether the grid matches the scan; when not, the map is placed instead.
  gridFit: GridFit;
  // What the Position menu can offer, and what Automatic picks now.
  sources: { file: boolean; pin: boolean; auto: BasemapFrameSource | null };
  // A fine-tune is in effect on the current source.
  tuned: boolean;
  gridAdjust: GridAdjust | null;
  epsg: number | null;
  placement: SitePlacement | null;
  terrainAvailable: boolean;
  status: { active: boolean; tiles: number; loading: number; error: string | null; autoHeight: number | null; failed: number; lastError: string | null };
  canEdit: boolean;
  // The rail's changes differ from what the project has saved.
  unsaved: boolean;
  saving: boolean;
}
export interface SmartViewInventory {
  // The map under the scene: null until the providers are known.
  basemap: SmartViewBasemap | null;
  notes: SmartViewRailItem[];
  // Notes of each splat / orthomosaic layer that is on, by file id.
  layerNotes: Record<string, SmartViewRailItem[]>;
  photos: SmartViewRailItem[];
  measurements: SmartViewRailItem[];
  // Measurements of each orthomosaic layer that is on, by file id.
  layerMeasurements: Record<string, SmartViewRailItem[]>;
  zones: { id: string; label: string; active: boolean }[];
  // RVT/IFC models that can be drawn over the cloud; empty when the project's
  // owner has no BIM-in-scene permission.
  bim: SmartViewBimItem[];
  // Orthomosaics, DWG drawings and Gaussian splats drawable inside the scene.
  layers: SmartViewLayerItem[];
  // Virtual Tour areas (Advanced Virtual Tour only; empty otherwise), how they
  // are made, and whether this user may rename, re-split and draw them.
  areas: RailArea[];
  areaMode: TourAreaMode;
  canRenameAreas: boolean;
  // The viewer iframe has booted: commands posted before this are dropped.
  loaded: boolean;
}

interface SmartViewProps {
  shareToken?: string;
  sharedFiles?: PCFile[];
  // Share mode has no file queries: the non-point-cloud files the link
  // exposes (ortho / drawings / splats / BIM) arrive from the share page.
  sharedLayerFiles?: PCFile[];
  // Entry configuration (the project page's menu): layers to switch on once
  // the viewer is up. Whether the clouds start hidden is read from the page
  // URL (?base=splat|ortho) so it is known before any file list arrives.
  startLayers?: string[];
  shareProjectId?: string;
  projectId?: string;
  embedded?: boolean;
  access?: FeatureAccess;
  guestMeasures?: SmartViewGuestMeasures;
  sharedProject?: SceneProjectInfo;
  // Enter the panorama nearest the camera as soon as the stations are in the
  // viewer (the project page's "Virtual tour" entry).
  autoTour?: boolean;
  // The Scene opens a note's linked file in its split pane; standalone
  // viewers navigate to the file's own viewer instead.
  onOpenLinkedFile?: (file: LinkedFile) => void;
  onVisibilityChange?: (fileId: string, visible: boolean) => void;
  onVisibilityInit?: (vis: Record<string, boolean>) => void;
  onInventoryChange?: (inv: SmartViewInventory) => void;
  onCameraPose?: (pose: CameraPose) => void;
}

export interface SmartViewHandle {
  setFileVisible(fileId: string, visible: boolean): void;
  setNoteVisible(id: string, visible: boolean): void;
  setStationVisible(id: string, visible: boolean): void;
  setMeasurementVisible(id: string, visible: boolean): void;
  setActiveZone(id: string | null): void;
  setBimVisible(fileId: string, visible: boolean): void;
  setLayerVisible(fileId: string, visible: boolean): void;
  // Open the placement panel (move / rotate / scale / opacity) for a layer or BIM model.
  openAlign(fileId: string): void;
  openClosestPanorama(): void;
  focusNote(id: string): void;
  flyToStation(id: string): void;
  flyToMeasurement(id: string): void;
  flyToZone(id: string): void;
  deleteNote(id: string): void;
  deleteMeasurement(id: string): void;
  deleteZone(id: string): void;
  setBasemap(patch: Partial<ProjectBasemap>): void;
  nudgeBasemap(forward: number, right: number, turn: number): void;
  resetBasemapTune(): void;
  saveBasemap(): void;
  revertBasemap(): void;
  openSitePlacement(): void;
  // Fly to a Virtual Tour area and open its scan nearest to where you are.
  goToArea(key: string): void;
  // An empty name gives the area its automatic name back.
  renameArea(key: string, name: string): Promise<void>;
  setAreaMode(mode: TourAreaMode): Promise<void>;
  // The plan where areas are drawn by hand.
  openAreaEditor(): void;
}

const SmartView = forwardRef<SmartViewHandle, SmartViewProps>(function SmartView(
  { shareToken, sharedFiles, sharedLayerFiles, startLayers, shareProjectId, projectId: propProjectId, embedded, access: accessProp, guestMeasures, sharedProject, autoTour, onOpenLinkedFile, onVisibilityChange, onVisibilityInit, onInventoryChange, onCameraPose },
  handleRef,
) {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const projectId = shareProjectId ?? propProjectId ?? urlProjectId;
  const isShareMode = !!shareToken;
  const { user: currentUser } = useAuth();
  const isAdmin = !isShareMode && isSuperAdmin(currentUser);
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const topbarRef = useRef<HTMLDivElement>(null);
  // Ref (not dep) — pose messages stream at several Hz and must not resubscribe
  // the message handler.
  const onCameraPoseRef = useRef(onCameraPose);
  onCameraPoseRef.current = onCameraPose;

  const [visibility, setVisibility] = useState<Record<string, boolean>>({});
  // ?base=splat|ortho: the point clouds are loaded but start switched off, so
  // the ortho / splat is the only base until the user ticks a cloud.
  const cloudsHiddenAtStart = useRef(['splat', 'ortho'].includes(new URLSearchParams(window.location.search).get('base') || '')).current;
  const [loaded, setLoaded] = useState(false);
  const [cloudsVisible, setCloudsVisible] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [openPanel, setOpenPanel] = useState<PanelKind>(null);
  const [navMode, setNavMode]   = useState<NavMode>('orbit');
  const [pointSize, setPointSize] = useState(1.0);
  const [opacity, setOpacity]     = useState(1.0);
  const [brightness, setBrightness] = useState(0);   // Potree rgbBrightness (-1..1, 0 = as-is)
  const [colorMode, setColorMode] = useState<ColorMode>('rgb');
  // EDL OFF by default (REQUIRED): EDL's post-process pass breaks the depth-buffer occlusion
  // that hides panorama/annotation markers behind the cloud — with EDL on they show THROUGH
  // the geometry. It also didn't measurably improve clarity in testing. ?edl=1 enables it for
  // A/B; the toolbar toggle flips it live.
  const [edlEnabled, setEdlEnabled] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('edl') === '1'; }
    catch { return false; }
  });

  const [annotationMode, setAnnotationMode] = useState(false);
  const [pendingPos, setPendingPos] = useState<AnnotationPosition | null>(null);
  const [pendingFileId, setPendingFileId] = useState<string | null>(null);
  const [measureKind, setMeasureKind] = useState<'line' | 'area' | null>(null);
  const [measureDist, setMeasureDist] = useState<number | null>(null);
  const measureMode = measureKind !== null;
  const [pendingText, setPendingText] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const activeCardRef = useRef<HTMLDivElement>(null);

  const [folderCollapsed, setFolderCollapsed] = useState<Record<string, boolean>>({});

  const [panoramasByFile, setPanoramasByFile] = useState<Record<string, PanoramaEntry[]>>({});
  // The union of every file's occupancy grid, for marker occlusion and ring floors.
  const [occIndex, setOccIndex] = useState<OccIndex | null>(null);
  const occIndexRef = useRef<OccIndex | null>(null);
  const [openPano, setOpenPano] = useState<{ id: string; heading?: number; pitch?: number } | null>(null);
  const fetchedPanoFiles = useRef<Set<string>>(new Set());
  // A project of 100 files answers one file at a time, and each answer used to
  // re-render the Scene, re-send every station to the viewer and recompute the
  // floor under every station (and rebuild the occupancy union from every grid):
  // answers are gathered and applied together, at most every PANO_BATCH_MS.
  const panoArrivals = useRef<ReturnType<typeof batchArrivals<PanoArrival>> | null>(null);
  if (!panoArrivals.current) {
    panoArrivals.current = batchArrivals<PanoArrival>(PANO_BATCH_MS, (items) => {
      const batch: Record<string, PanoramaEntry[]> = {};
      let ix = occIndexRef.current;
      for (const a of items) {
        if (a.panos) batch[a.fileId] = a.panos;
        if (a.grid) ix = addToOccIndex(ix, a.grid);
      }
      if (Object.keys(batch).length) setPanoramasByFile((prev) => ({ ...prev, ...batch }));
      if (ix !== occIndexRef.current) { occIndexRef.current = ix; setOccIndex(ix); }
    });
  }
  useEffect(() => () => panoArrivals.current?.cancel(), []);

  // Geometry inspector — per-cloud world bbox/offset/spacing reported by the iframe.
  const [cloudGeometry, setCloudGeometry] = useState<Record<string, CloudGeometry>>({});
  const [bboxOverlayOn, setBboxOverlayOn] = useState(false);

  const { data, loading, startPolling, stopPolling } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: 'point_cloud', page: 1, limit: 100 },
    skip: !projectId || isShareMode,
  });

  const { data: foldersData } = useQuery(FILE_FOLDERS_QUERY, {
    variables: { projectId, fileType: 'point_cloud' },
    skip: !projectId || isShareMode,
  });

  // Every other file of the project feeds the scene layers: BIM models
  // (RVT/IFC through the IFC→GLB pipeline), orthomosaics, DWG drawings and
  // Gaussian splats. Share mode gets the same list from the share page.
  const { data: layerData } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, page: 1, limit: 500 },
    skip: !projectId || isShareMode,
  });
  const otherFiles: PCFile[] = useMemo(
    () => (isShareMode ? (sharedLayerFiles ?? []) : (layerData?.projectFiles?.files ?? [])) as PCFile[],
    [isShareMode, sharedLayerFiles, layerData],
  );
  const bimFiles: PCFile[] = otherFiles.filter(
    (f: PCFile) => f.type === 'three_d' && ['rvt', 'ifc'].includes((f.extension || '').toLowerCase())
  );
  // The parent decides who is looking: the signed-in user, or a share
  // link's project owner.
  const access = accessProp ?? (isShareMode ? NO_ACCESS : accessOf(currentUser));
  const layerFiles: PCFile[] = useMemo(
    () => otherFiles.filter((f) => f.status === 'ready' && sceneLayerAllowed(layerKindOf(f), access)),
    [otherFiles, access],
  );
  const layerFileById = useMemo(() => {
    const m: Record<string, PCFile> = {};
    layerFiles.forEach((f) => { m[f.id] = f; });
    return m;
  }, [layerFiles]);
  const [bimVisible, setBimVisible] = useState<Record<string, boolean>>({});

  // BIM comes with the Scene permission itself. A share link cannot load
  // models: the GLB route authenticates a signed-in user.
  const canUseBimFiles = !isShareMode;
  // 1 = solid, same look as the standalone BIM viewer; lower it to ghost the model
  // for aligning against the point cloud.
  const [bimOpacity, setBimOpacity] = useState(1);
  const [bimError, setBimError] = useState<Record<string, string>>({});
  const [bimConverting, setBimConverting] = useState<Record<string, boolean>>({});
  const [bimSceneSize, setBimSceneSize] = useState<Record<string, number>>({});
  const [bimXform, setBimXform] = useState<Record<string, BimXform>>({});
  // bimLoaded holds only iframe-CONFIRMED models (bimModelLoaded message) — never
  // set optimistically, or a lost postMessage turns every later toggle into a
  // silent no-op. bimPending = loadBimModel posted, confirmation outstanding.
  const bimLoaded = useRef<Set<string>>(new Set());
  const bimPending = useRef<Set<string>>(new Set());
  // Mirrors bimVisible for async callbacks (poll ticks, translate responses, the
  // iframe loaded-confirmation) — closures must not act on a stale toggle state.
  const bimVisibleRef = useRef(bimVisible);
  useEffect(() => { bimVisibleRef.current = bimVisible; }, [bimVisible]);
  const bimPollBusy = useRef(false);
  const bimPollFails = useRef<Map<string, number>>(new Map());
  // Where the iframe put each BIM model's centre by default — the sliders show
  // offsets from it, while the saved placement is absolute.
  const [bimBaseCenter, setBimBaseCenter] = useState<Record<string, SceneXform>>({});

  // Scene layers (ortho / drawing / splat), same confirmation discipline as BIM:
  // layerLoaded = iframe-confirmed, layerPending = load posted.
  const [layerOn, setLayerOn] = useState<Record<string, boolean>>({});
  const layerOnRef = useRef(layerOn);
  useEffect(() => { layerOnRef.current = layerOn; }, [layerOn]);
  const layerLoaded = useRef<Set<string>>(new Set());
  const layerPending = useRef<Set<string>>(new Set());
  const [layerError, setLayerError] = useState<Record<string, string>>({});
  const [layerXform, setLayerXform] = useState<Record<string, SceneXform>>({});
  const [layerDefault, setLayerDefault] = useState<Record<string, SceneXform>>({});
  const [layerSize, setLayerSize] = useState<Record<string, number>>({});
  const [layerWarnings, setLayerWarnings] = useState<Record<string, string[]>>({});
  const [layerGeoref, setLayerGeoref] = useState<Record<string, boolean>>({});
  const [layerCrs, setLayerCrs] = useState<Record<string, { epsg: number | null; proj4: string | null; projected: boolean }>>({});
  const [bimGeoref, setBimGeoref] = useState<Record<string, boolean>>({});
  type XYZ = { x: number; y: number; z: number };
  const [layerNative, setLayerNative] = useState<Record<string, XYZ>>({});
  const [bimNative, setBimNative] = useState<Record<string, XYZ>>({});
  const [gatherMsg, setGatherMsg] = useState<string | null>(null);
  const gatherMsgTimer = useRef<number | null>(null);
  const fmtCoord = (c: XYZ) => `E ${Math.round(c.x).toLocaleString('en-GB')} · N ${Math.round(c.y).toLocaleString('en-GB')} · Z ${c.z.toFixed(1)}`;
  const [layerOpacity, setLayerOpacity] = useState<Record<string, number>>({});
  const [alignFor, setAlignFor] = useState<string | null>(null);
  const [setFileSceneTransform] = useMutation(SET_FILE_SCENE_TRANSFORM_MUTATION);
  const saveTimers = useRef<Map<string, number>>(new Map());

  const files: PCFile[] = isShareMode ? (sharedFiles ?? []) : (data?.projectFiles?.files ?? []);
  const folders: PCFolder[] = foldersData?.fileFolders ?? [];
  const readyFiles = files.filter((f) => f.status === 'ready');
  const hasProcessing = files.some((f) => f.status === 'processing');
  const conversionProgress = useConversionProgress(
    isShareMode ? [] : files.filter((f) => f.status === 'processing').map((f) => f.id)
  );
  // Phase-1 LGSX files are a panorama tour with NO point cloud (no Potree octree).
  // Without a point cloud, notes and measurements belong to the layer that is
  // the scene's base (orthomosaic first, then splat, then drawing).
  const activeLayerFileId = useMemo(() => {
    const on = layerFiles.filter((f) => layerOn[f.id]);
    const pick = (k: SceneLayerKind) => on.find((f) => layerKindOf(f) === k)?.id;
    return pick('ortho') ?? pick('splat') ?? pick('drawing') ?? null;
  }, [layerFiles, layerOn]);
  const annotationFileId = readyFiles[0]?.id ?? activeLayerFileId;
  const isNoteLayer = (f?: PCFile) => { const k = f ? layerKindOf(f) : null; return k === 'splat' || k === 'ortho'; };
  const annotationFileIsLayer = !!annotationFileId && isNoteLayer(layerFileById[annotationFileId]);
  // Splats and orthomosaics carry their own notes (made here or in their
  // standalone viewers); every one that is on contributes them to the scene.
  const noteLayerIds = useMemo(
    () => layerFiles.filter((f) => layerOn[f.id] && isNoteLayer(f)).map((f) => f.id),
    [layerFiles, layerOn], // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Orthomosaics carry their own measurements too, in the ortho's map CRS.
  const orthoLayerIds = useMemo(
    () => layerFiles.filter((f) => layerOn[f.id] && layerKindOf(f) === 'ortho').map((f) => f.id),
    [layerFiles, layerOn],
  );
  const hasLayerFiles = layerFiles.length > 0;

  const { data: annotationsData, refetch: refetchOwnNotes } = useQuery(FILE_ANNOTATIONS_QUERY, {
    variables: { fileId: annotationFileId },
    skip: !annotationFileId || !loaded || isShareMode,
    fetchPolicy: 'cache-and-network',
  });
  // Share links show notes only when their creator allowed guests to add them
  // for point clouds ("Allow adding notes in Point Clouds").
  const { data: guestAccessData } = useQuery(SHARED_NOTES_ACCESS_QUERY, {
    variables: { token: shareToken, fileId: annotationFileId },
    skip: !isShareMode || !annotationFileId,
    fetchPolicy: 'cache-and-network',
  });
  const guestCanNote = isShareMode && !!guestAccessData?.sharedNotesAccess;
  const { data: sharedNotesData, refetch: refetchSharedNotes } = useQuery(SHARED_FILE_ANNOTATIONS_QUERY, {
    variables: { token: shareToken, fileId: annotationFileId },
    skip: !annotationFileId || !loaded || !guestCanNote,
    fetchPolicy: 'cache-and-network',
  });
  const apollo = useApolloClient();
  const fetchLayerNotes = useCallback(async (ids: string[]): Promise<Record<string, Annotation[]>> => {
    const entries = await Promise.all(ids.map(async (fid): Promise<[string, Annotation[]]> => {
      try {
        const res = isShareMode
          ? await apollo.query({ query: SHARED_FILE_ANNOTATIONS_QUERY, variables: { token: shareToken, fileId: fid }, fetchPolicy: 'network-only' })
          : await apollo.query({ query: FILE_ANNOTATIONS_QUERY, variables: { fileId: fid }, fetchPolicy: 'network-only' });
        return [fid, (isShareMode ? res.data?.sharedFileAnnotations : res.data?.fileAnnotations) ?? []];
      } catch {
        return [fid, []];   // a share link without note access for this file
      }
    }));
    return Object.fromEntries(entries);
  }, [apollo, isShareMode, shareToken]);
  const [layerNotes, setLayerNotes] = useState<Record<string, Annotation[]>>({});
  const [layerNotesTick, setLayerNotesTick] = useState(0);
  useEffect(() => {
    if (!loaded) return;
    const ids = noteLayerIds.filter((id) => id !== annotationFileId);
    if (!ids.length) { setLayerNotes({}); return; }
    let cancelled = false;
    (async () => {
      const next = await fetchLayerNotes(ids);
      if (!cancelled) setLayerNotes(next);
    })();
    return () => { cancelled = true; };
  }, [loaded, noteLayerIds, annotationFileId, fetchLayerNotes, layerNotesTick]);
  const annotations: Annotation[] = useMemo(() => {
    const primary: Annotation[] = (isShareMode ? sharedNotesData?.sharedFileAnnotations : annotationsData?.fileAnnotations) ?? [];
    return [
      ...primary.map((a) => ({ ...a, fileId: annotationFileId ?? undefined, layerId: annotationFileIsLayer ? annotationFileId : null })),
      ...Object.entries(layerNotes).flatMap(([fid, list]) => list.map((a) => ({ ...a, fileId: fid, layerId: fid }))),
    ];
  }, [isShareMode, sharedNotesData, annotationsData, annotationFileId, annotationFileIsLayer, layerNotes]);
  const refetchAnnotations = useCallback(() => {
    if (isShareMode) { if (guestCanNote) refetchSharedNotes(); } else refetchOwnNotes();
    setLayerNotesTick((t) => t + 1);
  }, [isShareMode, guestCanNote, refetchSharedNotes, refetchOwnNotes]);
  const [guestName, setGuestName] = useState(() => {
    try { return localStorage.getItem(GUEST_NAME_KEY) || ''; } catch { return ''; }
  });
  const updateGuestName = (v: string) => {
    setGuestName(v);
    try { localStorage.setItem(GUEST_NAME_KEY, v); } catch { /* per-browser convenience only */ }
  };

  // Rejected saves used to vanish silently (e.g. FORBIDDEN from the access rule) —
  // surface every mutation failure in a transient banner instead.
  const [saveError, setSaveError] = useState('');
  const saveErrTimer = useRef<number | undefined>(undefined);
  const reportSaveError = useCallback((what: string) => (err: { message: string }) => {
    setSaveError(`${what} failed: ${err.message}`);
    if (saveErrTimer.current) window.clearTimeout(saveErrTimer.current);
    saveErrTimer.current = window.setTimeout(() => setSaveError(''), 7000);
  }, []);

  const [createAnnotation, { loading: creatingOwn }] = useMutation(CREATE_ANNOTATION_MUTATION, {
    onCompleted: () => { setPendingPos(null); setPendingText(''); refetchAnnotations(); },
    onError: reportSaveError('Saving note'),
  });
  const [replyToAnnotation, { loading: replyingOwn }] = useMutation(REPLY_ANNOTATION_MUTATION, {
    onCompleted: () => { setReplyText(''); refetchAnnotations(); },
    onError: reportSaveError('Reply'),
  });
  const [sharedCreateAnnotation, { loading: creatingGuest }] = useMutation(SHARED_CREATE_ANNOTATION_MUTATION, {
    onCompleted: () => { setPendingPos(null); setPendingText(''); refetchAnnotations(); },
    onError: reportSaveError('Saving note'),
  });
  const [sharedReplyToAnnotation, { loading: replyingGuest }] = useMutation(SHARED_REPLY_ANNOTATION_MUTATION, {
    onCompleted: () => { setReplyText(''); refetchAnnotations(); },
    onError: reportSaveError('Reply'),
  });
  const creating = creatingOwn || creatingGuest;
  const replying = replyingOwn || replyingGuest;
  const [resolveAnnotation] = useMutation(RESOLVE_ANNOTATION_MUTATION, {
    onCompleted: () => refetchAnnotations(),
    onError: reportSaveError('Updating note'),
  });
  const [deleteAnnotation] = useMutation(DELETE_ANNOTATION_MUTATION, {
    onCompleted: () => { setActiveId(null); setExpandedId(null); refetchAnnotations(); },
    onError: reportSaveError('Deleting note'),
  });
  const [addAttachment] = useMutation(ADD_ANNOTATION_ATTACHMENT_MUTATION);
  const [deleteAttachment] = useMutation(DELETE_ANNOTATION_ATTACHMENT_MUTATION);
  const [linkFile, { loading: linkingFile }] = useMutation(LINK_ANNOTATION_FILE_MUTATION, {
    onCompleted: () => { setLinkPickerFor(null); refetchAnnotations(); },
    onError: reportSaveError('Linking file'),
  });
  const [unlinkFile] = useMutation(UNLINK_ANNOTATION_FILE_MUTATION, {
    onCompleted: () => refetchAnnotations(),
    onError: reportSaveError('Unlinking file'),
  });
  const [linkPickerFor, setLinkPickerFor] = useState<string | null>(null);
  const [uploads, setUploads] = useState<Record<string, { annotationId: string; name: string; pct: number }>>({});
  const [attachError, setAttachError] = useState('');
  const [viewing, setViewing] = useState<NoteAttachment | null>(null);
  const attachInputRef = useRef<HTMLInputElement>(null);
  const attachTargetRef = useRef<string | null>(null);

  const refetchAnnotationList = async (): Promise<Annotation[]> => {
    const layered = Object.values(await fetchLayerNotes(noteLayerIds.filter((id) => id !== annotationFileId))).flat();
    if (isShareMode) {
      const res = await refetchSharedNotes();
      return [...(res.data?.sharedFileAnnotations ?? []), ...layered];
    }
    const res = await refetchOwnNotes();
    return [...(res.data?.fileAnnotations ?? []), ...layered];
  };

  // Upload flow: presign (REST) → browser PUT straight to S3 → record it
  // (GraphQL). Localhost can't PUT to the bucket (CORS), so a failed direct
  // PUT retries through the backend's piped pass-through.
  const uploadAttachments = async (annotationId: string, fileList: FileList) => {
    setAttachError('');
    const token = localStorage.getItem(TOKEN_KEY) || '';
    const all = Array.from(fileList);
    const pdfs = all.filter(isPdfFile);
    const skipped = all.filter((f) => !isPdfFile(f));
    if (skipped.length > 0) {
      setAttachError(`Only PDF files can be attached — skipped ${skipped.map((f) => f.name).join(', ')}`);
    }
    for (const file of pdfs) {
      const key = crypto.randomUUID();
      setUploads((u) => ({ ...u, [key]: { annotationId, name: file.name, pct: 0 } }));
      try {
        const presignRes = await fetch(`/api/annotations/${annotationId}/attachments/presign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ fileName: file.name, fileSize: file.size }),
        });
        const presign = await presignRes.json().catch(() => ({}));
        if (!presignRes.ok) throw new Error(presign.error || 'Could not prepare the upload');
        const { s3Key, uploadUrl } = presign as { s3Key: string; uploadUrl: string };
        try {
          await putWithProgress(uploadUrl, file, (pct) =>
            setUploads((u) => (u[key] ? { ...u, [key]: { ...u[key], pct } } : u)), PDF_MIME);
        } catch {
          const proxyRes = await fetch(`/api/annotations/attachments/upload?key=${encodeURIComponent(s3Key)}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': PDF_MIME },
            body: file,
          });
          if (!proxyRes.ok) {
            const body = await proxyRes.json().catch(() => ({}));
            throw new Error(body.error || 'Upload failed');
          }
        }
        await addAttachment({ variables: { annotationId, s3Key, fileName: file.name } });
      } catch (e) {
        setAttachError(`${file.name}: ${(e as Error).message}`);
      } finally {
        setUploads((u) => {
          const next = { ...u };
          delete next[key];
          return next;
        });
      }
    }
    refetchAnnotations();
  };

  // Presigned URLs expire after an hour — always refetch before opening one.
  const openAttachment = async (annotationId: string, attachmentId: string, mode: 'view' | 'download') => {
    setAttachError('');
    try {
      const list = await refetchAnnotationList();
      const fresh = list.find((x) => x.id === annotationId)?.attachments.find((x) => x.id === attachmentId);
      if (!fresh) { setAttachError('That file no longer exists'); return; }
      if (mode === 'download') {
        if (fresh.downloadUrl) window.location.assign(fresh.downloadUrl);
        else setAttachError(isShareMode ? 'This link does not allow downloads' : 'Downloads need S3 storage');
        return;
      }
      setViewing(fresh);
    } catch (e) {
      setAttachError((e as Error).message);
    }
  };

  const removeAttachment = async (annotationId: string, att: NoteAttachment) => {
    if (!window.confirm(`Remove "${att.fileName}" from this note?`)) return;
    setAttachError('');
    try {
      await deleteAttachment({ variables: { annotationId, attachmentId: att.id } });
      refetchAnnotations();
    } catch (e) {
      setAttachError((e as Error).message);
    }
  };

  const documentFormats = useDocumentFormats();
  const openLinkedFile = (file: LinkedFile) => {
    if (onOpenLinkedFile) { onOpenLinkedFile(file); return; }
    const route = linkedFileRoute(file, projectId, shareToken, documentFormats);
    if (route) navigate(route);
  };

  useEffect(() => {
    if (!viewing) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setViewing(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing]);

  // ── Measurements ────────────────────────────────────────────────────────────
  // World-space measurements live on the scene's note file, unless that is an
  // orthomosaic (its measurements are in its map CRS and load per layer below)
  // or a drawing (its measurements are 2D, in the drawing's own frame).
  const noteFileKind = annotationFileId ? layerKindOf(layerFileById[annotationFileId] ?? {}) : null;
  const cloudMeasureFileId = annotationFileId && noteFileKind !== 'ortho' && noteFileKind !== 'drawing' ? annotationFileId : null;
  // Every share link shows the saved measurements; guests save their own only
  // where its options allow it, and on a point cloud only (a splat standing in
  // as the note file holds none).
  const guestCloudMeasures = isShareMode && !!guestMeasures?.pointCloud && !!cloudMeasureFileId && cloudMeasureFileId === readyFiles[0]?.id;
  const guestOrthoMeasures = isShareMode && !!guestMeasures?.ortho;
  const cloudMeasuresOn = !!cloudMeasureFileId && loaded;
  const { data: measurementsData, refetch: refetchMeasurements } = useQuery(FILE_MEASUREMENTS_QUERY, {
    variables: { fileId: cloudMeasureFileId },
    skip: !cloudMeasuresOn || isShareMode,
    fetchPolicy: 'cache-and-network',
  });
  const { data: sharedMeasurementsData, refetch: refetchSharedMeasurements } = useQuery(SHARED_FILE_MEASUREMENTS_QUERY, {
    variables: { token: shareToken, fileId: cloudMeasureFileId },
    skip: !cloudMeasuresOn || !isShareMode,
    fetchPolicy: 'cache-and-network',
  });
  const measurements = useMemo<MeasurementItem[]>(
    () => (isShareMode ? sharedMeasurementsData?.sharedFileMeasurements : measurementsData?.fileMeasurements) ?? [],
    [isShareMode, sharedMeasurementsData, measurementsData],
  );
  const [layerMeasures, setLayerMeasures] = useState<Record<string, MeasurementItem[]>>({});
  const [layerMeasuresTick, setLayerMeasuresTick] = useState(0);
  // Watched, not fetched once: the split-pane ortho viewer refetches the same
  // (shared)FileMeasurements entry, and the scene must follow its edits.
  useEffect(() => {
    if (!loaded) return;
    setLayerMeasures((prev) => Object.fromEntries(orthoLayerIds.filter((id) => prev[id]).map((id) => [id, prev[id]])));
    const subs = orthoLayerIds.map((fid) => apollo
      .watchQuery(isShareMode
        ? { query: SHARED_FILE_MEASUREMENTS_QUERY, variables: { token: shareToken, fileId: fid }, fetchPolicy: 'cache-and-network' }
        : { query: FILE_MEASUREMENTS_QUERY, variables: { fileId: fid }, fetchPolicy: 'cache-and-network' })
      .subscribe({
        next: ({ data }) => {
          if (!data) return;
          const rows = (isShareMode ? data.sharedFileMeasurements : data.fileMeasurements) ?? [];
          const list = (rows as MeasurementItem[]).map((m) => ({ ...m, layerId: fid }));
          setLayerMeasures((prev) => ({ ...prev, [fid]: list }));
        },
        error: () => setLayerMeasures((prev) => ({ ...prev, [fid]: [] })),
      }));
    return () => subs.forEach((sub) => sub.unsubscribe());
  }, [loaded, isShareMode, shareToken, orthoLayerIds, apollo, layerMeasuresTick]);
  const layerMeasureList = useMemo(() => Object.values(layerMeasures).flat(), [layerMeasures]);
  const allMeasurements = [...measurements, ...layerMeasureList];
  // A skipped query's refetch still goes to the network, with no file id.
  const refreshMeasurements = () => {
    if (cloudMeasuresOn) {
      if (isShareMode) refetchSharedMeasurements();
      else refetchMeasurements();
    }
    setLayerMeasuresTick((t) => t + 1);
  };
  const canDeleteMeasurement = (m: MeasurementItem) => !isShareMode || !!m.fromThisLink;
  const [hiddenMeasures, setHiddenMeasures] = useState<Set<string>>(new Set());
  // Per-item visibility for notes and panorama stations, driven from the
  // Combined View rail (same pattern as hiddenMeasures).
  const [hiddenNotes, setHiddenNotes] = useState<Set<string>>(new Set());
  const [hiddenStations, setHiddenStations] = useState<Set<string>>(new Set());

  const [createMeasurement] = useMutation(CREATE_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Saving measurement'),
  });
  const [createAreaMeasurement] = useMutation(CREATE_AREA_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Saving area measurement'),
  });
  const [deleteMeasurementMut] = useMutation(DELETE_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Deleting measurement'),
  });
  const [sharedCreateMeasurement] = useMutation(SHARED_CREATE_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Saving measurement'),
  });
  const [sharedCreateAreaMeasurement] = useMutation(SHARED_CREATE_AREA_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Saving area measurement'),
  });
  const [sharedDeleteMeasurement] = useMutation(SHARED_DELETE_MEASUREMENT_MUTATION, {
    onCompleted: () => refreshMeasurements(),
    onError: reportSaveError('Deleting measurement'),
  });
  const deleteMeasurementById = (id: string) => {
    if (isShareMode) sharedDeleteMeasurement({ variables: { token: shareToken, measurementId: id } });
    else deleteMeasurementMut({ variables: { measurementId: id } });
  };

  const toggleMeasureVisible = (id: string) => {
    setHiddenMeasures((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // ── Focus zones ─────────────────────────────────────────────────────────────
  const { data: zonesData, refetch: refetchZones } = useQuery(FILE_FOCUS_ZONES_QUERY, {
    variables: { fileId: annotationFileId },
    skip: !annotationFileId || !loaded || isShareMode,
    fetchPolicy: 'cache-and-network',
  });
  const focusZones: FocusZoneItem[] = zonesData?.fileFocusZones ?? [];
  const [zoneEditMode, setZoneEditMode] = useState(false);
  const zoneEditModeRef = useRef(false);
  zoneEditModeRef.current = zoneEditMode;
  const [zoneDraft, setZoneDraft] = useState<{ min: Vec3; max: Vec3 } | null>(null);
  const [zoneName, setZoneName] = useState('');
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);
  const activeZone = focusZones.find((z) => z.id === activeZoneId) ?? null;

  const [createFocusZone, { loading: savingZone }] = useMutation(CREATE_FOCUS_ZONE_MUTATION, {
    onCompleted: () => {
      setZoneEditMode(false);
      setZoneDraft(null);
      setZoneName('');
      post({ type: 'setFocusZoneEdit', enabled: false });
      refetchZones();
    },
    onError: reportSaveError('Saving zone'),
  });
  const [deleteFocusZoneMut] = useMutation(DELETE_FOCUS_ZONE_MUTATION, {
    onCompleted: () => refetchZones(),
    onError: reportSaveError('Deleting zone'),
  });

  // Polling while files are still converting
  useEffect(() => {
    if (hasProcessing) startPolling(5000); else stopPolling();
  }, [hasProcessing, startPolling, stopPolling]);

  // Default visibility for newly-ready files
  const visInitReported = useRef(false);
  useEffect(() => {
    if (!readyFiles.length) return;
    const initial = visInitReported.current || !cloudsHiddenAtStart;
    setVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      readyFiles.forEach((f) => { if (!(f.id in next)) { next[f.id] = initial; changed = true; } });
      return changed ? next : prev;
    });
    if (!visInitReported.current) {
      visInitReported.current = true;
      const vis: Record<string, boolean> = {};
      readyFiles.forEach((f) => { vis[f.id] = initial; });
      onVisibilityInit?.(vis);
    }
  }, [readyFiles.map((f) => f.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on file-set change
  const iframeKey = readyFiles.map((f) => f.id).sort().join(',') || (hasLayerFiles ? 'layers-only' : '');
  const prevKeyRef = useRef('');
  useEffect(() => {
    if (iframeKey === prevKeyRef.current) return;
    prevKeyRef.current = iframeKey;
    setLoaded(false);
    setCloudsVisible(false);
    setLoadError('');
    setCloudGeometry({});
    // The remounted iframe has no BIM models — a stale bimLoaded entry would turn
    // every later toggle into a silent setBimModelVisible no-op.
    bimLoaded.current.clear();
    bimPending.current.clear();
    setBimVisible({});
    setBimError({});
    setBimBaseCenter({});
    layerLoaded.current.clear();
    layerPending.current.clear();
    setLayerOn({});
    setLayerError({});
    setLayerXform({});
    setLayerDefault({});
    setAlignFor(null);
    setZoneEditMode(false);
    setZoneDraft(null);
    setZoneName('');
    // The fresh iframe starts with picking off — armed note/measure modes would
    // keep their banners up while canvas clicks silently do nothing.
    setAnnotationMode(false);
    setPendingPos(null);
    setPendingText('');
    setMeasureKind(null);
    setMeasureDist(null);
  }, [iframeKey]);

  const post = useCallback((cmd: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(cmd, '*');
  }, []);
  // The message handler reads these through refs: it is bound once per
  // share-mode change, not per file-list render.
  const annotationFileIdRef = useRef(annotationFileId);
  annotationFileIdRef.current = annotationFileId;
  // Where a world-space measurement is saved, and whether a point cloud is on
  // to hold it (written where allHidden is known).
  const cloudMeasureRef = useRef<{ fileId: string | null; on: boolean }>({ fileId: null, on: false });
  // What a share-link guest may save, and the name they gave for their notes.
  const guestMeasureRef = useRef({ cloud: false, ortho: false, name: '' });
  guestMeasureRef.current = { cloud: guestCloudMeasures, ortho: guestOrthoMeasures, name: guestName.trim() };
  const bimFilesRef = useRef(bimFiles);
  bimFilesRef.current = bimFiles;
  const layerDefaultRef = useRef(layerDefault);
  layerDefaultRef.current = layerDefault;
  const bimBaseCenterRef = useRef(bimBaseCenter);
  bimBaseCenterRef.current = bimBaseCenter;
  const saveSceneTransformRef = useRef<(fileId: string, transform: SceneXform | null) => void>(() => {});

  // ── Base map ────────────────────────────────────────────────────────────────
  // Map tiles under everything that is on. The scene's grid comes from the
  // project's EPSG or the first cloud's own CRS; a scene without one is drawn
  // where the user placed it on the map. Preferences are saved per project.
  const { data: sceneProjectData } = useQuery(PROJECT_SCENE_QUERY, {
    variables: { id: projectId },
    skip: !projectId || isShareMode,
  });
  const sceneProject: SceneProjectInfo | null = isShareMode ? (sharedProject ?? null) : (sceneProjectData?.project ?? null);
  // A per-user permission (a share link follows its project owner's): without
  // it the Base map block, its settings and the map itself stay out of the Scene.
  // The keyboard pace (Platform Settings → Scene) goes to the viewer by
  // message, not in its URL: a URL change would reload the iframe.
  const { data: sceneSettingsData } = useQuery(SCENE_SETTINGS_QUERY);
  useEffect(() => {
    const s = sceneSettingsData?.sceneSettings;
    if (!loaded || !s) return;
    post({ type: 'setSceneSettings', walkMps: s.walkSpeedMps, runMultiplier: s.runMultiplier, distanceFactor: s.distanceFactor });
  }, [loaded, sceneSettingsData, post]);

  const basemapAllowed = access.basemap;
  const { data: basemapConfigData } = useQuery(BASEMAP_CONFIG_QUERY, {
    variables: { shareToken: shareToken ?? null },
    skip: !loaded || !basemapAllowed,
  });
  const basemapConfig: BasemapConfig | null = basemapAllowed ? (basemapConfigData?.basemapConfig ?? null) : null;
  const [basemapPrefs, setBasemapPrefs] = useState<ProjectBasemap>(projectBasemapOf(null));
  const [savedBasemap, setSavedBasemap] = useState<ProjectBasemap>(projectBasemapOf(null));
  const [sitePlacement, setSitePlacement] = useState<SitePlacement | null>(null);
  const [savedSitePlacement, setSavedSitePlacement] = useState<SitePlacement | null>(null);
  const [placementPreview, setPlacementPreview] = useState<SitePlacement | null | undefined>(undefined);
  const basemapInitRef = useRef<string | null>(null);
  useEffect(() => {
    if (!sceneProject) return;
    if (basemapInitRef.current === sceneProject.id) return;
    basemapInitRef.current = sceneProject.id;
    setBasemapPrefs(projectBasemapOf(sceneProject.basemap));
    setSavedBasemap(projectBasemapOf(sceneProject.basemap));
    setSitePlacement(sitePlacementOf(sceneProject.sitePlacement));
    setSavedSitePlacement(sitePlacementOf(sceneProject.sitePlacement));
  }, [sceneProject]);
  const crs = useMemo(
    () => sceneCrs(sceneProject?.epsg ?? null, [...files, ...layerFiles], Object.values(layerCrs)),
    [sceneProject?.epsg, files, layerFiles, layerCrs],
  );
  const livePlacement = placementPreview === undefined ? sitePlacement : placementPreview;
  const contentCentre = useMemo(() => {
    const boxes = Object.values(cloudGeometry);
    if (!boxes.length) return null;
    let minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const g of boxes) {
      minX = Math.min(minX, g.worldMin.x); minY = Math.min(minY, g.worldMin.y); minZ = Math.min(minZ, g.worldMin.z);
      maxX = Math.max(maxX, g.worldMax.x); maxY = Math.max(maxY, g.worldMax.y);
    }
    return Number.isFinite(minX) ? { x: (minX + maxX) / 2, y: (minY + maxY) / 2, z: minZ } : null;
  }, [cloudGeometry]);
  const fit = useMemo(() => gridFit(crs, contentCentre, sceneProject), [crs, contentCentre, sceneProject]);
  const { frame, source: frameSource } = useMemo(
    () => sceneBasemapFrame({
      crs, fit, position: basemapPrefs.position, gridAdjust: basemapPrefs.gridAdjust,
      placement: livePlacement, location: sceneProject, centre: contentCentre,
    }),
    [crs, fit, basemapPrefs.position, basemapPrefs.gridAdjust, livePlacement, sceneProject, contentCentre],
  );
  const effectivePrefs = useMemo<ProjectBasemap>(
    () => ({ ...basemapPrefs, provider: providerOrFallback(basemapPrefs.provider, basemapConfig) }),
    [basemapPrefs, basemapConfig],
  );
  const basemapEngine = useMemo(() => basemapEngineConfig(effectivePrefs, basemapConfig, frame), [effectivePrefs, basemapConfig, frame]);
  const basemapEngineSig = JSON.stringify(basemapEngine);
  useEffect(() => {
    if (!loaded) return;
    post({ type: 'setBasemap', config: basemapEngine });
  }, [loaded, basemapEngineSig, post]); // eslint-disable-line react-hooks/exhaustive-deps
  const [basemapStatus, setBasemapStatus] = useState<SmartViewBasemap['status']>({ active: false, tiles: 0, loading: 0, error: null, autoHeight: null, failed: 0, lastError: null });
  const [setProjectBasemapMut, { loading: savingBasemap }] = useMutation(SET_PROJECT_BASEMAP_MUTATION, { onError: reportSaveError('Saving base map') });
  const [setSitePlacementMut, { loading: savingPlacement }] = useMutation(SET_PROJECT_SITE_PLACEMENT_MUTATION, { onError: reportSaveError('Saving site placement') });
  const basemapPrefsRef = useRef(basemapPrefs);
  basemapPrefsRef.current = basemapPrefs;
  const savedBasemapRef = useRef(savedBasemap);
  savedBasemapRef.current = savedBasemap;
  // Changes stay in this view until saved: the project's map is what everyone
  // opening the Scene starts with, so trying a provider must not change it.
  const changeBasemap = useCallback((patch: Partial<ProjectBasemap>) => {
    const next = { ...basemapPrefsRef.current, ...patch };
    basemapPrefsRef.current = next;
    setBasemapPrefs(next);
  }, []);
  const sitePlacementRef = useRef(sitePlacement);
  sitePlacementRef.current = sitePlacement;
  const savedSitePlacementRef = useRef(savedSitePlacement);
  savedSitePlacementRef.current = savedSitePlacement;
  const changePlacement = useCallback((p: SitePlacement | null) => {
    sitePlacementRef.current = p;
    setSitePlacement(p);
  }, []);
  // The map settings and the placement are one thing to the user: one Save
  // for both, each written only when it changed.
  const saveBasemap = useCallback(async () => {
    if (isShareMode || !projectId) return;
    const value = projectBasemapOf(basemapPrefsRef.current);
    const placement = sitePlacementOf(sitePlacementRef.current);
    if (!sameProjectBasemap(value, savedBasemapRef.current)) {
      const res = await setProjectBasemapMut({ variables: { projectId, basemap: value } });
      if (!res.data?.setProjectBasemap) return;
      setSavedBasemap(value);
    }
    if (!sameSitePlacement(placement, savedSitePlacementRef.current)) {
      const res = await setSitePlacementMut({ variables: { projectId, placement } });
      if (res.data?.setProjectSitePlacement) setSavedSitePlacement(placement);
    }
  }, [isShareMode, projectId, setProjectBasemapMut, setSitePlacementMut]);
  const revertBasemap = useCallback(() => {
    basemapPrefsRef.current = savedBasemapRef.current;
    setBasemapPrefs(savedBasemapRef.current);
    changePlacement(savedSitePlacementRef.current);
  }, [changePlacement]);
  // Fine-tune: slide the map as seen on screen (forward / right, metres) and
  // turn it (degrees, counter-clockwise), on whatever is positioning it now.
  const cameraHeadingRef = useRef(Math.PI / 2);
  const cameraPosRef = useRef<Vec3 | null>(null);
  const basemapSourceRef = useRef<{ source: string | null; centre: { x: number; y: number; z: number } | null }>({ source: null, centre: null });
  basemapSourceRef.current = { source: frameSource, centre: contentCentre };
  const sceneProjectRef = useRef(sceneProject);
  sceneProjectRef.current = sceneProject;
  const nudgeBasemap = useCallback((forward: number, right: number, turn: number) => {
    const { source, centre } = basemapSourceRef.current;
    const shift = screenShift(cameraHeadingRef.current, forward, right);
    if (source === 'crs' && centre) {
      changeBasemap({ gridAdjust: nudgeGridAdjust(basemapPrefsRef.current.gridAdjust, centre, shift, turn) });
    } else if (source === 'placement' || source === 'pin') {
      const base = sitePlacementRef.current ?? pinPlacement(sceneProjectRef.current, centre);
      if (base) changePlacement(nudgePlacement(base, shift, turn));
    }
  }, [changeBasemap, changePlacement]);
  const resetBasemapTune = useCallback(() => {
    if (basemapSourceRef.current.source === 'crs') changeBasemap({ gridAdjust: null });
    else changePlacement(null);
  }, [changeBasemap, changePlacement]);
  const [placementOpen, setPlacementOpen] = useState(false);
  const [sceneAnchor, setSceneAnchor] = useState<SceneAnchor | null>(null);
  const openSitePlacement = useCallback(() => {
    setSceneAnchor(null);
    post({ type: 'requestSceneAnchor', requestId: 1 });
    setPlacementOpen(true);
  }, [post]);
  const closeSitePlacement = () => { setPlacementOpen(false); setPlacementPreview(undefined); };
  const applySitePlacement = (p: SitePlacement | null) => {
    changePlacement(p);
    closeSitePlacement();
  };
  const changeBasemapRef = useRef(changeBasemap);
  changeBasemapRef.current = changeBasemap;
  const saveBasemapRef = useRef(saveBasemap);
  saveBasemapRef.current = saveBasemap;
  const nudgeBasemapRef = useRef(nudgeBasemap);
  nudgeBasemapRef.current = nudgeBasemap;
  const resetBasemapTuneRef = useRef(resetBasemapTune);
  resetBasemapTuneRef.current = resetBasemapTune;
  const openSitePlacementRef = useRef(openSitePlacement);
  openSitePlacementRef.current = openSitePlacement;

  // Iframe → React
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (!e.data || typeof e.data !== 'object') return;
      if (iframeRef.current && e.source !== iframeRef.current.contentWindow) return;
      if (e.data.type === 'loaded') setLoaded(true);
      if (e.data.type === 'cloudsVisible') setCloudsVisible(true);
      if (e.data.type === 'cameraPose') {
        if (typeof e.data.heading === 'number' && Number.isFinite(e.data.heading)) cameraHeadingRef.current = e.data.heading;
        if ([e.data.x, e.data.y, e.data.z].every((v) => typeof v === 'number' && Number.isFinite(v))) cameraPosRef.current = { x: e.data.x, y: e.data.y, z: e.data.z };
        onCameraPoseRef.current?.({ x: e.data.x, y: e.data.y, z: e.data.z, heading: e.data.heading });
      }
      if (e.data.type === 'error') setLoadError(e.data.message || 'Unknown error');
      if (e.data.type === 'panoramaClicked') {
        const heading = typeof e.data.heading === 'number' ? (e.data.heading as number) : undefined;
        const pitch   = typeof e.data.pitch   === 'number' ? (e.data.pitch   as number) : undefined;
        setOpenPano({ id: e.data.id as string, heading, pitch });
      }
      if (e.data.type === 'pointPicked') {
        // Notes on a splat file keep the splat's own frame (what the photoreal
        // viewer stores), so a pick that landed on that splat uses its local point.
        const lp = e.data.layer as { fileId: string; local: AnnotationPosition } | null | undefined;
        setPendingFileId(lp ? lp.fileId : annotationFileIdRef.current);
        setPendingPos(lp ? lp.local : (e.data.position as AnnotationPosition));
        setAnnotationMode(false);
        setOpenPanel('annotations');
        post({ type: 'setAnnotationMode', enabled: false });
      }
      // A measurement saved on an orthomosaic keeps that ortho's map CRS and
      // its planar length / area; one saved on the cloud stays in world space.
      if (e.data.type === 'measureResult' && typeof e.data.distance === 'number') {
        const ortho = e.data.ortho as OrthoLineResult | null | undefined;
        const cloud = cloudMeasureRef.current;
        const target = measurementTarget({ ortho, cloudVisible: cloud.on });
        let line: { fileId: string; start: Vec3; end: Vec3; distance: number } | null = null;
        if (target.kind === 'ortho' && ortho && ortho.start && ortho.end && typeof ortho.distance === 'number') {
          line = { fileId: target.fileId, start: toPointInput(ortho.start), end: toPointInput(ortho.end), distance: ortho.distance };
        } else if (target.kind === 'cloud' && cloud.fileId && e.data.start && e.data.end) {
          line = { fileId: cloud.fileId, start: e.data.start, end: e.data.end, distance: e.data.distance };
        }
        setMeasureDist(line?.distance ?? (e.data.distance as number));
        // A share link saves only where its options allow it; elsewhere it
        // measures client-side (an unauthenticated save is a FORBIDDEN banner).
        const guest = guestMeasureRef.current;
        if (line && !isShareMode) createMeasurement({ variables: line });
        else if (line && (target.kind === 'ortho' ? guest.ortho : guest.cloud)) {
          sharedCreateMeasurement({ variables: { ...line, token: shareToken, guestName: guest.name || null } });
        }
      }
      if (e.data.type === 'areaResult' && typeof e.data.area === 'number' && Array.isArray(e.data.points)) {
        const ortho = e.data.ortho as OrthoAreaResult | null | undefined;
        const cloud = cloudMeasureRef.current;
        const target = measurementTarget({ ortho, cloudVisible: cloud.on });
        let area: { fileId: string; points: Vec3[]; area: number } | null = null;
        if (target.kind === 'ortho' && ortho && Array.isArray(ortho.points) && ortho.points.length >= 3 && typeof ortho.area === 'number') {
          area = { fileId: target.fileId, points: ortho.points.map(toPointInput), area: ortho.area };
        } else if (target.kind === 'cloud' && cloud.fileId && e.data.points.length >= 3) {
          area = { fileId: cloud.fileId, points: (e.data.points as Vec3[]).map(toPointInput), area: e.data.area };
        }
        const guest = guestMeasureRef.current;
        if (area && !isShareMode) createAreaMeasurement({ variables: area });
        else if (area && (target.kind === 'ortho' ? guest.ortho : guest.cloud)) {
          sharedCreateAreaMeasurement({ variables: { ...area, token: shareToken, guestName: guest.name || null } });
        }
      }
      if (e.data.type === 'markerClicked') {
        const id = e.data.id as string;
        setActiveId(id);
        setExpandedId(id);
        setOpenPanel('annotations');
      }
      if (e.data.type === 'focusZoneChanged' && e.data.min && e.data.max) {
        if (!zoneEditModeRef.current) return;
        const min = e.data.min as Vec3;
        const max = e.data.max as Vec3;
        setZoneDraft({
          min: { x: min.x, y: min.y, z: min.z },
          max: { x: max.x, y: max.y, z: max.z },
        });
      }
      if (e.data.type === 'basemapStatus') {
        setBasemapStatus({
          active: !!e.data.active,
          tiles: Number(e.data.tiles) || 0,
          loading: Number(e.data.loading) || 0,
          error: typeof e.data.error === 'string' ? e.data.error : null,
          autoHeight: typeof e.data.autoHeight === 'number' ? e.data.autoHeight : null,
          failed: Number(e.data.failed) || 0,
          lastError: typeof e.data.lastError === 'string' ? e.data.lastError : null,
        });
      }
      if (e.data.type === 'sceneAnchor') {
        const a = e.data.anchor as SceneAnchor | null;
        setSceneAnchor(a && Number.isFinite(a.x) && Number.isFinite(a.y) ? { x: a.x, y: a.y, z: Number.isFinite(a.z) ? a.z : 0 } : null);
      }
      if (e.data.type === 'cloudGeometry') {
        const g = e.data as CloudGeometry & { type: string };
        setCloudGeometry((prev) => ({ ...prev, [g.fileId]: g }));
      }
      if (e.data.type === 'bimModelLoaded') {
        const fid = e.data.fileId as string;
        bimPending.current.delete(fid);
        bimLoaded.current.add(fid);   // only iframe confirmation marks a model loaded
        if (typeof e.data.sceneSize === 'number') setBimSceneSize((prev) => ({ ...prev, [fid]: e.data.sceneSize as number }));
        setBimGeoref((prev) => ({ ...prev, [fid]: !!e.data.georef }));
        if (e.data.nativeCenter) setBimNative((prev) => ({ ...prev, [fid]: e.data.nativeCenter as XYZ }));
        const bc = e.data.baseCenter as { x: number; y: number; z: number } | undefined;
        if (bc) {
          const base = { x: bc.x, y: bc.y, z: bc.z, rot: 0, scale: 1 };
          setBimBaseCenter((prev) => ({ ...prev, [fid]: base }));
          // A saved placement wins over the iframe's default centre.
          const saved = bimFilesRef.current.find((f) => f.id === fid)?.sceneTransform;
          const abs = saved ?? base;
          setBimXform((prev) => ({ ...prev, [fid]: { dx: abs.x - base.x, dy: abs.y - base.y, dz: abs.z - base.z, rot: abs.rot, scale: abs.scale } }));
          if (saved) post({ type: 'setBimTransform', fileId: fid, transform: saved });
        }
        // Big GLBs take a while — respect a toggle-off that happened during the load.
        if (!bimVisibleRef.current[fid]) post({ type: 'setBimModelVisible', fileId: fid, visible: false });
      }
      if (e.data.type === 'layerLoaded') {
        const fid = e.data.fileId as string;
        layerPending.current.delete(fid);
        layerLoaded.current.add(fid);
        const tr = e.data.transform as SceneXform;
        const def = e.data.defaultTransform as SceneXform;
        setLayerXform((prev) => ({ ...prev, [fid]: tr }));
        setLayerDefault((prev) => ({ ...prev, [fid]: def }));
        if (typeof e.data.sceneSize === 'number') setLayerSize((prev) => ({ ...prev, [fid]: e.data.sceneSize as number }));
        setLayerWarnings((prev) => ({ ...prev, [fid]: Array.isArray(e.data.warnings) ? (e.data.warnings as string[]) : [] }));
        setLayerGeoref((prev) => ({ ...prev, [fid]: !!e.data.georef }));
        if (e.data.crs && typeof e.data.crs === 'object') {
          const c = e.data.crs as { epsg?: number | null; proj4?: string | null; projected?: boolean };
          setLayerCrs((prev) => ({
            ...prev,
            [fid]: { epsg: typeof c.epsg === 'number' ? c.epsg : null, proj4: typeof c.proj4 === 'string' ? c.proj4 : null, projected: c.projected !== false },
          }));
        }
        if (e.data.nativeCenter) setLayerNative((prev) => ({ ...prev, [fid]: e.data.nativeCenter as XYZ }));
        if (!layerOnRef.current[fid]) post({ type: 'setLayerVisible', fileId: fid, visible: false });
      }
      // The viewer moved a file itself (Gather): keep the sliders in step and
      // persist it exactly like a manual placement.
      if (e.data.type === 'layerTransform') {
        const fid = e.data.fileId as string;
        const tr = e.data.transform as SceneXform;
        setLayerXform((prev) => ({ ...prev, [fid]: tr }));
        if (typeof e.data.sceneSize === 'number') setLayerSize((prev) => ({ ...prev, [fid]: e.data.sceneSize as number }));
        saveSceneTransformRef.current(fid, sameXform(tr, layerDefaultRef.current[fid]) ? null : tr);
      }
      if (e.data.type === 'gatherDone') {
        const moved = (e.data.moved as { fileId: string }[]) ?? [];
        const msg = moved.length === 0
          ? 'Nothing to gather — everything that is on is a georeferenced base.'
          : `Moved ${moved.length} file${moved.length === 1 ? '' : 's'} onto the base`
            + (isShareMode ? '.' : '. Saved.');
        setGatherMsg(msg);
        if (gatherMsgTimer.current) window.clearTimeout(gatherMsgTimer.current);
        gatherMsgTimer.current = window.setTimeout(() => setGatherMsg(null), 8000);
      }
      if (e.data.type === 'bimTransform') {
        const fid = e.data.fileId as string;
        const abs = e.data.transform as SceneXform;
        if (typeof e.data.sceneSize === 'number') setBimSceneSize((prev) => ({ ...prev, [fid]: e.data.sceneSize as number }));
        const base = bimBaseCenterRef.current[fid];
        if (base) {
          setBimXform((prev) => ({ ...prev, [fid]: { dx: abs.x - base.x, dy: abs.y - base.y, dz: abs.z - base.z, rot: abs.rot, scale: abs.scale } }));
          saveSceneTransformRef.current(fid, sameXform(abs, base) ? null : abs);
        }
      }
      if (e.data.type === 'layerError') {
        const fid = e.data.fileId as string;
        layerPending.current.delete(fid);
        layerLoaded.current.delete(fid);
        setLayerOn((v) => ({ ...v, [fid]: false }));
        setLayerError((prev) => ({ ...prev, [fid]: (e.data.message as string) || 'could not load' }));
      }
      if (e.data.type === 'bimModelError') {
        const fid = e.data.fileId as string;
        bimPending.current.delete(fid);
        bimLoaded.current.delete(fid);   // allow retry
        setBimVisible((v) => ({ ...v, [fid]: false }));
        setBimError((prev) => ({ ...prev, [fid]: 'could not load model' }));
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [post, isShareMode, shareToken, createMeasurement, createAreaMeasurement, sharedCreateMeasurement, sharedCreateAreaMeasurement]);

  // Push saved measurements (and their visibility) to the viewer. An ortho's
  // measurements carry its layerId: the engine places them from the ortho's
  // map CRS and labels them with the stored distance / area. Focus zones are
  // cloud boxes, so they filter only the cloud's.
  useEffect(() => {
    if (!loaded) return;
    const list = measurements.map((m) => {
      const pts = (m.kind === 'area' ? m.points ?? [] : [m.start, m.end].filter(Boolean)) as Vec3[];
      const inZone = !activeZone || pts.every((p) => pointInBox(p, activeZone.min, activeZone.max));
      return {
        id: m.id, kind: m.kind === 'area' ? 'area' : 'line',
        start: m.start, end: m.end, points: m.points, area: m.area,
        visible: !hiddenMeasures.has(m.id) && inZone,
      };
    });
    const layered = layerMeasureList.map((m) => ({
      id: m.id, kind: m.kind === 'area' ? 'area' : 'line',
      start: m.start, end: m.end, points: m.points, area: m.area, distance: m.distance,
      layerId: m.layerId,
      visible: !hiddenMeasures.has(m.id),
    }));
    post({ type: 'setMeasurements', measurements: [...list, ...layered] });
  }, [loaded, measurements, layerMeasureList, hiddenMeasures, activeZone, post]);

  // Clip the cloud to the active zone. Keyed on `loaded` too, so an iframe
  // remount replays the active clip the same way hidden clouds are re-sent.
  useEffect(() => {
    if (!loaded) return;
    post({
      type: 'setFocusZoneActive',
      box: activeZone ? { min: activeZone.min, max: activeZone.max } : null,
    });
  }, [loaded, activeZone, post]);

  // On viewer load: replay settings (markers + visibility are handled by dedicated effects below)
  useEffect(() => {
    if (!loaded) return;
    Object.entries(visibility).forEach(([fileId, visible]) => {
      if (!visible) post({ type: 'setCloudVisible', fileId, visible: false });
    });
    if (pointSize !== 1.0) post({ type: 'setPointSize', value: pointSize });
    if (opacity !== 1.0) post({ type: 'setOpacity', value: opacity });
    if (brightness !== 0) post({ type: 'setBrightness', value: brightness });
    if (colorMode !== 'rgb') post({ type: 'setColorMode', value: colorMode });
    if (!edlEnabled) post({ type: 'setEDL', enabled: false });
    post({ type: 'setNavigationMode', mode: navMode });
  }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single source of truth for panorama markers in the iframe: re-send whenever
  // the set of known panoramas changes or the viewer (re)loads.
  useEffect(() => {
    if (!loaded) return;
    const allMarkers: Array<{ id: string; position: { x: number; y: number; z: number }; fileId: string; url: string; label: string }> = [];
    Object.entries(panoramasByFile).forEach(([fileId, panos]) => {
      panos.forEach((p) => {
        if (hiddenStations.has(p.id)) return;
        if (activeZone && !pointInBox(p.position, activeZone.min, activeZone.max)) return;
        allMarkers.push({ id: p.id, position: p.position, fileId, url: p.url, label: `Scan ${p.index + 1}` });
      });
    });
    post({ type: 'setPanoramaMarkers', markers: allMarkers });
  }, [loaded, panoramasByFile, activeZone, post, hiddenStations]);

  // Default view is the Virtual Tour, opened at the "photographer's corner":
  // a station at the TYPICAL height (main floor), positioned a bit to the SIDE
  // of the others (edge of the cluster, away from dense packs) but still
  // overlooking them — and FACING the mass of stations. Standing at the edge
  // looking in frames the whole space; standing in the middle puts it behind
  // you. Sorted first so the pick is stable across reloads (per-file fetches
  // complete in random order). Once per mount — leaving the tour back to the
  // point cloud must not re-trigger it. Projects without panoramas stay in the
  // point cloud view, and so does a scene opened on an ortho / splat base
  // (?base=…): the tour belongs to the clouds, which start switched off there.
  const autoTourRef = useRef(false);
  useEffect(() => {
    if (!loaded || autoTourRef.current || cloudsHiddenAtStart) return;
    const entries = Object.values(panoramasByFile).flat()
      .sort((a, b) => (a.fileId === b.fileId ? a.index - b.index : a.fileId.localeCompare(b.fileId)));
    if (entries.length === 0) return;
    autoTourRef.current = true;

    // 1) The MAIN FLOOR: densest 1 m height band (sliding window over sorted z,
    // not fixed buckets — a floor whose stations straddle a bucket boundary
    // must not get split and undercounted). Ties keep the LOWEST band, so with
    // equally-scanned storeys the tour opens at the entrance level.
    const zs = entries.map((p) => p.position.z).sort((a, b) => a - b);
    let winLo = 0, winCnt = 1;
    for (let i = 0, j = 0; i < zs.length; i++) {
      while (zs[i] - zs[j] > TOUR_START_Z_BAND) j++;
      if (i - j + 1 > winCnt) { winCnt = i - j + 1; winLo = j; }
    }
    const zLo = zs[winLo], zHi = zs[winLo + winCnt - 1];
    const floor = entries.filter((p) => p.position.z >= zLo && p.position.z <= zHi);
    const pool = floor.length >= 2 ? floor : entries;

    let cx = 0, cy = 0;
    for (const p of pool) { cx += p.position.x; cy += p.position.y; }
    cx /= pool.length; cy /= pool.length;

    // 2) Edge-of-cluster vantage: reward distance from the centroid (capped, so
    // "a bit on the side" beats "the far stray"), penalise local crowding, and
    // require the station to still overlook a few others (tour rings in view).
    let maxDc = 0;
    for (const p of pool) maxDc = Math.max(maxDc, Math.hypot(p.position.x - cx, p.position.y - cy));
    const dcCap = maxDc * 0.75;

    let best: PanoramaEntry | null = null;
    let bestScore = -Infinity;
    for (const p of pool) {
      let crowd = 0, overlooks = 0;
      for (const q of pool) {
        if (q === p) continue;
        const d = dist3(p.position, q.position);
        if (d <= 4) crowd++;
        if (d <= TOUR_MAX_DISTANCE) overlooks++;
      }
      if (pool.length > 3 && overlooks < 2) continue;   // isolated stray — bad opener
      const dc = Math.hypot(p.position.x - cx, p.position.y - cy);
      const score = Math.min(dc, dcCap) - 2 * crowd;
      if (score > bestScore) { bestScore = score; best = p; }
    }
    if (!best) best = pool[0];

    // 3) Face the mass of stations: every same-floor station votes with a unit
    // vector; the resultant bearing is "where most of the panoramas are". A
    // near-zero resultant (symmetric spread) falls back to facing the centroid.
    let vx = 0, vy = 0;
    for (const q of pool) {
      if (q === best) continue;
      const d = dist3(best.position, q.position);
      if (d < 1e-6) continue;
      vx += (q.position.x - best.position.x) / d;
      vy += (q.position.y - best.position.y) / d;
    }
    if (Math.hypot(vx, vy) < 0.5) {
      vx = cx - best.position.x;
      vy = cy - best.position.y;
    }
    const heading = (vx || vy) ? Math.atan2(vy, vx) : 0;

    // goToPanorama (not setOpenPano directly): flies the CLOUD camera to the station
    // first, then reports back panoramaClicked → overlay opens facing `heading`. Keeps
    // the underlying point cloud, minimap and occlusion queries at the station like a
    post({ type: 'goToPanorama', id: best.id, heading, pitch: 0 });
  }, [loaded, panoramasByFile, post]);

  // Sync panorama-marker visibility to file visibility: markers from hidden
  // files are hidden in the viewer, minimap, picking, and DOM overlay.
  useEffect(() => {
    if (!loaded) return;
    const hiddenFileIds = Object.entries(visibility)
      .filter(([, v]) => v === false)
      .map(([fid]) => fid);
    post({ type: 'setPanoramaVisibility', hiddenFileIds });
  }, [loaded, visibility, post]);

  // Fetch panoramas per ready file. (
  // cloud's `loaded`, so fetch on readyFiles alone in that case.
  useEffect(() => {
    if (!loaded) return;
    const jwtToken = localStorage.getItem(TOKEN_KEY) || '';
    readyFiles.forEach(async (file) => {
      if (!file.panoramaCount || fetchedPanoFiles.current.has(file.id)) return;
      fetchedPanoFiles.current.add(file.id);
      try {
        // Local dev must get PROXY urls (S3 CORS from localhost is unreliable), but
        // the Vite proxy makes this fetch same-origin — no Origin header for the
        // backend to detect localhost by — so dev builds ask explicitly.
        const devMode = import.meta.env.DEV ? 'mode=proxy' : '';
        const url = isShareMode
          ? `/api/panoramas/${file.id}?shareToken=${encodeURIComponent(shareToken!)}${devMode ? `&${devMode}` : ''}`
          : `/api/panoramas/${file.id}${devMode ? `?${devMode}` : ''}`;
        const res = await fetch(url, isShareMode ? {} : {
          headers: { Authorization: `Bearer ${jwtToken}` },
        });
        if (!res.ok) return;
        const { panoramas, occupancyUrl } = (await res.json()) as { panoramas: Array<Omit<PanoramaEntry, 'id' | 'fileId'>>; occupancyUrl?: string };
        if (!panoramas.length) return;
        const entries: PanoramaEntry[] = panoramas.map((p) => ({
          ...p,
          id: `${file.id}:${p.index}`,
          fileId: file.id,
        }));
        panoArrivals.current?.add({ fileId: file.id, panos: entries });
        // Occupancy grid for marker occlusion (unioned across files, raycast in JS).
        // Non-fatal: a 404 / non-OCC1 body (older file) just leaves it out of the union.
        if (occupancyUrl) {
          try {
            const ores = await fetch(occupancyUrl);
            if (ores.ok) {
              const grid = parseOccupancy(await ores.arrayBuffer());
              if (grid) panoArrivals.current?.add({ fileId: file.id, grid });
            }
          } catch { /* non-fatal */ }
        }
      } catch { /* non-fatal */ }
    });
  }, [loaded, readyFiles.map((f) => f.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync annotation markers
  const zoneFilteredAnnotations = useMemo(
    () => activeZone
      ? annotations.filter((a) => !!a.layerId || pointInBox(a.position, activeZone.min, activeZone.max))
      : annotations,
    [annotations, activeZone],
  );
  const zoneHiddenAnnotCount = annotations.length - zoneFilteredAnnotations.length;
  useEffect(() => {
    if (!loaded) return;
    post({ type: 'clearMarkers' });
    // Index by the zone-filtered list (not the visible one) so a note keeps its
    // panel number while individually hidden from the rail.
    zoneFilteredAnnotations.forEach((a, i) => {
      if (hiddenNotes.has(a.id)) return;
      post({
        type: 'addMarker', id: a.id, position: a.position, index: i + 1, status: a.status, active: a.id === activeId,
        ...(a.layerId ? { layerId: a.layerId } : {}),
      });
    });
  }, [zoneFilteredAnnotations, loaded, post, activeId, hiddenNotes]);

  useEffect(() => {
    if (activeId && activeCardRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeId]);

  // Click-outside to close any open panel
  useEffect(() => {
    if (!openPanel) return;
    const onDocClick = (e: MouseEvent) => {
      if (topbarRef.current && !topbarRef.current.contains(e.target as Node)) {
        setOpenPanel(null);
      }
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenPanel(null); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [openPanel]);

  const togglePanel = (kind: NonNullable<PanelKind>) =>
    setOpenPanel((cur) => (cur === kind ? null : kind));

  // Show/hide a BIM model on the point cloud. First show ensures the GLB exists —
  // triggering our IFC→GLB conversion (or RVT→IFC→GLB) if it doesn't — then loads
  // it into the Potree scene; later toggles just flip visibility.
  const bimBase = (fileId: string) =>
    (bimFiles.find((f) => f.id === fileId)?.extension || '').toLowerCase() === 'rvt' ? 'rvt2ifc' : 'ifc3d';

  // error.txt can hold a full Python traceback — the file row fits one line.
  const bimFriendlyError = (raw?: string, fallback = 'conversion failed') => {
    const lines = (raw || '').trim().split('\n').map((l) => l.trim()).filter(Boolean);
    const last = lines[lines.length - 1] || fallback;
    return last.length > 140 ? `${last.slice(0, 140)}…` : last;
  };

  const clearBimConverting = (fileId: string) =>
    setBimConverting((p) => { const rest = { ...p }; delete rest[fileId]; return rest; });

  const failBim = (fileId: string, message: string) => {
    clearBimConverting(fileId);
    setBimVisible((v) => ({ ...v, [fileId]: false }));
    setBimError((prev) => ({ ...prev, [fileId]: message }));
  };

  const postBimLoad = (fileId: string) => {
    if (bimPending.current.has(fileId)) return;   // load already in flight in the iframe
    if (bimLoaded.current.has(fileId)) {
      post({ type: 'setBimModelVisible', fileId, visible: true });
      return;
    }
    bimPending.current.add(fileId);
    const tok = localStorage.getItem(TOKEN_KEY) || '';
    const saved = bimFilesRef.current.find((f) => f.id === fileId)?.sceneTransform ?? null;
    // Prod: the GLB from CloudFront's nearest edge.
    cdnFolder(fileId, { headers: { Authorization: `Bearer ${tok}` } }).then((folder) => {
      const url = folder ? cdnAssetUrl(folder, 'model.glb') : `/api/ifc3d/file/${fileId}?token=${encodeURIComponent(tok)}`;
      post({ type: 'loadBimModel', fileId, url, transform: saved });
    });
  };

  const toggleBim = (fileId: string) => {
    const next = !(bimVisible[fileId] ?? false);
    setBimVisible((v) => ({ ...v, [fileId]: next }));
    if (!next) {
      post({ type: 'setBimModelVisible', fileId, visible: false });
      return;
    }
    setBimError((e) => { const rest = { ...e }; delete rest[fileId]; return rest; });
    if (bimLoaded.current.has(fileId)) {
      post({ type: 'setBimModelVisible', fileId, visible: true });
      return;
    }
    if (bimConverting[fileId] || bimPending.current.has(fileId)) return;   // already under way
    // Converting is set BEFORE the request: rvt2ifc streams the RVT to Autodesk
    // inside this POST (minutes for big files) — the row must show "converting…"
    // and block re-submits for that whole window, not just after the response.
    setBimConverting((p) => ({ ...p, [fileId]: true }));
    (async () => {
      try {
        const tok = localStorage.getItem(TOKEN_KEY) || '';
        // Idempotent on the backend: returns ready immediately when the GLB already
        // exists, otherwise submits the conversion job.
        const res = await fetch(`/api/${bimBase(fileId)}/translate/${fileId}`, {
          method: 'POST', headers: { Authorization: `Bearer ${tok}` },
        });
        const st = (await res.json().catch(() => ({}))) as { status?: string; error?: string };
        if (!res.ok) throw new Error(bimFriendlyError(st.error, 'could not start conversion'));
        if (st.status === 'ready') {
          clearBimConverting(fileId);
          if (bimVisibleRef.current[fileId]) postBimLoad(fileId);
        }
        // else: stays converting — the status poll takes over.
      } catch (err) {
        failBim(fileId, err instanceof Error ? err.message : 'conversion failed');
      }
    })();
  };

  const toggleBimRef = useRef(toggleBim);
  useEffect(() => { toggleBimRef.current = toggleBim; });

  // Poll running conversions; load each model once its GLB is ready. Decisions use
  // bimVisibleRef (fresh), never the closure — a tick can resolve after a toggle.
  // Keyed on the joined id set so toggling other rows doesn't reset the cadence.
  const bimConvertingIds = Object.keys(bimConverting).filter((id) => bimConverting[id]).sort().join(',');
  useEffect(() => {
    if (!bimConvertingIds) return;
    const ids = bimConvertingIds.split(',');
    const timer = window.setInterval(async () => {
      if (bimPollBusy.current) return;   // previous tick still running — don't overlap
      bimPollBusy.current = true;
      try {
        const tok = localStorage.getItem(TOKEN_KEY) || '';
        for (const id of ids) {
          try {
            const res = await fetch(`/api/${bimBase(id)}/status/${id}`, { headers: { Authorization: `Bearer ${tok}` } });
            const st = (await res.json().catch(() => ({}))) as { status?: string; error?: string };
            if (!res.ok) {
              // Persistent 401/5xx must not show "converting…" forever.
              const fails = (bimPollFails.current.get(id) ?? 0) + 1;
              bimPollFails.current.set(id, fails);
              if (fails >= 3) {
                bimPollFails.current.delete(id);
                failBim(id, bimFriendlyError(st.error, 'could not check conversion status'));
              }
              continue;
            }
            bimPollFails.current.delete(id);
            if (st.status === 'ready') {
              clearBimConverting(id);
              if (bimVisibleRef.current[id]) postBimLoad(id);
            } else if (st.status === 'failed') {
              failBim(id, bimFriendlyError(st.error));
            }
          } catch { /* network hiccup — keep polling */ }
        }
      } finally {
        bimPollBusy.current = false;
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [bimConvertingIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeBimOpacity = (v: number) => {
    setBimOpacity(v);
    post({ type: 'setBimOpacity', value: v });
  };

  // Placement persistence: debounced per file, absolute coordinates, never in
  // share mode (guests may nudge a layer to look, nothing is written).
  const saveSceneTransform = useCallback((fileId: string, transform: SceneXform | null) => {
    if (isShareMode) return;
    const prev = saveTimers.current.get(fileId);
    if (prev) window.clearTimeout(prev);
    saveTimers.current.set(fileId, window.setTimeout(() => {
      saveTimers.current.delete(fileId);
      setFileSceneTransform({ variables: { fileId, transform } }).catch(() => { /* a lost save shows on reload */ });
    }, 600));
  }, [isShareMode, setFileSceneTransform]);
  saveSceneTransformRef.current = saveSceneTransform;
  useEffect(() => () => { saveTimers.current.forEach((t) => window.clearTimeout(t)); }, []);

  const bimAbsolute = (fileId: string, xf: BimXform): SceneXform | null => {
    const base = bimBaseCenter[fileId];
    if (!base) return null;
    return { x: base.x + xf.dx, y: base.y + xf.dy, z: base.z + xf.dz, rot: xf.rot, scale: xf.scale };
  };
  const updateBimXform = (fileId: string, patch: Partial<BimXform>) => {
    const next = { ...(bimXform[fileId] ?? DEFAULT_BIM_XFORM), ...patch };
    setBimXform((prev) => ({ ...prev, [fileId]: next }));
    const abs = bimAbsolute(fileId, next);
    post({ type: 'setBimTransform', fileId, transform: abs ?? next });
    if (abs) saveSceneTransform(fileId, sameXform(abs, bimBaseCenter[fileId]) ? null : abs);
  };
  const resetBimXform = (fileId: string) => updateBimXform(fileId, DEFAULT_BIM_XFORM);

  // ── Scene layers: orthomosaic / drawing / splat inside the engine ────────
  const authHeaders = (): Record<string, string> => (isShareMode ? {} : { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) || ''}` });
  const shareQS = (lead: '?' | '&') => (isShareMode ? `${lead}shareToken=${encodeURIComponent(shareToken!)}` : '');

  const failLayer = (fileId: string, message: string) => {
    layerPending.current.delete(fileId);
    setLayerOn((v) => ({ ...v, [fileId]: false }));
    setLayerError((prev) => ({ ...prev, [fileId]: message }));
  };

  const postLayerLoad = async (file: PCFile) => {
    const fileId = file.id;
    const kind = layerKindOf(file);
    if (!kind || layerPending.current.has(fileId)) return;
    if (layerLoaded.current.has(fileId)) { post({ type: 'setLayerVisible', fileId, visible: true }); return; }
    layerPending.current.add(fileId);
    const common = { type: 'loadLayer', kind, fileId, transform: file.sceneTransform ?? null, visible: true };
    try {
      if (kind === 'ortho') {
        const params = new URLSearchParams();
        if (import.meta.env.DEV) params.set('mode', 'proxy');
        if (isShareMode) params.set('shareToken', shareToken!);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const res = await fetch(`/api/ortho/${fileId}${qs}`, { headers: authHeaders() });
        const d = (await res.json().catch(() => ({}))) as { status?: string; url?: string; metaUrl?: string; error?: string };
        if (!res.ok || !d.url || !d.metaUrl) throw new Error(d.error || (d.status === 'processing' ? 'still converting' : 'not available'));
        post({ ...common, url: d.url, metaUrl: d.metaUrl });
      } else if (kind === 'splat') {
        const res = await fetch(`/api/files/${fileId}/gaussian-url${shareQS('?')}`, { headers: authHeaders() });
        const d = (await res.json().catch(() => ({}))) as { url?: string | null; tiles?: unknown; meta?: { offset?: number[] | null } };
        if (!res.ok || !d.url) throw new Error('splat scene not ready');
        post({ ...common, url: d.url, tiles: d.tiles ?? null, offset: d.meta?.offset ?? null });
      } else {
        // The iframe fetches these itself; its fetch wrapper adds the token to
        // the API ones. The manifest always comes from the API; on prod the
        // SVG comes from CloudFront, still gzipped, and the layer unpacks it.
        const folder = canUnpackGzip()
          ? await cdnFolder(fileId, isShareMode ? { shareToken } : { headers: authHeaders() })
          : null;
        post({
          ...common,
          manifestUrl: `/api/dwg2d/manifest/${fileId}${shareQS('?')}`,
          tabUrl: folder ? cdnAssetUrl(folder, 'tab-0.svg.gz') : `/api/dwg2d/tab/${fileId}/0${shareQS('?')}`,
          fileUrl: folder ? cdnAssetUrl(folder, 'drawing.svg.gz') : `/api/dwg2d/file/${fileId}${shareQS('?')}`,
        });
      }
    } catch (err) {
      failLayer(fileId, err instanceof Error ? err.message : 'could not load');
    }
  };

  const setAllClouds = (visible: boolean) => {
    readyFiles.forEach((f) => {
      if ((visibility[f.id] ?? true) === visible) return;
      setFileVisible(f.id, visible);
      onVisibilityChange?.(f.id, visible);
    });
  };

  const setLayerVisible = (fileId: string, visible: boolean) => {
    const file = layerFileById[fileId];
    if (!file) return;
    if ((layerOnRef.current[fileId] ?? false) === visible) return;
    setLayerOn((v) => ({ ...v, [fileId]: visible }));
    layerOnRef.current = { ...layerOnRef.current, [fileId]: visible };
    if (!visible) {
      post({ type: 'setLayerVisible', fileId, visible: false });
      return;
    }
    setLayerError((e) => { const rest = { ...e }; delete rest[fileId]; return rest; });
    // A splat and the point clouds never share the scene: switching a splat
    // on hides the clouds and the other splats.
    if (layerKindOf(file) === 'splat') {
      setAllClouds(false);
      layerFiles.forEach((f) => { if (f.id !== fileId && layerKindOf(f) === 'splat' && layerOnRef.current[f.id]) setLayerVisible(f.id, false); });
    }
    postLayerLoad(file);
  };
  const setLayerVisibleRef = useRef(setLayerVisible);
  useEffect(() => { setLayerVisibleRef.current = setLayerVisible; });

  const updateLayerXform = (fileId: string, patch: Partial<SceneXform>) => {
    const cur = layerXform[fileId] ?? layerDefault[fileId];
    if (!cur) return;
    const next = { ...cur, ...patch };
    setLayerXform((prev) => ({ ...prev, [fileId]: next }));
    post({ type: 'setLayerTransform', fileId, transform: next });
    saveSceneTransform(fileId, sameXform(next, layerDefault[fileId]) ? null : next);
  };
  const resetLayerXform = (fileId: string) => {
    const def = layerDefault[fileId];
    if (def) updateLayerXform(fileId, def);
  };
  const changeLayerOpacity = (fileId: string, v: number) => {
    setLayerOpacity((prev) => ({ ...prev, [fileId]: v }));
    post({ type: 'setLayerOpacity', fileId, value: v });
  };

  // Entry configuration: once the viewer is up, switch the requested layers
  // on (the project page's menu, applied once per mount).
  const startApplied = useRef(false);
  useEffect(() => {
    if (!loaded || startApplied.current || !startLayers?.length) return;
    if (startLayers.some((id) => !layerFileById[id])) return;   // file list still loading
    startApplied.current = true;
    startLayers.forEach((id) => setLayerVisibleRef.current(id, true));
  }, [loaded, startLayers, layerFileById]); // eslint-disable-line react-hooks/exhaustive-deps

  // div, not label: a label would forward clicks on the value readout to the
  // range input instead of opening the type-in editor.
  // free: a position slider, symmetric around the default placement (min =
  // -max); its span is FreeRange's and the type-in accepts any number.
  const bimSlider = (
    label: string, value: number, min: number, max: number, step: number,
    onChange: (v: number) => void, fmt: (v: number) => string, free = false,
  ) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: '#4b5569', padding: '2px 0' }}>
      <span style={{ width: 54 }}>{label}</span>
      {free ? (
        <FreeRange value={value} half={max} onChange={onChange} />
      ) : (
        <input type="range" min={min} max={max} step={step} value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))} style={{ flex: 1 }} />
      )}
      <BimValueInput value={value} min={free ? -Infinity : min} max={free ? Infinity : max} fmt={fmt} onCommit={onChange} />
    </div>
  );

  const handleNavMode = (mode: NavMode) => {
    setNavMode(mode);
    post({ type: 'setNavigationMode', mode });
  };

  const cancelZoneEdit = useCallback(() => {
    setZoneEditMode(false);
    setZoneDraft(null);
    setZoneName('');
    post({ type: 'setFocusZoneEdit', enabled: false });
  }, [post]);

  const startZoneEdit = useCallback(() => {
    setZoneEditMode(true);
    setZoneDraft(null);
    setZoneName('');
    post({ type: 'setFocusZoneEdit', enabled: true });
    if (annotationMode) { setAnnotationMode(false); post({ type: 'setAnnotationMode', enabled: false }); }
    if (measureMode) { setMeasureKind(null); setMeasureDist(null); post({ type: 'setMeasureMode', kind: null }); }
  }, [annotationMode, measureMode, post]);

  const handleSaveZone = useCallback(() => {
    if (!zoneDraft || !zoneName.trim() || !annotationFileId) return;
    createFocusZone({ variables: { fileId: annotationFileId, name: zoneName.trim(), min: zoneDraft.min, max: zoneDraft.max } });
  }, [zoneDraft, zoneName, annotationFileId, createFocusZone]);

  // Adding notes / measurements starts from their panels (same pattern as the
  // zones panel's "New zone") — the modes themselves run on the canvas, with
  // the floating banner carrying instructions + cancel while the panel is shut.
  const startAddNote = useCallback(() => {
    setPendingPos(null);
    setPendingText('');
    setAnnotationMode(true);
    post({ type: 'setAnnotationMode', enabled: true });
    if (measureMode) { setMeasureKind(null); setMeasureDist(null); post({ type: 'setMeasureMode', kind: null }); }
    if (zoneEditMode) cancelZoneEdit();
  }, [measureMode, zoneEditMode, cancelZoneEdit, post]);

  const cancelAddNote = useCallback(() => {
    setAnnotationMode(false);
    post({ type: 'setAnnotationMode', enabled: false });
  }, [post]);

  const cancelMeasure = useCallback(() => {
    setMeasureKind(null);
    setMeasureDist(null);
    post({ type: 'setMeasureMode', kind: null });
  }, [post]);

  const selectMeasureKind = useCallback((kind: 'line' | 'area') => {
    setMeasureKind(kind);
    setMeasureDist(null);
    post({ type: 'setMeasureMode', kind });
    if (annotationMode) { setAnnotationMode(false); post({ type: 'setAnnotationMode', enabled: false }); }
    if (zoneEditMode) cancelZoneEdit();
  }, [annotationMode, zoneEditMode, cancelZoneEdit, post]);

  const handleSaveAnnotation = useCallback(() => {
    const fileId = pendingFileId ?? annotationFileId;
    if (!pendingPos || !pendingText.trim() || !fileId) return;
    if (isShareMode) {
      sharedCreateAnnotation({ variables: {
        token: shareToken, fileId, position: pendingPos, text: pendingText.trim(), guestName: guestName.trim() || null,
      } });
    } else {
      createAnnotation({ variables: { fileId, position: pendingPos, text: pendingText.trim() } });
    }
  }, [pendingPos, pendingFileId, pendingText, annotationFileId, createAnnotation, isShareMode, sharedCreateAnnotation, shareToken, guestName]);

  const handleReply = useCallback((id: string) => {
    if (!replyText.trim()) return;
    if (isShareMode) {
      sharedReplyToAnnotation({ variables: {
        token: shareToken, annotationId: id, text: replyText.trim(), guestName: guestName.trim() || null,
      } });
    } else {
      replyToAnnotation({ variables: { annotationId: id, text: replyText.trim() } });
    }
  }, [replyText, replyToAnnotation, isShareMode, sharedReplyToAnnotation, shareToken, guestName]);

  const handleCardClick = useCallback((id: string, position: AnnotationPosition, layerId?: string | null) => {
    setActiveId(id);
    setExpandedId((p) => p === id ? null : id);
    post({ type: 'highlightMarker', id });
    post({ type: 'flyToAnnotation', id, position: layerId ? null : position });
  }, [post]);

  const handlePointSize = (v: number) => { setPointSize(v); post({ type: 'setPointSize', value: v }); };
  const handleOpacity   = (v: number) => { setOpacity(v);   post({ type: 'setOpacity',   value: v }); };
  const handleBrightness = (v: number) => { setBrightness(v); post({ type: 'setBrightness', value: v }); };
  const handleColorMode = (m: ColorMode) => { setColorMode(m); post({ type: 'setColorMode', value: m }); };
  const handleEdlToggle = () => { const n = !edlEnabled; setEdlEnabled(n); post({ type: 'setEDL', enabled: n }); };

  const handleFlyToMarker = (p: PanoramaEntry) => post({ type: 'flyToMarker', position: p.position });

  // An ortho's measurement is stored in its map CRS: only the engine, which
  // placed it, knows where it sits in the world.
  const handleMeasureCardClick = (m: MeasurementItem) =>
    post(m.layerId
      ? { type: 'flyToMeasurement', id: m.id }
      : { type: 'flyToMeasurement', id: m.id, start: m.start, end: m.end, points: m.points });

  const setFileVisible = (fileId: string, visible: boolean) => {
    setVisibility((prev) => ({ ...prev, [fileId]: visible }));
    if (loaded) post({ type: 'setCloudVisible', fileId, visible });
  };

  const setInHiddenSet = (setter: React.Dispatch<React.SetStateAction<Set<string>>>) =>
    (id: string, visible: boolean) => setter((prev) => {
      const next = new Set(prev);
      if (visible) next.delete(id); else next.add(id);
      return next;
    });

  useImperativeHandle(handleRef, () => ({
    setFileVisible,
    setNoteVisible: setInHiddenSet(setHiddenNotes),
    setStationVisible: setInHiddenSet(setHiddenStations),
    setMeasurementVisible: setInHiddenSet(setHiddenMeasures),
    setActiveZone: (id: string | null) => setActiveZoneId(id),
    setBimVisible: (fileId: string, visible: boolean) => {
      if ((bimVisibleRef.current[fileId] ?? false) !== visible) toggleBimRef.current(fileId);
    },
    setLayerVisible: (fileId: string, visible: boolean) => setLayerVisibleRef.current(fileId, visible),
    openAlign: (fileId: string) => { setAlignFor(fileId); setOpenPanel('align'); },
    openClosestPanorama: () => post({ type: 'openClosestPanorama' }),
    focusNote: (id: string) => railActionsRef.current.focusNote(id),
    flyToStation: (id: string) => railActionsRef.current.flyToStation(id),
    flyToMeasurement: (id: string) => railActionsRef.current.flyToMeasurement(id),
    flyToZone: (id: string) => railActionsRef.current.flyToZone(id),
    deleteNote: (id: string) => { deleteAnnotation({ variables: { annotationId: id } }); },
    deleteMeasurement: (id: string) => deleteMeasurementById(id),
    deleteZone: (id: string) => {
      setActiveZoneId((cur) => (cur === id ? null : cur));
      deleteFocusZoneMut({ variables: { zoneId: id } });
    },
    setBasemap: (patch) => changeBasemapRef.current(patch),
    nudgeBasemap: (forward, right, turn) => nudgeBasemapRef.current(forward, right, turn),
    resetBasemapTune: () => resetBasemapTuneRef.current(),
    saveBasemap: () => { saveBasemapRef.current(); },
    revertBasemap: () => revertBasemap(),
    openSitePlacement: () => openSitePlacementRef.current(),
    goToArea: (key: string) => railActionsRef.current.goToArea(key),
    renameArea: (key: string, name: string) => railActionsRef.current.renameArea(key, name),
    setAreaMode: (mode: TourAreaMode) => railActionsRef.current.setAreaMode(mode),
    openAreaEditor: () => setAreaEditorOpen(true),
  }), [loaded, post]); // eslint-disable-line react-hooks/exhaustive-deps

  // The Scene rail lists what exists; these open it in the viewer. Kept in a
  // ref because the handle above is memoised and the lists change.
  const railActionsRef = useRef({
    focusNote: (_id: string) => {},
    flyToStation: (_id: string) => {},
    flyToMeasurement: (_id: string) => {},
    flyToZone: (_id: string) => {},
    goToArea: (_key: string) => {},
    renameArea: async (_key: string, _name: string) => {},
    setAreaMode: async (_mode: TourAreaMode) => {},
  });
  useEffect(() => {
    railActionsRef.current = {
      focusNote: (id) => {
        const a = annotations.find((x) => x.id === id);
        if (!a) return;
        setActiveId(id);
        setExpandedId(id);
        setOpenPanel('annotations');
        post({ type: 'highlightMarker', id });
        post({ type: 'flyToAnnotation', id, position: a.layerId ? null : a.position });
      },
      flyToStation: (id) => {
        const p = allPanoEntries.find((x) => x.id === id);
        if (p) handleFlyToMarker(p);
      },
      flyToMeasurement: (id) => {
        const m = measurements.find((x) => x.id === id) ?? layerMeasureList.find((x) => x.id === id);
        if (m) handleMeasureCardClick(m);
      },
      flyToZone: (id) => {
        const z = focusZones.find((x) => x.id === id);
        if (z) post({ type: 'flyToMeasurement', start: z.min, end: z.max });
      },
      // From the scan you stand at in the tour, else from the camera: you
      // arrive at the area's near edge, facing into it.
      goToArea: (key) => {
        const area = tourAreaList.find((a) => a.key === key);
        if (!area) return;
        const from = (tourStationId ? allPanoEntries.find((p) => p.id === tourStationId)?.position : null) ?? cameraPosRef.current;
        const target = from ? nearestStation(area.stations, from) : area.representative;
        if (target.id === tourStationId) return;
        const dx = from ? target.position.x - from.x : 0, dy = from ? target.position.y - from.y : 0;
        const heading = Math.hypot(dx, dy) > 1e-6 ? Math.atan2(dy, dx) : undefined;
        setOpenPano(null);
        post({ type: 'goToPanorama', id: target.id, heading, pitch: 0, travel: true });
      },
      // A drawn area is renamed in its group (an empty name keeps the old one).
      renameArea: async (key, name) => {
        const area = tourAreaList.find((a) => a.key === key);
        if (!area || !projectId) throw new Error('This area is no longer in the tour');
        if (area.groupId) {
          if (!name.trim()) return;
          const groups = areaGroups.map((g) => (g.id === area.groupId ? { id: g.id, name, stationIds: g.stationIds } : { id: g.id, name: g.name, stationIds: g.stationIds }));
          await saveTourAreaGroupsMut({ variables: { projectId, groups } });
          return;
        }
        await setTourAreaNameMut({ variables: { projectId, stationId: area.nameStation, name, mode: areaMode } });
      },
      setAreaMode: async (mode) => {
        if (!projectId) return;
        await setTourAreaModeMut({ variables: { projectId, mode } });
      },
    };
  });

  const toggleVisibility = (fileId: string) => {
    const next = !(visibility[fileId] ?? true);
    setFileVisible(fileId, next);
    onVisibilityChange?.(fileId, next);
    if (next) layerFiles.forEach((f) => { if (layerKindOf(f) === 'splat' && layerOnRef.current[f.id]) setLayerVisibleRef.current(f.id, false); });
  };

  const allHidden  = readyFiles.length > 0 && readyFiles.every((f) => visibility[f.id] === false);
  const cloudsOn = readyFiles.length > 0 && !allHidden;
  // Something other than a cloud is drawn (ortho / splat / drawing / BIM):
  // the cloud-only chrome (tour switch, all-hidden banner) stays out of the way.
  const layerBaseOn = activeLayerFileId !== null || Object.values(bimVisible).some(Boolean);
  // Nothing drawn at all: the navigation switch, tools and HUD have nothing
  // to act on, so only the rail (and the empty-scene banner) stays.
  const sceneEmpty = loaded && (readyFiles.length === 0 || allHidden) && !layerBaseOn;
  // Notes, measurements and zones are stored under the project's first point
  // cloud, so their tools only make sense while a cloud is on. A project with
  // no cloud at all keeps them for the layer that stands in as the note file.
  const cloudToolsOn = readyFiles.length > 0 ? !allHidden : !!annotationFileId;
  cloudMeasureRef.current = { fileId: cloudMeasureFileId, on: cloudsOn };
  // Notes also live on splats and orthomosaics, so the note tool stays while
  // one of those is on.
  const noteToolsOn = cloudToolsOn || noteLayerIds.length > 0;
  // Measurements also live on orthomosaics (focus zones stay cloud-only).
  const measureToolsOn = cloudToolsOn || orthoLayerIds.length > 0;
  useEffect(() => {
    if (!noteToolsOn && annotationMode) cancelAddNote();
    if (!measureToolsOn && measureMode) cancelMeasure();
    if (!cloudToolsOn && zoneEditMode) cancelZoneEdit();
  }, [cloudToolsOn, noteToolsOn, measureToolsOn, annotationMode, measureMode, zoneEditMode, cancelAddNote, cancelMeasure, cancelZoneEdit]);
  const allVisible = readyFiles.length > 0 && readyFiles.every((f) => visibility[f.id] !== false);

  useEffect(() => {
    if (!allHidden || !openPano) return;
    setOpenPano(null);
    setNavMode('orbit');
    post({ type: 'setNavigationMode', mode: 'orbit' });
  }, [allHidden, openPano, post]);

  const toggleFolderVisibility = (folderId: string) => {
    const folderFiles = readyFiles.filter((f) => f.folderId === folderId);
    const allFolderVisible = folderFiles.every((f) => visibility[f.id] !== false);
    const makeVisible = !allFolderVisible;
    const patch: Record<string, boolean> = {};
    folderFiles.forEach((f) => { patch[f.id] = makeVisible; });
    setVisibility((prev) => ({ ...prev, ...patch }));
    if (loaded) folderFiles.forEach((f) => post({ type: 'setCloudVisible', fileId: f.id, visible: makeVisible }));
    folderFiles.forEach((f) => onVisibilityChange?.(f.id, makeVisible));
  };

  const toggleAll = () => {
    const makeVisible = !allVisible;
    const patch: Record<string, boolean> = {};
    readyFiles.forEach((f) => { patch[f.id] = makeVisible; });
    setVisibility((prev) => ({ ...prev, ...patch }));
    if (loaded) readyFiles.forEach((f) => post({ type: 'setCloudVisible', fileId: f.id, visible: makeVisible }));
    readyFiles.forEach((f) => onVisibilityChange?.(f.id, makeVisible));
  };

  const jwtToken = localStorage.getItem(TOKEN_KEY) || '';
  // Density/size A/B params (pbIdle, pbMove, psMin, psK, sizeType) pass through from the
  // page URL to the viewer iframe, so tuning candidates can be compared on a deployed
  // build by editing the address bar — no redeploy.
  const tuneParams = (() => {
    const src = new URLSearchParams(window.location.search);
    const out = new URLSearchParams();
    for (const k of ['pbIdle', 'pbMove', 'psMin', 'psK', 'psMul', 'sizeType', 'pws', 'bubble', 'hq', 'hier', 'hud', 'batch', 'pr', 'ondemand',
      'moveLevel', 'moveHq', 'psMax', 'edl', 'edlStrength', 'pbFull', 'conc', 'reqNodes', 'reqMB', 'workerFetch', 'adaptMove', 'moveFps', 'mm', 'inflight', 'prefetchKB',
      'adaptIdle', 'idleFps', 'rampMs', 'refineFps', 'refineHq', 'hz', 'batchLevel', 'walk', 'run', 'wheelS', 'psNear', 'psNearM', 'psFarM',
      'tiledebug', 'flashMs', 'adaptBudget', 'splatFps', 'trace', 'inflightMax', 'centerBias']) {
      const v = src.get(k);
      if (v) out.set(k, v);
    }
    const q = out.toString();
    return q ? `&${q}` : '';
  })();
  const viewerPage = (localStorage.getItem('geodraft_engine') === 'potree'
    || new URLSearchParams(window.location.search).get('engine') === 'potree')
    ? '/potree-viewer.html' : '/geo-viewer.html';
  // A layers-only scene (ortho / splat / drawing without a point cloud) boots
  // the engine with no fileIds; it frames whatever layer arrives first.
  const iframeFileIds = readyFiles.map((f) => f.id).sort().join(',');
  const cloudsParam = cloudsHiddenAtStart ? '&clouds=hidden' : '';
  const iframeSrc = (readyFiles.length > 0 || hasLayerFiles)
    ? isShareMode
      ? `${viewerPage}?fileIds=${encodeURIComponent(iframeFileIds)}&shareToken=${encodeURIComponent(shareToken!)}${cloudsParam}${tuneParams}`
      : `${viewerPage}?fileIds=${encodeURIComponent(iframeFileIds)}&token=${encodeURIComponent(jwtToken)}${cloudsParam}${tuneParams}`
    : undefined;

  // Stable reference so panorama-overlay effects (markers, occlusion) don't
  // re-fire on every unrelated re-render.
  const allPanoEntries = useMemo(() => Object.values(panoramasByFile).flat(), [panoramasByFile]);

  // ── Advanced Virtual Tour ───────────────────────────────────────────────────
  // Areas are the scans the rings connect (utils/tourAreas.ts), found from the
  // stations the tour shows; their names are saved on the project.
  const advancedTour = access.advancedTour;
  const { data: sharedAreaData } = useQuery(SHARED_TOUR_AREAS_QUERY, {
    variables: { token: shareToken },
    skip: !isShareMode || !advancedTour,
  });
  const areaSettings = (isShareMode ? sharedAreaData?.sharedTourAreas : sceneProjectData?.project?.tourAreas) as
    { mode: TourAreaMode; names: TourAreaName[]; groups: TourAreaGroup[] } | undefined;
  const areaMode: TourAreaMode = areaSettings?.mode ?? 'gaps';
  const areaNames: TourAreaName[] = areaSettings?.names ?? NO_AREA_NAMES;
  const areaGroups: TourAreaGroup[] = areaSettings?.groups ?? NO_AREA_GROUPS;
  const fileLabelSig = readyFiles.map((f) => `${f.id}=${f.originalName}`).join('|');
  const fileLabels = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of readyFiles) {
      const dot = f.originalName.lastIndexOf('.');
      m.set(f.id, dot > 0 ? f.originalName.slice(0, dot) : f.originalName);
    }
    return m;
  }, [fileLabelSig]); // eslint-disable-line react-hooks/exhaustive-deps
  const tourStations = useMemo(
    () => allPanoEntries.filter((p) => !hiddenStations.has(p.id) && (!activeZone || pointInBox(p.position, activeZone.min, activeZone.max))),
    [allPanoEntries, hiddenStations, activeZone],
  );
  const withGrid = !!occIndex;
  // The floor under each scan: measured in the occupancy grid where there is
  // one, else a scanner height below the scan (storeys for the floor split).
  const floorZOf = useMemo(() => {
    const m = new Map<string, number>();
    if (!advancedTour) return m;
    for (const p of tourStations) m.set(p.id, (occIndex ? floorBelow(occIndex, p.position) : null) ?? p.position.z - TOUR_SCANNER_HEIGHT);
    return m;
  }, [advancedTour, tourStations, occIndex]);
  const floorZ = useCallback((s: TourStation) => floorZOf.get(s.id) ?? s.position.z - TOUR_SCANNER_HEIGHT, [floorZOf]);
  const tourAreaList = useMemo(
    () => (advancedTour
      ? tourAreas(tourStations, { link: ringLink(withGrid), fileName: (id) => fileLabels.get(id) ?? '', names: areaNames, mode: areaMode, groups: areaGroups, floorZ })
      : []),
    [advancedTour, tourStations, withGrid, fileLabels, areaNames, areaMode, areaGroups, floorZ],
  );
  const [areaEditorOpen, setAreaEditorOpen] = useState(false);
  const floorsForEditor = useMemo(
    () => (areaEditorOpen ? floorLevels(tourStations, floorZ) : null),
    [areaEditorOpen, tourStations, floorZ],
  );
  const tourAreaOf = useMemo(() => areaByStation(tourAreaList), [tourAreaList]);
  const tourMapModel = useMemo(
    () => (tourAreaList.length ? buildTourMap(tourAreaList, ringLink(withGrid)) : null),
    [tourAreaList, withGrid],
  );
  // Both start hidden every time the Scene opens; they stay as set across hops.
  const [tourCompassOn, setTourCompassOn] = useState(false);
  const [tourMapOn, setTourMapOn] = useState(false);
  const [tourStationId, setTourStationId] = useState<string | null>(null);
  useEffect(() => { if (!openPano) setTourStationId(null); }, [openPano]);
  const northDeg = frameSource === 'placement' && sitePlacement ? sitePlacement.heading : 0;
  const tourExtras = useMemo<TourExtras | null>(
    () => (advancedTour
      ? { areas: tourAreaList, areaOf: tourAreaOf, map: tourMapModel, northDeg, compassOn: tourCompassOn, mapOn: tourMapOn, onCompass: setTourCompassOn, onMap: setTourMapOn }
      : null),
    [advancedTour, tourAreaList, tourAreaOf, tourMapModel, northDeg, tourCompassOn, tourMapOn],
  );
  const canRenameAreas = !isShareMode && advancedTour && canEditProject((sceneProjectData?.project?.myRole ?? null) as ProjectRole | null);
  const [setTourAreaNameMut] = useMutation(SET_TOUR_AREA_NAME_MUTATION);
  const [setTourAreaModeMut] = useMutation(SET_TOUR_AREA_MODE_MUTATION);
  const [saveTourAreaGroupsMut] = useMutation(SAVE_TOUR_AREA_GROUPS_MUTATION);

  const autoTourFired = useRef(false);
  useEffect(() => {
    if (!autoTour || autoTourFired.current || !loaded || allPanoEntries.length === 0) return;
    autoTourFired.current = true;
    // The station markers reach the iframe on the effect after this one.
    const t = window.setTimeout(() => post({ type: 'openClosestPanorama' }), 800);
    return () => window.clearTimeout(t);
  }, [autoTour, loaded, allPanoEntries.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Push the rail inventory (notes / photos / measurements / zones + visibility)
  // to the Combined View parent. Runs every render behind a signature guard —
  // the source arrays get fresh (empty) identities while queries are in flight,
  // and dep-array identity would loop parent setState forever.
  const inventorySigRef = useRef('');
  useEffect(() => {
    if (!onInventoryChange) return;
    const multiFile = Object.keys(panoramasByFile).length > 1;
    const fileName = (fid: string) => files.find((f) => f.id === fid)?.originalName ?? '';
    const inv: SmartViewInventory = {
      basemap: basemapConfig && basemapLocatable(sceneProject, crs, sitePlacement) ? {
        providers: basemapConfig.providers.map((p) => ({ id: p.id, label: p.label })),
        prefs: effectivePrefs,
        state: basemapState(effectivePrefs, basemapConfig, frame),
        frame: frameSource,
        gridFit: fit,
        sources: {
          file: !!crs,
          pin: !!sitePlacement || !!pinPlacement(sceneProject, contentCentre),
          auto: sceneBasemapFrame({ crs, fit, position: 'auto', gridAdjust: null, placement: sitePlacement, location: sceneProject, centre: contentCentre }).source,
        },
        tuned: frameSource === 'crs' ? !!basemapPrefs.gridAdjust : frameSource === 'placement',
        gridAdjust: basemapPrefs.gridAdjust,
        epsg: crs?.epsg ?? null,
        placement: sitePlacement,
        terrainAvailable: !!basemapConfig.terrainUrl,
        status: basemapStatus,
        canEdit: !isShareMode,
        unsaved: !isShareMode && (!sameProjectBasemap(basemapPrefs, savedBasemap) || !sameSitePlacement(sitePlacement, savedSitePlacement)),
        saving: savingBasemap || savingPlacement,
      } : null,
      notes: annotations.filter((a) => !a.layerId).map((a) => ({ id: a.id, label: a.text, visible: !hiddenNotes.has(a.id), deletable: !isShareMode })),
      layerNotes: Object.fromEntries(noteLayerIds.map((fid) => [
        fid, annotations.filter((a) => a.layerId === fid).map((a) => ({ id: a.id, label: a.text, visible: !hiddenNotes.has(a.id) })),
      ])),
      photos: allPanoEntries.map((p) => ({
        id: p.id,
        label: `Scan ${p.index + 1}${multiFile ? ` · ${fileName(p.fileId)}` : ''}`,
        visible: !hiddenStations.has(p.id),
      })),
      measurements: measurements.map((m) => ({ id: m.id, label: measurementLabel(m), visible: !hiddenMeasures.has(m.id), deletable: canDeleteMeasurement(m) })),
      layerMeasurements: Object.fromEntries(orthoLayerIds.map((fid) => [
        fid, (layerMeasures[fid] ?? []).map((m) => ({ id: m.id, label: measurementLabel(m), visible: !hiddenMeasures.has(m.id), deletable: canDeleteMeasurement(m) })),
      ])),
      zones: focusZones.map((z) => ({ id: z.id, label: z.name, active: z.id === activeZoneId })),
      bim: canUseBimFiles ? bimFiles.map((f) => ({
        id: f.id,
        visible: !!bimVisible[f.id],
        converting: !!bimConverting[f.id],
        error: bimError[f.id],
        georef: bimGeoref[f.id],
      })) : [],
      layers: layerFiles.map((f) => ({
        id: f.id,
        kind: layerKindOf(f) as SceneLayerKind,
        visible: !!layerOn[f.id],
        loading: !!layerOn[f.id] && !layerLoaded.current.has(f.id) && !layerError[f.id],
        error: layerError[f.id],
        georef: layerGeoref[f.id],
      })),
      areas: tourAreaList.map((a) => ({
        key: a.key,
        name: a.name,
        colour: a.colour,
        scans: a.stations.length,
        named: a.named,
        here: !!tourStationId && tourAreaOf.get(tourStationId)?.key === a.key,
      })),
      areaMode,
      canRenameAreas,
      loaded,
    };
    const sig = JSON.stringify(inv);
    if (sig === inventorySigRef.current) return;
    inventorySigRef.current = sig;
    onInventoryChange(inv);
  });

  // Stable color per file for the geometry inspector — order follows readyFiles
  // (sorted by id) so the same file keeps the same color across renders.
  const orderedFileIds = readyFiles.map((f) => f.id).slice().sort();
  const inspectColorByFile: Record<string, string> = {};
  orderedFileIds.forEach((id, i) => { inspectColorByFile[id] = INSPECT_COLORS[i % INSPECT_COLORS.length]; });

  // Sync the diagnostic bbox overlay to the iframe whenever the toggle changes
  // or new files finish loading (so their geometry is available to draw).
  // Admin-only feature — non-admin users never trigger overlay rendering.
  useEffect(() => {
    if (!loaded || !isAdmin) return;
    const items = orderedFileIds
      .filter((id) => cloudGeometry[id])
      .map((id) => ({ fileId: id, color: inspectColorByFile[id] }));
    post({ type: 'setBboxOverlay', enabled: bboxOverlayOn, items });
  }, [bboxOverlayOn, loaded, cloudGeometry, orderedFileIds.join(','), post, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  // When panoramas come from more than one point cloud file, prefix each photo
  // label with the parent file name so "0.jpg" / "1.jpg" entries are
  // distinguishable. Single-file uploads keep the bare image name.
  const panoFileLabels: Record<string, string> = {};
  readyFiles.forEach((f) => {
    const dot = f.originalName.lastIndexOf('.');
    panoFileLabels[f.id] = dot > 0 ? f.originalName.slice(0, dot) : f.originalName;
  });
  const multipleFilesHavePanos = Object.keys(panoramasByFile).length > 1;
  const labelForPano = (p: PanoramaEntry) =>
    multipleFilesHavePanos && panoFileLabels[p.fileId]
      ? `${panoFileLabels[p.fileId]} - ${p.image}`
      : p.image;
  const openAnnots     = annotations.filter((a) => a.status === 'open').length;
  const navModeLabel   = navMode === 'firstPerson' ? 'FIRST-PERSON'
                       : navMode === 'walk'        ? 'WALK'
                       : 'ORBIT';

  return (
    <div className={`${styles.container} ${embedded ? styles.containerEmbedded : ''}`}>
      {/* Viewer fills the entire viewport */}
      <div className={styles.viewerArea}>
        {iframeSrc && (
          <iframe
            key={iframeKey}
            ref={iframeRef}
            src={iframeSrc}
            className={styles.iframe}
            title="Point Cloud Viewer"
          />
        )}
      </div>

      {/* Floating top bar */}
      <header className={styles.topbar} ref={topbarRef}>
        <div className={styles.brand}>
          {!embedded && (
            <button className={styles.backBtn} onClick={() => isShareMode ? navigate(`/share/${shareToken}`) : navigate(-1)} title="Back">
              <Icon name="arrow_back" style={{ fontSize: 16 }} />
            </button>
          )}
          <div className={styles.brandText}>
            <span className={styles.brandLabel}>
              SMART VIEW
            </span>
            <span className={styles.brandTitle}>
              Project · {readyFiles.length} {`cloud${readyFiles.length === 1 ? '' : 's'}`}
            </span>
          </div>
        </div>

        {!sceneEmpty && <ModeSwitch value={navMode} onChange={handleNavMode} disabled={!loaded} />}

        {!(sceneEmpty && embedded) && (
        <div className={styles.rightCluster}>
          {/* ─ Resource buttons (open panels). Embedded in the Scene the rail
              lists files, models, photos, notes, measurements and zones, so
              the top bar keeps only the actions that create something. ─ */}
          {!embedded && (
          <div className={styles.panelWrap}>
            <button
              className={`${styles.resourceBtn} ${openPanel === 'files' ? styles.resourceBtnOpen : ''}`}
              onClick={() => togglePanel('files')}
              title="Point Cloud Files"
            >
              <Icon name="grain" />
              <span className={styles.resourceText}>Files</span>
              {files.length > 0 && <span className={styles.resourceBadge}>{files.length}</span>}
            </button>
            {openPanel === 'files' && (
              <FloatingPanel title="POINT CLOUD FILES" onClose={() => setOpenPanel(null)}>
                {readyFiles.length > 0 && (
                  <div className={styles.toggleAllRow}>
                    <button className={styles.toggleAllBtn} onClick={toggleAll}>
                      <Icon name={allVisible ? 'visibility_off' : 'visibility'} style={{ fontSize: 12 }} />
                      {allVisible ? 'Hide all' : 'Show all'}
                    </button>
                  </div>
                )}
                {files.length === 0 && (
                  <div className={styles.empty}>
                    <Icon name="grain" />
                    No point cloud files in this project
                  </div>
                )}
                {(() => {
                  const renderFile = (file: PCFile) => {
                    const isReady = file.status === 'ready';
                    const isVisible = visibility[file.id] ?? true;
                    const statusCls = file.status === 'ready' ? styles.statusReady
                                    : file.status === 'processing' ? styles.statusBusy
                                    : styles.statusError;
                    return (
                      <div key={file.id} className={`${styles.fileRow} ${!isReady ? styles.fileRowDim : ''}`}>
                        <button
                          className={`${styles.eye} ${isReady && !isVisible ? styles.eyeOff : ''}`}
                          onClick={() => isReady && toggleVisibility(file.id)}
                          disabled={!isReady}
                          title={!isReady ? `${file.status}` : (isVisible ? 'Hide' : 'Show')}
                        >
                          <Icon name={isReady && !isVisible ? 'visibility_off' : 'visibility'} />
                        </button>
                        <div className={styles.fileMain}>
                          <span className={`${styles.fileName} ${isReady && !isVisible ? styles.fileNameDim : ''}`}>
                            {file.originalName}
                          </span>
                          <span className={styles.fileMeta}>{formatSize(file.fileSize)} · .{file.extension.toLowerCase() === 'zip' ? 'zip (rcp)' : file.extension}</span>
                          {file.status === 'processing' && conversionProgress[file.id] && (
                            <ConversionProgressBar progress={conversionProgress[file.id]} />
                          )}
                        </div>
                        <span className={`${styles.statusDot} ${statusCls}`} />
                      </div>
                    );
                  };

                  if (folders.length === 0) {
                    return files.map(renderFile);
                  }

                  const ungrouped = files.filter((f) => !f.folderId);
                  return (
                    <>
                      {ungrouped.length > 0 && (() => {
                        const readyUngrouped = ungrouped.filter((f) => f.status === 'ready');
                        const allUngroupedVisible = readyUngrouped.length > 0 && readyUngrouped.every((f) => visibility[f.id] !== false);
                        const isUngroupedCollapsed = folderCollapsed['__ungrouped__'];
                        return (
                          <div>
                            <div className={styles.folderSection}>
                              <button
                                className={styles.folderSectionEye}
                                onClick={() => {
                                  const makeVisible = !allUngroupedVisible;
                                  const patch: Record<string, boolean> = {};
                                  readyUngrouped.forEach((f) => { patch[f.id] = makeVisible; });
                                  setVisibility((prev) => ({ ...prev, ...patch }));
                                  if (loaded) readyUngrouped.forEach((f) => post({ type: 'setCloudVisible', fileId: f.id, visible: makeVisible }));
                                  readyUngrouped.forEach((f) => onVisibilityChange?.(f.id, makeVisible));
                                }}
                                disabled={readyUngrouped.length === 0}
                                title={allUngroupedVisible ? 'Hide ungrouped' : 'Show ungrouped'}
                              >
                                <Icon name={allUngroupedVisible ? 'visibility' : 'visibility_off'} style={{ fontSize: 15 }} />
                              </button>
                              <Icon name="list" style={{ fontSize: 13 }} />
                              <span className={styles.folderSectionName}>Ungrouped</span>
                              <span className={styles.folderCount}>{ungrouped.length}</span>
                              <button
                                className={styles.folderCollapseBtn}
                                onClick={() => setFolderCollapsed((prev) => ({ ...prev, '__ungrouped__': !prev['__ungrouped__'] }))}
                                title={isUngroupedCollapsed ? 'Expand' : 'Collapse'}
                              >
                                <Icon name="expand_more" style={{ fontSize: 14, transition: 'transform 0.15s', transform: isUngroupedCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
                              </button>
                            </div>
                            {!isUngroupedCollapsed && ungrouped.map(renderFile)}
                          </div>
                        );
                      })()}
                      {folders.map((folder) => {
                        const folderFiles = files.filter((f) => f.folderId === folder.id);
                        const readyInFolder = folderFiles.filter((f) => f.status === 'ready');
                        const allFolderVisible = readyInFolder.length > 0 && readyInFolder.every((f) => visibility[f.id] !== false);
                        const isCollapsed = folderCollapsed[folder.id];
                        return (
                          <div key={folder.id}>
                            <div className={styles.folderSection}>
                              <button
                                className={styles.folderSectionEye}
                                onClick={() => toggleFolderVisibility(folder.id)}
                                disabled={readyInFolder.length === 0}
                                title={allFolderVisible ? 'Hide folder' : 'Show folder'}
                              >
                                <Icon name={allFolderVisible ? 'visibility' : 'visibility_off'} style={{ fontSize: 15 }} />
                              </button>
                              <Icon name="folder" style={{ fontSize: 13, color: '#f97316' }} />
                              <span className={styles.folderSectionName}>{folder.name}</span>
                              <span className={styles.folderCount}>{folderFiles.length}</span>
                              <button
                                className={styles.folderCollapseBtn}
                                onClick={() => setFolderCollapsed((prev) => ({ ...prev, [folder.id]: !prev[folder.id] }))}
                                title={isCollapsed ? 'Expand' : 'Collapse'}
                              >
                                <Icon name="expand_more" style={{ fontSize: 14, transition: 'transform 0.15s', transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }} />
                              </button>
                            </div>
                            {!isCollapsed && (
                              folderFiles.length === 0
                                ? <div className={styles.folderEmpty}>No files in this folder</div>
                                : folderFiles.map(renderFile)
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </FloatingPanel>
            )}
          </div>
          )}

          {/* ─ BIM files: overlay a BIM model on the point cloud (any signed-in Scene user). ─ */}
          {!embedded && canUseBimFiles && (
          <div className={styles.panelWrap}>
            <button
              className={`${styles.resourceBtn} ${openPanel === 'bim' ? styles.resourceBtnOpen : ''}`}
              onClick={() => togglePanel('bim')}
              title="BIM Files"
            >
              <Icon name="view_in_ar" />
              <span className={styles.resourceText}>BIM files</span>
              {bimFiles.length > 0 && <span className={styles.resourceBadge}>{bimFiles.length}</span>}
            </button>
            {openPanel === 'bim' && (
              <FloatingPanel title="BIM FILES" onClose={() => setOpenPanel(null)}>
                {bimFiles.length === 0 && (
                  <div className={styles.empty}>
                    <Icon name="view_in_ar" />
                    No BIM models (RVT/IFC) in this project
                  </div>
                )}
                {bimFiles.some((f) => bimVisible[f.id] && !bimConverting[f.id]) && (
                  <div className={styles.toggleAllRow} style={{ gap: 8 }}>
                    <span style={{ fontSize: 11, color: '#4b5569' }}>Opacity</span>
                    <input
                      type="range" min={0.1} max={1} step={0.05} value={bimOpacity}
                      onChange={(e) => changeBimOpacity(parseFloat(e.target.value))}
                      style={{ flex: 1 }}
                    />
                  </div>
                )}
                {bimFiles.map((file) => {
                  const on = bimVisible[file.id] ?? false;
                  const xf = bimXform[file.id] ?? DEFAULT_BIM_XFORM;
                  const range = Math.max(bimSceneSize[file.id] ?? 20, 5);
                  return (
                    <div key={file.id}>
                      <div className={styles.fileRow}>
                        <button
                          className={`${styles.eye} ${!on ? styles.eyeOff : ''}`}
                          onClick={() => toggleBim(file.id)}
                          disabled={!loaded}
                          title={!loaded ? 'Wait for the point cloud to load' : on ? 'Hide from point cloud' : 'Show on point cloud'}
                        >
                          <Icon name={on ? 'visibility' : 'visibility_off'} />
                        </button>
                        <div className={styles.fileMain}>
                          <span className={`${styles.fileName} ${!on ? styles.fileNameDim : ''}`}>
                            {file.originalName}
                          </span>
                          <span className={styles.fileMeta}>
                            {formatSize(file.fileSize)} · .{file.extension}
                            {bimConverting[file.id] ? ' · converting…' : ''}
                            {bimError[file.id] ? ` · ${bimError[file.id]}` : ''}
                          </span>
                        </div>
                      </div>
                      {on && !bimConverting[file.id] && (
                        <div style={{ padding: '4px 8px 10px 8px' }}>
                          <div style={{ fontSize: 10, color: '#7b849a', textTransform: 'uppercase', letterSpacing: '.04em', margin: '2px 0 4px' }}>
                            Align to point cloud
                          </div>
                          {/* +v.toFixed(2) keeps typed precision visible (3.25m) without trailing zeros (3.5m, 20m) */}
                          {bimSlider('Move X', xf.dx, -range, range, range / 200, (v) => updateBimXform(file.id, { dx: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Move Y', xf.dy, -range, range, range / 200, (v) => updateBimXform(file.id, { dy: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Move Z', xf.dz, -range, range, range / 200, (v) => updateBimXform(file.id, { dz: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Rotate', xf.rot, 0, 360, 1, (v) => updateBimXform(file.id, { rot: v }), (v) => `${+v.toFixed(1)}°`)}
                          {bimSlider('Scale', xf.scale, 0.2, 5, 0.01, (v) => updateBimXform(file.id, { scale: v }), (v) => `${v.toFixed(2)}×`)}
                          <button className={styles.toggleAllBtn} style={{ marginTop: 6 }} onClick={() => resetBimXform(file.id)}>
                            <Icon name="restart_alt" style={{ fontSize: 12 }} />
                            Reset alignment
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </FloatingPanel>
            )}
          </div>
          )}

          {!embedded && allPanoEntries.length > 0 && (
            <div className={styles.panelWrap}>
              <button
                className={`${styles.resourceBtn} ${openPanel === 'photos' ? styles.resourceBtnOpen : ''}`}
                onClick={() => togglePanel('photos')}
                title="360 Photos"
              >
                <Icon name="360" />
                <span className={styles.resourceText}>Photos</span>
                <span className={styles.resourceBadge}>{allPanoEntries.length}</span>
              </button>
              {openPanel === 'photos' && (
                <FloatingPanel title="360 PHOTOS" onClose={() => setOpenPanel(null)}>
                  {allPanoEntries.map((p) => (
                    <button
                      key={p.id}
                      className={styles.photoRow}
                      onClick={() => { handleFlyToMarker(p); setOpenPanel(null); }}
                      disabled={!loaded}
                    >
                      <div className={styles.photoIcon}>
                        <Icon name="panorama" />
                      </div>
                      <div className={styles.fileMain}>
                        <span className={styles.fileName}>{labelForPano(p)}</span>
                        <span className={styles.fileMeta}>Scan position {String(p.index + 1).padStart(2, '0')}</span>
                      </div>
                    </button>
                  ))}
                </FloatingPanel>
              )}
            </div>
          )}

          {isAdmin && readyFiles.length >= 2 && (
            <div className={styles.panelWrap}>
              <button
                className={`${styles.resourceBtn} ${openPanel === 'inspect' ? styles.resourceBtnOpen : ''}`}
                onClick={() => togglePanel('inspect')}
                title="Geometry inspector — visualize per-file bounding boxes & coordinate frames"
              >
                <Icon name="straighten" />
                <span className={styles.resourceText}>Inspect</span>
                <span className={styles.resourceBadge}>{Object.keys(cloudGeometry).length}</span>
              </button>
              {openPanel === 'inspect' && (
                <FloatingPanel title="GEOMETRY INSPECTOR" wide onClose={() => setOpenPanel(null)}>
                  {(() => {
                    const entries = orderedFileIds
                      .map((id) => ({ id, geom: cloudGeometry[id], file: readyFiles.find((f) => f.id === id) }))
                      .filter((e) => e.geom && e.file);
                    if (entries.length === 0) {
                      return <div className={styles.folderEmpty}>Waiting for cloud geometry…</div>;
                    }
                    const fmt = (n: number) => (Math.abs(n) >= 1000 ? n.toFixed(0) : n.toFixed(2));
                    const center = (g: CloudGeometry) => ({
                      x: (g.worldMin.x + g.worldMax.x) / 2,
                      y: (g.worldMin.y + g.worldMax.y) / 2,
                      z: (g.worldMin.z + g.worldMax.z) / 2,
                    });
                    const dims = (g: CloudGeometry) => ({
                      w: g.worldMax.x - g.worldMin.x,
                      d: g.worldMax.y - g.worldMin.y,
                      h: g.worldMax.z - g.worldMin.z,
                    });
                    const ref = entries[0].geom;
                    const refC = center(ref);
                    const distFromRef = (g: CloudGeometry) => {
                      const c = center(g);
                      const dx = c.x - refC.x, dy = c.y - refC.y, dz = c.z - refC.z;
                      return Math.sqrt(dx * dx + dy * dy + dz * dz);
                    };
                    return (
                      <>
                        <label
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 10px', marginBottom: 8,
                            background: 'rgba(99,102,241,0.08)', borderRadius: 6,
                            border: '1px solid rgba(99,102,241,0.2)', cursor: 'pointer',
                            fontSize: 12,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={bboxOverlayOn}
                            onChange={(e) => setBboxOverlayOn(e.target.checked)}
                            style={{ accentColor: '#6366f1' }}
                          />
                          <span style={{ fontWeight: 600 }}>Show bounding boxes in 3D</span>
                        </label>
                        <p style={{ fontSize: 11, color: '#4b5569', marginBottom: 10, lineHeight: 1.4 }}>
                          Each file is shown at its native world coordinates. Large distances between
                          centers mean the files were exported in different reference frames or
                          captured at separate sites. Compare dimensions / centers below.
                        </p>
                        {entries.map((e, i) => {
                          const c = center(e.geom!);
                          const dm = dims(e.geom!);
                          const dist = i === 0 ? 0 : distFromRef(e.geom!);
                          return (
                            <div
                              key={e.id}
                              style={{
                                padding: '8px 10px', marginBottom: 8,
                                background: 'rgba(255,255,255,0.04)', borderRadius: 6,
                                borderLeft: `3px solid ${inspectColorByFile[e.id]}`,
                                fontSize: 11, lineHeight: 1.5,
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                <span style={{
                                  width: 10, height: 10, borderRadius: 2,
                                  background: inspectColorByFile[e.id], flexShrink: 0,
                                }} />
                                <span style={{ fontWeight: 600, color: '#131b2e' }}>{e.file!.originalName}</span>
                              </div>
                              <div style={{ color: '#4b5569' }}>
                                <div><strong>Center:</strong> ({fmt(c.x)}, {fmt(c.y)}, {fmt(c.z)})</div>
                                <div><strong>Size:</strong> {fmt(dm.w)} × {fmt(dm.d)} × {fmt(dm.h)} m</div>
                                {e.geom!.offset && (
                                  <div><strong>Offset:</strong> ({fmt(e.geom!.offset.x)}, {fmt(e.geom!.offset.y)}, {fmt(e.geom!.offset.z)})</div>
                                )}
                                {e.geom!.spacing != null && (
                                  <div><strong>Spacing:</strong> {e.geom!.spacing.toFixed(3)} m</div>
                                )}
                                {e.geom!.pointCount != null && (
                                  <div><strong>Points:</strong> {e.geom!.pointCount.toLocaleString()}</div>
                                )}
                                {i > 0 && (
                                  <div style={{ marginTop: 3, color: dist > 50 ? '#b45309' : '#4b5569' }}>
                                    <strong>Distance from {entries[0].file!.originalName}:</strong> {dist.toFixed(1)} m
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    );
                  })()}
                </FloatingPanel>
              )}
            </div>
          )}

          {(!isShareMode || guestCanNote) && loaded && annotationFileId && noteToolsOn && (
            <div className={styles.panelWrap}>
              {embedded ? (
                <button
                  className={`${styles.resourceBtn} ${annotationMode || pendingPos ? styles.resourceBtnOpen : ''}`}
                  onClick={() => {
                    if (annotationMode) { cancelAddNote(); return; }
                    if (pendingPos) { setPendingPos(null); setPendingText(''); return; }
                    startAddNote();
                    setOpenPanel('annotations');
                  }}
                  title={annotationMode || pendingPos ? 'Cancel the new note' : 'New note'}
                >
                  <Icon name="add_comment" />
                  <span className={styles.resourceText}>Note</span>
                  {pendingPos && <span className={`${styles.resourceBadge} ${styles.resourceBadgeAccent}`}>NEW</span>}
                </button>
              ) : (
              <button
                className={`${styles.resourceBtn} ${openPanel === 'annotations' ? styles.resourceBtnOpen : ''}`}
                onClick={() => togglePanel('annotations')}
                title="Annotations"
              >
                <Icon name="comment" />
                <span className={styles.resourceText}>Notes</span>
                {(openAnnots > 0 || pendingPos) && (
                  <span className={`${styles.resourceBadge} ${pendingPos ? styles.resourceBadgeAccent : ''}`}>
                    {pendingPos ? 'NEW' : openAnnots}
                  </span>
                )}
              </button>
              )}
              {openPanel === 'annotations' && (!embedded || annotationMode || pendingPos || expandedId) && (
                <FloatingPanel title={embedded && !annotationMode && !pendingPos ? 'NOTE' : 'ANNOTATIONS'} wide onClose={() => setOpenPanel(null)}>
                  {!pendingPos && !annotationMode && !embedded && (
                    <div className={styles.toggleAllRow}>
                      <button className={styles.toggleAllBtn} onClick={startAddNote}>
                        <Icon name="add" style={{ fontSize: 12 }} />
                        New note
                      </button>
                    </div>
                  )}
                  {annotationMode && (
                    <div className={styles.annotForm}>
                      <div className={styles.annotHint}>
                        <Icon name="ads_click" />
                        Click on the scene to place the note
                      </div>
                      <div className={styles.btnRow}>
                        <button className={styles.btnGhost} onClick={cancelAddNote}>Cancel</button>
                      </div>
                    </div>
                  )}
                  {pendingPos && (
                    <div className={styles.annotForm}>
                      <div className={styles.annotHint}>
                        <Icon name="location_on" />
                        Position captured — add a comment
                      </div>
                      {isShareMode && (
                        <input
                          className={styles.textarea}
                          placeholder="Your name (shown on the note)"
                          value={guestName}
                          maxLength={60}
                          onChange={(e) => updateGuestName(e.target.value)}
                        />
                      )}
                      <textarea
                        className={styles.textarea}
                        placeholder="Describe the issue or note…"
                        value={pendingText}
                        onChange={(e) => setPendingText(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className={styles.btnRow}>
                        <button className={styles.btnGhost} onClick={() => { setPendingPos(null); setPendingText(''); }}>
                          Cancel
                        </button>
                        <div style={{ flex: 1 }} />
                        <button
                          className={styles.btnPrimary}
                          onClick={handleSaveAnnotation}
                          disabled={!pendingText.trim() || creating}
                        >
                          {creating ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                  {annotations.length === 0 && !pendingPos && !annotationMode && !embedded && (
                    <p className={styles.emptyAnnots}>
                      Click <strong>New note</strong>, then click the point cloud to drop a marker.
                    </p>
                  )}
                  {zoneHiddenAnnotCount > 0 && !embedded && (
                    <div className={styles.zoneHiddenHint}>
                      <Icon name="filter_alt" style={{ fontSize: 12 }} />
                      {zoneHiddenAnnotCount} hidden by zone
                    </div>
                  )}
                  {(embedded ? zoneFilteredAnnotations.filter((a) => a.id === expandedId) : zoneFilteredAnnotations).map((a, i) => (
                    <div
                      key={a.id}
                      ref={a.id === activeId ? activeCardRef : null}
                      className={`${styles.annotCard} ${a.id === activeId ? styles.annotCardActive : ''} ${a.status === 'resolved' ? styles.annotCardResolved : ''}`}
                      onClick={() => handleCardClick(a.id, a.position, a.layerId)}
                    >
                      <div className={styles.annotCardHead}>
                        <span className={styles.annotIdx}>{String(i + 1).padStart(2, '0')}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className={styles.annotAuthor}>{authorName(a)}</p>
                          <p className={styles.annotTime}>{formatTime(a.createdAt)}</p>
                        </div>
                        {a.layerId && (
                          <span className={styles.noteChip} title={layerFileById[a.layerId]?.originalName ?? ''}>
                            <Icon name={layerKindOf(layerFileById[a.layerId] ?? {}) === 'ortho' ? 'satellite_alt' : 'blur_on'} style={{ fontSize: 12 }} />
                          </span>
                        )}
                        {a.attachments.length > 0 && (
                          <span className={styles.noteChip} title={`${a.attachments.length} attached file(s)`}>
                            <Icon name="attach_file" style={{ fontSize: 12 }} />
                            {a.attachments.length}
                          </span>
                        )}
                        {a.linkedFiles.length > 0 && (
                          <span className={styles.noteChip} title={`${a.linkedFiles.length} linked file(s)`}>
                            <Icon name="link" style={{ fontSize: 12 }} />
                            {a.linkedFiles.length}
                          </span>
                        )}
                        {a.status === 'resolved' && <span className={styles.resolvedBadge}>Resolved</span>}
                      </div>
                      <p className={styles.annotText}>{a.text}</p>
                      {a.replies.length > 0 && (
                        <div className={styles.annotReplies}>
                          {a.replies.map((r) => (
                            <div key={r.id} className={styles.replyItem}>
                              <p className={styles.replyAuthor}>{authorName(r)}</p>
                              <p className={styles.replyText}>{r.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {expandedId === a.id && (
                        <div className={styles.annotActions} onClick={(e) => e.stopPropagation()}>
                          <div className={styles.attachBlock}>
                            <div className={styles.attachHeader}>
                              <span className={styles.attachLabel}>Files</span>
                              {!isShareMode && (
                                <button
                                  className={styles.attachAddBtn}
                                  onClick={() => { attachTargetRef.current = a.id; attachInputRef.current?.click(); }}
                                >
                                  <Icon name="attach_file" style={{ fontSize: 13 }} />
                                  Attach PDF
                                </button>
                              )}
                            </div>
                            {a.attachments.map((att) => (
                              <div key={att.id} className={styles.attachRow}>
                                <Icon name={isPdfAttachment(att) ? 'picture_as_pdf' : 'draft'} className={styles.attachIcon} />
                                <button
                                  className={styles.attachName}
                                  onClick={() => openAttachment(a.id, att.id, 'view')}
                                  title={`View ${att.fileName}`}
                                >
                                  {att.fileName}
                                </button>
                                <span className={styles.attachSize}>{formatBytes(att.fileSize)}</span>
                                {att.downloadUrl && (
                                  <button className={styles.attachIconBtn} onClick={() => openAttachment(a.id, att.id, 'download')} title="Download">
                                    <Icon name="download" style={{ fontSize: 14 }} />
                                  </button>
                                )}
                                {!isShareMode && (
                                  <button className={styles.attachIconBtn} onClick={() => removeAttachment(a.id, att)} title="Remove">
                                    <Icon name="close" style={{ fontSize: 14 }} />
                                  </button>
                                )}
                              </div>
                            ))}
                            {Object.entries(uploads).filter(([, u]) => u.annotationId === a.id).map(([k, u]) => (
                              <div key={k} className={styles.attachRow}>
                                <Icon name="upload" className={styles.attachIcon} />
                                <div className={styles.attachUpload}>
                                  <span className={styles.attachUploadName}>{u.name}</span>
                                  <div className={styles.attachProgress}>
                                    <div className={styles.attachProgressFill} style={{ width: `${u.pct}%` }} />
                                  </div>
                                </div>
                                <span className={styles.attachSize}>{u.pct}%</span>
                              </div>
                            ))}
                            {a.attachments.length === 0 && !Object.values(uploads).some((u) => u.annotationId === a.id) && (
                              <p className={styles.attachEmpty}>{isShareMode ? 'No files attached' : 'No PDFs attached yet'}</p>
                            )}
                            {attachError && <p className={styles.attachError}>{attachError}</p>}
                          </div>
                          <div className={styles.attachBlock}>
                            <div className={styles.attachHeader}>
                              <span className={styles.attachLabel}>Linked files</span>
                              {!isShareMode && projectId && linkPickerFor !== a.id && (
                                <button className={styles.attachAddBtn} onClick={() => setLinkPickerFor(a.id)}>
                                  <Icon name="link" style={{ fontSize: 13 }} />
                                  Link file
                                </button>
                              )}
                            </div>
                            {a.linkedFiles.map((f) => (
                              <div key={f.id} className={styles.attachRow}>
                                <Icon name={linkedFileIcon(f.type)} className={styles.attachIcon} />
                                <button className={styles.attachName} onClick={() => openLinkedFile(f)} title={`Open ${f.originalName}`}>
                                  {f.originalName}
                                </button>
                                <span className={styles.attachSize}>{(f.extension || '').toUpperCase()}</span>
                                {!isShareMode && (
                                  <button className={styles.attachIconBtn} onClick={() => unlinkFile({ variables: { annotationId: a.id, fileId: f.id } })} title="Unlink">
                                    <Icon name="close" style={{ fontSize: 14 }} />
                                  </button>
                                )}
                              </div>
                            ))}
                            {a.linkedFiles.length === 0 && linkPickerFor !== a.id && (
                              <p className={styles.attachEmpty}>No linked files</p>
                            )}
                            {linkPickerFor === a.id && projectId && (
                              <NoteFilePicker
                                projectId={projectId}
                                excludeIds={[...a.linkedFiles.map((f) => f.id), ...(annotationFileId ? [annotationFileId] : [])]}
                                busy={linkingFile}
                                onPick={(f) => linkFile({ variables: { annotationId: a.id, fileId: f.id } })}
                                onClose={() => setLinkPickerFor(null)}
                              />
                            )}
                          </div>
                          {isShareMode && (
                            <input
                              className={styles.textarea}
                              placeholder="Your name (shown on the reply)"
                              value={guestName}
                              maxLength={60}
                              onChange={(e) => updateGuestName(e.target.value)}
                            />
                          )}
                          <textarea
                            className={styles.textarea}
                            placeholder="Add a reply…"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            rows={2}
                          />
                          <div className={styles.btnRow}>
                            {!isShareMode && (
                              <button className={styles.btnSecondary} onClick={() => resolveAnnotation({ variables: { annotationId: a.id } })}>
                                {a.status === 'resolved' ? 'Reopen' : 'Resolve'}
                              </button>
                            )}
                            <div style={{ flex: 1 }} />
                            {!isShareMode && (
                              <button className={styles.btnDestructive} onClick={() => deleteAnnotation({ variables: { annotationId: a.id } })} title="Delete">
                                <Icon name="delete" />
                              </button>
                            )}
                            <button
                              className={styles.btnPrimary}
                              onClick={() => handleReply(a.id)}
                              disabled={!replyText.trim() || replying}
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </FloatingPanel>
              )}
            </div>
          )}

          {/* Share mode keeps the measure tool and shows the saved
              measurements; a guest's own are saved only where the link's
              options allow it. */}
          {loaded && annotationFileId && measureToolsOn && (
            <div className={styles.panelWrap}>
              {embedded ? (
                <>
                  <button
                    className={`${styles.resourceBtn} ${measureKind === 'line' ? styles.resourceBtnOpen : ''}`}
                    onClick={() => { if (measureKind === 'line') cancelMeasure(); else { selectMeasureKind('line'); setOpenPanel('measures'); } }}
                    title={measureKind === 'line' ? 'Stop measuring' : 'New distance'}
                  >
                    <Icon name="straighten" />
                    <span className={styles.resourceText}>Distance</span>
                  </button>
                  <button
                    className={`${styles.resourceBtn} ${measureKind === 'area' ? styles.resourceBtnOpen : ''}`}
                    onClick={() => { if (measureKind === 'area') cancelMeasure(); else { selectMeasureKind('area'); setOpenPanel('measures'); } }}
                    title={measureKind === 'area' ? 'Stop measuring' : 'New area'}
                  >
                    <Icon name="square_foot" />
                    <span className={styles.resourceText}>Area</span>
                  </button>
                </>
              ) : (
              <button
                className={`${styles.resourceBtn} ${openPanel === 'measures' ? styles.resourceBtnOpen : ''}`}
                onClick={() => togglePanel('measures')}
                title="Measurements"
              >
                <Icon name="straighten" />
                <span className={styles.resourceText}>Measures</span>
                {allMeasurements.length > 0 && (
                  <span className={styles.resourceBadge}>{allMeasurements.length}</span>
                )}
              </button>
              )}
              {openPanel === 'measures' && (!embedded || measureMode) && (
                <FloatingPanel title={embedded ? 'MEASURING' : 'MEASUREMENTS'} onClose={() => setOpenPanel(null)}>
                  {!measureMode ? (
                    <div className={styles.toggleAllRow} style={{ gap: 8 }}>
                      <button className={styles.toggleAllBtn} onClick={() => selectMeasureKind('line')}>
                        <Icon name="straighten" style={{ fontSize: 12 }} />
                        New distance
                      </button>
                      <button className={styles.toggleAllBtn} onClick={() => selectMeasureKind('area')}>
                        <Icon name="square_foot" style={{ fontSize: 12 }} />
                        New area
                      </button>
                    </div>
                  ) : (
                    <div className={styles.annotForm}>
                      <div className={styles.annotHint}>
                        <Icon name={measureKind === 'area' ? 'square_foot' : 'straighten'} />
                        {measureKind === 'area'
                          ? 'Click points on the surface to outline an area — click the first point again to close it'
                          : 'Click a start point on the surface, then an end point'}
                      </div>
                      <div className={styles.btnRow}>
                        <button className={styles.btnGhost} onClick={cancelMeasure}>Done</button>
                      </div>
                    </div>
                  )}
                  {allMeasurements.length === 0 && !measureMode && !embedded && (
                    <p className={styles.emptyAnnots}>
                      Start a <strong>Distance</strong> or <strong>Area</strong> measurement, then click points on the cloud.
                    </p>
                  )}
                  {(embedded ? [] : allMeasurements).map((m, i) => {
                    const hidden = hiddenMeasures.has(m.id);
                    const isArea = m.kind === 'area';
                    return (
                      <div
                        key={m.id}
                        className={styles.measureCard}
                        onClick={() => handleMeasureCardClick(m)}
                        title={isArea ? 'Fly to area' : 'Fly to measurement'}
                      >
                        <span className={styles.annotIdx}>
                          <Icon name={isArea ? 'square_foot' : 'straighten'} style={{ fontSize: 16 }} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className={styles.measureDistance}>
                            {isArea ? `${(m.area ?? 0).toFixed(2)} m²` : `${(m.distance ?? 0).toFixed(2)} m`}
                          </p>
                          <p className={styles.annotTime}>{authorName(m)} · {formatTime(m.createdAt)}</p>
                        </div>
                        <button
                          className={styles.measureIconBtn}
                          onClick={(e) => { e.stopPropagation(); toggleMeasureVisible(m.id); }}
                          title={hidden ? 'Show' : 'Hide'}
                        >
                          <Icon name={hidden ? 'visibility_off' : 'visibility'} />
                        </button>
                        {canDeleteMeasurement(m) && (
                          <button
                            className={styles.measureIconBtn}
                            onClick={(e) => { e.stopPropagation(); deleteMeasurementById(m.id); }}
                            title="Delete measurement"
                          >
                            <Icon name="delete" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </FloatingPanel>
              )}
            </div>
          )}

          {!isShareMode && loaded && annotationFileId && cloudToolsOn && (
            <div className={styles.panelWrap}>
              {embedded ? (
                <button
                  className={`${styles.resourceBtn} ${zoneEditMode ? styles.resourceBtnOpen : ''}`}
                  onClick={() => { if (zoneEditMode) cancelZoneEdit(); else { startZoneEdit(); setOpenPanel('zones'); } }}
                  title={zoneEditMode ? 'Cancel the new zone' : 'New focus zone'}
                >
                  <Icon name="highlight_alt" />
                  <span className={styles.resourceText}>Zone</span>
                </button>
              ) : (
              <button
                className={`${styles.resourceBtn} ${openPanel === 'zones' ? styles.resourceBtnOpen : ''}`}
                onClick={() => togglePanel('zones')}
                title="Focus zones"
              >
                <Icon name="highlight_alt" />
                <span className={styles.resourceText}>Zones</span>
                {focusZones.length > 0 && (
                  <span className={styles.resourceBadge}>{focusZones.length}</span>
                )}
              </button>
              )}
              {openPanel === 'zones' && (!embedded || zoneEditMode) && (
                <FloatingPanel title={embedded ? 'NEW ZONE' : 'FOCUS ZONES'} onClose={() => setOpenPanel(null)}>
                  {zoneEditMode ? (
                    <div className={styles.annotForm}>
                      <div className={styles.annotHint}>
                        <Icon name="highlight_alt" />
                        {zoneDraft ? 'Drag arrows or faces · ⌥-drag = opposite side' : 'Placing zone box…'}
                      </div>
                      <input
                        className={styles.zoneNameInput}
                        placeholder="Zone name…"
                        value={zoneName}
                        onChange={(e) => setZoneName(e.target.value)}
                        autoFocus
                      />
                      <div className={styles.btnRow}>
                        <button className={styles.btnGhost} onClick={cancelZoneEdit}>Cancel</button>
                        <div style={{ flex: 1 }} />
                        <button
                          className={styles.btnPrimary}
                          onClick={handleSaveZone}
                          disabled={!zoneDraft || !zoneName.trim() || savingZone}
                        >
                          {savingZone ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.toggleAllRow}>
                      <button className={styles.toggleAllBtn} onClick={startZoneEdit}>
                        <Icon name="add" style={{ fontSize: 12 }} />
                        New zone
                      </button>
                    </div>
                  )}
                  {focusZones.length === 0 && !zoneEditMode && !embedded && (
                    <p className={styles.emptyAnnots}>
                      Create a zone to focus the view on one area — clouds, notes and measurements outside it are hidden while it's active.
                    </p>
                  )}
                  {(embedded ? [] : focusZones).map((z) => {
                    const active = z.id === activeZoneId;
                    return (
                      <div
                        key={z.id}
                        className={`${styles.zoneCard} ${active ? styles.zoneCardActive : ''}`}
                        onClick={() => post({ type: 'flyToMeasurement', start: z.min, end: z.max })}
                        title="Fly to zone"
                      >
                        <span className={styles.annotIdx}>
                          <Icon name="highlight_alt" style={{ fontSize: 16 }} />
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p className={styles.zoneName}>{z.name}</p>
                          <p className={styles.annotTime}>{z.createdBy.firstName} {z.createdBy.lastName} · {formatTime(z.createdAt)}</p>
                        </div>
                        <button
                          className={`${styles.measureIconBtn} ${active ? styles.zoneRadioOn : ''}`}
                          onClick={(e) => { e.stopPropagation(); setActiveZoneId(active ? null : z.id); }}
                          title={active ? 'Deactivate zone' : 'Activate zone'}
                        >
                          <Icon name={active ? 'radio_button_checked' : 'radio_button_unchecked'} />
                        </button>
                        <button
                          className={styles.measureIconBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (active) setActiveZoneId(null);
                            deleteFocusZoneMut({ variables: { zoneId: z.id } });
                          }}
                          title="Delete zone"
                        >
                          <Icon name="delete" />
                        </button>
                      </div>
                    );
                  })}
                </FloatingPanel>
              )}
            </div>
          )}

          <div className={styles.divider} />

          <div className={styles.panelWrap}>
            <button
              className={`${styles.iconBtn} ${openPanel === 'settings' ? styles.iconBtnActive : ''}`}
              onClick={() => togglePanel('settings')}
              title={cloudsOn ? 'Render settings' : 'Navigation hints'}
            >
              <Icon name="tune" />
            </button>
            {openPanel === 'settings' && (
              <FloatingPanel title={cloudsOn ? 'RENDER SETTINGS' : 'NAVIGATION HINTS'} onClose={() => setOpenPanel(null)}>
                {cloudsOn && (
                  <>
                    <div className={styles.section}>
                      <div className={styles.sectionLabel}>
                        <span>Point Size</span>
                        <span>{pointSize.toFixed(1)}</span>
                      </div>
                      <input
                        type="range"
                        className={styles.slider}
                        min={0.5} max={5} step={0.1}
                        value={pointSize}
                        disabled={!loaded}
                        onChange={(e) => handlePointSize(Number(e.target.value))}
                      />
                    </div>
                    <div className={styles.section}>
                      <div className={styles.sectionLabel}>
                        <span>Opacity</span>
                        <span>{Math.round(opacity * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        className={styles.slider}
                        min={0} max={1} step={0.01}
                        value={opacity}
                        disabled={!loaded}
                        onChange={(e) => handleOpacity(Number(e.target.value))}
                      />
                    </div>
                    <div className={styles.section}>
                      <div className={styles.sectionLabel}>
                        <span>Brightness</span>
                        <span>{brightness > 0 ? '+' : ''}{Math.round(brightness * 100)}%</span>
                      </div>
                      <input
                        type="range"
                        className={styles.slider}
                        min={-1} max={1} step={0.05}
                        value={brightness}
                        disabled={!loaded}
                        onChange={(e) => handleBrightness(Number(e.target.value))}
                      />
                    </div>
                    <div className={styles.section}>
                      <div className={styles.sectionLabel}>
                        <span>Color Mode</span>
                        <span>{colorMode.toUpperCase()}</span>
                      </div>
                      <div className={styles.colorRow}>
                        {(['rgb', 'elevation', 'intensity'] as ColorMode[]).map((m) => (
                          <button
                            key={m}
                            className={`${styles.colorChip} ${colorMode === m ? styles.colorChipActive : ''}`}
                            onClick={() => handleColorMode(m)}
                            disabled={!loaded}
                          >
                            {m === 'rgb' ? 'RGB' : m === 'elevation' ? 'Elev' : 'Inten'}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                <div className={styles.section}>
                  {cloudsOn && (
                    <div className={styles.sectionLabel}>
                      <span>Navigation Hints</span>
                    </div>
                  )}
                  <div className={styles.kbdRow}><span>Look</span><span className={styles.kbd}>L · Drag</span></div>
                  <div className={styles.kbdRow}><span>Move</span><span className={styles.kbd}>R · Drag</span></div>
                  <div className={styles.kbdRow}><span>Pan</span><span className={styles.kbd}>M · Drag</span></div>
                  <div className={styles.kbdRow}><span>Fwd / Back</span><span className={styles.kbd}>Wheel</span></div>
                  <div className={styles.kbdRow}><span>Walk</span><span className={styles.kbd}>W A S D</span></div>
                </div>
              </FloatingPanel>
            )}
          </div>

          {alignFor && (
            <div className={styles.panelWrap}>
              {openPanel === 'align' && (() => {
                const bim = bimFiles.find((f) => f.id === alignFor);
                const layer = layerFileById[alignFor];
                const name = bim?.originalName ?? layer?.originalName ?? '';
                const close = () => { setOpenPanel(null); setAlignFor(null); };
                if (bim) {
                  const xf = bimXform[bim.id] ?? DEFAULT_BIM_XFORM;
                  const range = Math.max(bimSceneSize[bim.id] ?? 20, 5);
                  const ready = bimLoaded.current.has(bim.id);
                  return (
                    <FloatingPanel title="PLACEMENT" onClose={close}>
                      <div className={styles.alignName} title={name}>{name}</div>
                      {!ready && <div className={styles.alignHint}>Loading the model…</div>}
                      {ready && bimGeoref[bim.id] === false && (
                        <div className={styles.alignWarn}>No georeference — placed at the centre of the scene. "Gather" in the viewer moves it onto the base.</div>
                      )}
                      {ready && bimGeoref[bim.id] === true && <div className={styles.alignHint}>Georeferenced — drawn at its own coordinates.</div>}
                      {ready && bimNative[bim.id] && <div className={styles.alignHint}>Own coordinates: {fmtCoord(bimNative[bim.id])}</div>}
                      {ready && (
                        <div style={{ padding: '4px 8px 10px 8px' }}>
                          {bimSlider('Move X', xf.dx, -range, range, range / 200, (v) => updateBimXform(bim.id, { dx: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Move Y', xf.dy, -range, range, range / 200, (v) => updateBimXform(bim.id, { dy: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Move Z', xf.dz, -range, range, range / 200, (v) => updateBimXform(bim.id, { dz: v }), (v) => `${+v.toFixed(2)}m`, true)}
                          {bimSlider('Rotate', xf.rot, 0, 360, 1, (v) => updateBimXform(bim.id, { rot: v }), (v) => `${+v.toFixed(1)}°`)}
                          {bimSlider('Scale', xf.scale, 0.2, 5, 0.01, (v) => updateBimXform(bim.id, { scale: v }), (v) => `${v.toFixed(2)}×`)}
                          {bimSlider('Opacity', bimOpacity, 0.1, 1, 0.05, changeBimOpacity, (v) => `${Math.round(v * 100)}%`)}
                          <button className={styles.toggleAllBtn} style={{ marginTop: 6 }} onClick={() => resetBimXform(bim.id)}>
                            <Icon name="restart_alt" style={{ fontSize: 12 }} />
                            Reset placement
                          </button>
                          {!isShareMode && <div className={styles.alignHint}>Changes are saved for everyone in this project.</div>}
                        </div>
                      )}
                    </FloatingPanel>
                  );
                }
                if (!layer) return null;
                const cur = layerXform[layer.id];
                const def = layerDefault[layer.id];
                const range = Math.max(layerSize[layer.id] ?? 20, 5);
                const kind = layerKindOf(layer);
                return (
                  <FloatingPanel title="PLACEMENT" onClose={close}>
                    <div className={styles.alignName} title={name}>{name}</div>
                    {(layerWarnings[layer.id] ?? []).map((w) => <div key={w} className={styles.alignWarn}>{w}</div>)}
                    {cur && def && layerGeoref[layer.id] === false && (
                      <div className={styles.alignWarn}>No georeference — placed where its own coordinates put it. "Gather" in the viewer moves it onto the base.</div>
                    )}
                    {cur && def && layerGeoref[layer.id] === true && <div className={styles.alignHint}>Georeferenced — drawn at its own coordinates.</div>}
                    {cur && def && layerNative[layer.id] && <div className={styles.alignHint}>Own coordinates: {fmtCoord(layerNative[layer.id])}</div>}
                    {(!cur || !def) && <div className={styles.alignHint}>{layerError[layer.id] ? layerError[layer.id] : 'Loading…'}</div>}
                    {cur && def && (
                      <div style={{ padding: '4px 8px 10px 8px' }}>
                        {bimSlider('Move X', cur.x - def.x, -range, range, range / 200, (v) => updateLayerXform(layer.id, { x: def.x + v }), (v) => `${+v.toFixed(2)}m`, true)}
                        {bimSlider('Move Y', cur.y - def.y, -range, range, range / 200, (v) => updateLayerXform(layer.id, { y: def.y + v }), (v) => `${+v.toFixed(2)}m`, true)}
                        {bimSlider(kind === 'splat' ? 'Move Z' : 'Height', cur.z - def.z, -range, range, range / 200, (v) => updateLayerXform(layer.id, { z: def.z + v }), (v) => `${+v.toFixed(2)}m`, true)}
                        {bimSlider('Rotate', cur.rot, 0, 360, 1, (v) => updateLayerXform(layer.id, { rot: v }), (v) => `${+v.toFixed(1)}°`)}
                        {bimSlider('Scale', cur.scale, 0.2, 5, 0.01, (v) => updateLayerXform(layer.id, { scale: v }), (v) => `${v.toFixed(2)}×`)}
                        {bimSlider('Opacity', layerOpacity[layer.id] ?? 1, 0.1, 1, 0.05, (v) => changeLayerOpacity(layer.id, v), (v) => `${Math.round(v * 100)}%`)}
                        <button className={styles.toggleAllBtn} style={{ marginTop: 6 }} onClick={() => resetLayerXform(layer.id)}>
                          <Icon name="restart_alt" style={{ fontSize: 12 }} />
                          Reset placement
                        </button>
                        {!isShareMode && <div className={styles.alignHint}>Changes are saved for everyone in this project.</div>}
                      </div>
                    )}
                  </FloatingPanel>
                );
              })()}
            </div>
          )}

          {placementOpen && (
            <div className={styles.panelWrap}>
              <FloatingPanel title="PLACE ON MAP" onClose={closeSitePlacement} wide>
                <SitePlacementPanel
                  initial={sitePlacement}
                  anchor={sceneAnchor}
                  defaultLatLng={sceneProject?.latitude != null && sceneProject?.longitude != null ? { lat: sceneProject.latitude, lng: sceneProject.longitude } : null}
                  onPreview={setPlacementPreview}
                  onSave={applySitePlacement}
                  onClose={closeSitePlacement}
                />
              </FloatingPanel>
            </div>
          )}

          <div className={styles.divider} />

          <button className={styles.iconBtn} onClick={() => post({ type: 'fitToVisible' })} disabled={!loaded} title="Fit visible files">
            <Icon name="center_focus_strong" />
          </button>
          <button
            className={`${styles.iconBtn} ${!edlEnabled ? styles.iconBtnActive : ''}`}
            onClick={handleEdlToggle}
            disabled={!loaded}
            title={edlEnabled ? 'Depth test off — click to enable' : 'Depth test on — click to disable'}
          >
            <Icon name="layers" />
          </button>
        </div>
        )}
      </header>

      {/* View-mode quick switch (cloud / random panorama) */}
      {loaded && allPanoEntries.length > 0 && !allHidden && (
        <div className={styles.viewModeSwitch}>
          <button
            className={`${styles.viewModeBtn} ${styles.viewModeBtnActive}`}
            onClick={() => post({ type: 'fitToScreen' })}
            title="Show point cloud (Fit to screen)"
          >
            <Icon name="scatter_plot" />
            Point Cloud
          </button>
          <div className={styles.viewModeDivider} />
          <button
            className={styles.viewModeBtn}
            onClick={() => post({ type: 'openClosestPanorama' })}
            title="Start virtual tour from the panorama closest to your current view"
          >
            <Icon name="tour" />
            Virtual Tour
          </button>
        </div>
      )}

      {gatherMsg && (
        <div className={styles.pickHint}>
          <Icon name="info" />
          {gatherMsg}
          <button className={styles.pickHintCancel} onClick={() => setGatherMsg(null)}>OK</button>
        </div>
      )}

      {/* Failed-save banner (notes / measurements) */}
      {saveError && (
        <div className={styles.pickHint} style={{ background: 'rgba(239,68,68,0.92)', color: '#fff' }}>
          <Icon name="error" />
          {saveError}
        </div>
      )}

      {/* Pick-mode banner */}
      {annotationMode && (
        <div className={styles.pickHint}>
          <Icon name="ads_click" />
          Click on the scene to place the note
          <button className={styles.pickHintCancel} onClick={cancelAddNote}>Cancel</button>
        </div>
      )}

      {/* Measure-mode banner */}
      {measureMode && (
        <div className={styles.pickHint}>
          <Icon name={measureKind === 'area' ? 'square_foot' : 'straighten'} />
          {measureKind === 'area'
            ? 'Click points on the surface to outline an area — click the first point again to close it'
            : measureDist != null
              ? `Distance: ${measureDist.toFixed(2)} m — click two new points to measure again`
              : 'Click a start point on the surface, then an end point'}
          <button className={styles.pickHintCancel} onClick={cancelMeasure}>Done</button>
        </div>
      )}

      {/* All-hidden warning */}
      {loaded && allHidden && !layerBaseOn && (
        <div className={styles.allHiddenBanner}>
          <Icon name="visibility_off" style={{ fontSize: 14 }} />
          Nothing is switched on — tick a file under Overlay
        </div>
      )}

      {/* Loading / empty / error overlays */}
      {!loading && readyFiles.length === 0 && !hasLayerFiles && (
        <div className={styles.fullOverlay}>
          <div className={styles.fullOverlayIcon}>
            <Icon name={files.length === 0 ? 'cloud_off' : 'sync'} />
          </div>
          <p className={styles.fullOverlayTitle}>
            {files.length === 0 ? 'No point cloud files' : 'Converting files…'}
          </p>
          <p className={styles.fullOverlaySub}>
            {files.length === 0
              ? 'Upload E57, LAS, or LAZ files to this project to use Smart View.'
              : 'The viewer will appear automatically once conversion completes.'}
          </p>
          {hasProcessing && <div className={styles.progressBar}><div className={styles.progressFill} /></div>}
        </div>
      )}

      {readyFiles.length > 0 && !loaded && !loadError && (
        <div className={styles.fullOverlay}>
          <div className={styles.fullOverlayIcon}>
            <Icon name="downloading" />
          </div>
          <p className={styles.fullOverlayTitle}>Loading point clouds…</p>
          <p className={styles.fullOverlaySub}>{readyFiles.length} file{readyFiles.length > 1 ? 's' : ''} streaming</p>
          <div className={styles.progressBar}><div className={styles.progressFill} /></div>
        </div>
      )}

      {readyFiles.length > 0 && !loaded && loadError && (
        <div className={styles.fullOverlay}>
          <div className={styles.fullOverlayIcon} style={{ background: 'rgba(220,38,38,0.08)', borderColor: 'rgba(220,38,38,0.3)', color: 'var(--sv-red)' }}>
            <Icon name="error" />
          </div>
          <p className={styles.fullOverlayTitle}>Failed to load</p>
          <p className={styles.fullOverlaySub}>{loadError}</p>
        </div>
      )}

      {/* Streaming indicator — shown after initial 'loaded' until the first
          octree nodes actually render. With many clouds (60+ e57 files) the
          gap can be 2-3 s. */}
      {loaded && !cloudsVisible && !allHidden && (
        <div className={styles.streamingHint}>
          <Icon name="progress_activity" />
          <span>Streaming clouds…</span>
        </div>
      )}

      {/* Status HUD */}
      {loaded && !sceneEmpty && (
        <div className={styles.statusHud}>
          <span className={styles.statusGroup}>
            <span className={styles.statusAccent}>{navModeLabel}</span>
          </span>
          <span style={{ color: 'var(--sv-text-dim)' }}>·</span>
          {allHidden || readyFiles.length === 0 ? (
            <span className={styles.statusGroup}>
              <span>Layers</span>
              <span className={styles.statusVal}>{layerFiles.filter((f) => layerOn[f.id]).length + Object.values(bimVisible).filter(Boolean).length}</span>
            </span>
          ) : (
            <span className={styles.statusGroup}>
              <span>Clouds</span>
              <span className={styles.statusVal}>{readyFiles.filter((f) => visibility[f.id] !== false).length}/{readyFiles.length}</span>
            </span>
          )}
          {allPanoEntries.length > 0 && !allHidden && (
            <>
              <span style={{ color: 'var(--sv-text-dim)' }}>·</span>
              <span className={styles.statusGroup}>
                <span>Scans</span>
                <span className={styles.statusVal}>{allPanoEntries.length}</span>
              </span>
            </>
          )}
        </div>
      )}

      {areaEditorOpen && floorsForEditor && canRenameAreas && (
        <TourAreaEditor
          stations={tourStations}
          levelOf={floorsForEditor.levelOf}
          levels={floorsForEditor.count}
          groups={areaGroups}
          onSave={async (groups) => {
            if (!projectId) return;
            await saveTourAreaGroupsMut({ variables: { projectId, groups: groups.map((g) => ({ id: g.id, name: g.name, stationIds: g.stationIds })) } });
          }}
          onClose={() => setAreaEditorOpen(false)}
        />
      )}

      {/* Panorama overlay */}
      {(() => {
        if (!openPano) return null;
        if (!allPanoEntries.length) return null;
        return (
          <PanoramaOverlay
            panoramas={allPanoEntries}
            startId={openPano.id}
            initialHeading={openPano.heading}
            initialPitch={openPano.pitch}
            measurements={measurements}
            hiddenMeasures={hiddenMeasures}
            annotations={annotations.filter((a) => !hiddenNotes.has(a.id) && !a.layerId)}
            hiddenStations={hiddenStations}
            occIndex={occIndex}
            activeZone={activeZone ? { min: activeZone.min, max: activeZone.max } : null}
            iframeRef={iframeRef}
            post={post}
            tour={tourExtras}
            onStation={setTourStationId}
            onClose={(dir) => {
              setOpenPano(null);
              if (dir && typeof dir.heading === 'number') {
                post({ type: 'setCameraDirection', heading: dir.heading, pitch: dir.pitch });
              } else {
                setNavMode('orbit');
                post({ type: 'setNavigationMode', mode: 'orbit' });
              }
            }}
          />
        );
      })()}
      {!isShareMode && (
        <input
          ref={attachInputRef}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(e) => {
            const target = attachTargetRef.current;
            if (target && e.target.files?.length) uploadAttachments(target, e.target.files);
            e.target.value = '';
          }}
        />
      )}
      {viewing && (() => {
        const pdf = isPdfAttachment(viewing);
        return (
          <div className={styles.lightbox} onClick={() => setViewing(null)}>
            <div className={styles.lightboxInner} onClick={(e) => e.stopPropagation()}>
              <div className={styles.lightboxBar}>
                <Icon name={pdf ? 'picture_as_pdf' : 'draft'} className={styles.attachIcon} />
                <span className={styles.lightboxName} title={viewing.fileName}>{viewing.fileName}</span>
                <span className={styles.attachSize}>
                  {formatBytes(viewing.fileSize)}
                  {viewing.uploadedBy ? ` · ${viewing.uploadedBy.firstName} ${viewing.uploadedBy.lastName}` : ''}
                </span>
                <div style={{ flex: 1 }} />
                {viewing.downloadUrl && (
                  <a className={styles.lightboxBtn} href={viewing.downloadUrl}>
                    <Icon name="download" style={{ fontSize: 15 }} />
                    Download
                  </a>
                )}
                <button className={styles.lightboxBtn} onClick={() => setViewing(null)} title="Close (Esc)">
                  <Icon name="close" style={{ fontSize: 15 }} />
                </button>
              </div>
              <div className={styles.lightboxBody}>
                {!viewing.url ? (
                  <p className={styles.lightboxNoPreview}>Preview needs S3 storage.</p>
                ) : pdf ? (
                  <iframe className={styles.lightboxFrame} src={viewing.url} title={viewing.fileName} />
                ) : (
                  <div className={styles.lightboxNoPreview}>
                    <Icon name="draft" style={{ fontSize: 40 }} />
                    <p>No preview for this file type — download it to open.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
});

export default SmartView;
